// The Connections page (2026-10-15 redesign): Your connections, the connection panel, the
// integrations catalogue, other ways to connect and the Meta Ads setup.
// Namespace `connections-page`. All 24 languages carry translated values; their terms follow the
// reviewed connection-access, connect-access, connections, workspace-topbar and welcome copy.
// Placeholders are `{name}`, filled with `formatCopy`; plural nouns use `one:…|other:…` forms for
// `formatCount`, listing every CLDR category a language uses. Product, protocol and permission
// names, and Meta's own interface labels, stay as they are.
import type { NamespaceCatalog } from '@/i18n/ns';

const copy = {
  bg: {
    eyebrow: 'Работно пространство на {company}',
    title: 'Връзки',
    intro:
      'Свържете инструментите си, за да намирате информация и да вършите работа. Изберете кой може да използва всяка връзка и дали Poneva може да чете, или и да прави промени.',
    activity: 'Дейност',
    backToConnections: 'Обратно към „Връзки“',
    yoursTitle: 'Вашите връзки',
    filterLabel: 'Показване на връзки',
    filterAll: 'Всички',
    filterMine: 'Само аз',
    filterShared: 'Споделени',
    filterAttention: 'Изискват внимание',
    columnConnection: 'Връзка',
    columnWho: 'Кой може да я използва',
    columnWhat: 'Какво може да прави Poneva',
    columnStatus: 'Състояние',
    yoursLoading: 'Зареждане на връзките ви…',
    yoursEmpty:
      'Все още нищо не е свързано. Потърсете по-долу, за да свържете първия си инструмент.',
    filterEmpty: 'Засега тук няма нищо.',
    yoursLoadError: 'Връзките ви не можаха да се заредят.',
    serversLoadError: 'Собствените ви сървъри не можаха да се заредят.',
    databasesLoadError: 'Базите ви данни не можаха да се заредят.',
    retry: 'Опитайте отново',
    stateConnected: 'Свързано',
    stateAttention: 'Нужен е вход',
    stateReview: 'Прегледайте достъпа',
    stateUnavailable: 'Не е налично',
    stateOff: 'Изключено',
    stateConnecting: 'Свързване…',
    signInAgain: 'Влезте отново',
    attentionBody:
      'Входът за {name} е изтекъл или е бил отменен. Влезте отново, за да продължите да използвате връзката.',
    reviewBody:
      'Акаунтът зад {name} е променен. Прегледайте кой може да използва връзката и какво може да прави Poneva, преди тя да се използва отново.',
    unavailableBody: 'Входът в {name} е активен, но инструментите в момента не са налични.',
    offBody:
      'Връзката с {name} е изключена. Никой не може да я използва, докато не бъде включена отново. Нищо не се изтрива.',
    kindServer: 'Собствен сървър',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:таблица|other:таблици',
    adAccountsNoun: 'one:рекламен акаунт|other:рекламни акаунта',
    toolsNoun: 'one:инструмент|other:инструмента',
    close: 'Затваряне',
    moreActions: 'Още действия за {name}',
    tabOverview: 'Общ преглед',
    tabAccess: 'Достъп',
    tabActivity: 'Дейност',
    connectedOn: 'От {date}',
    lastUsed: 'Последно използвана {when}',
    whatTitle: 'Какво може да прави Poneva',
    readsLine: 'Чете, когато поискате',
    changesAsk: 'Пита ви, преди да промени нещо',
    changesAllowed: 'Прави промени, без да пита. Всяка промяна се записва в „Дейност“.',
    changesNever: 'Никога не променя нищо',
    readinessReady: '{count} на разположение',
    change: 'Промяна',
    tryTitle: 'Изпробвайте в чат',
    tryPrompt: 'С какво можеш да ми помогнеш чрез {name}?',
    addAccount: 'Добавяне на още един акаунт',
    disconnect: 'Прекъсване на връзката',
    disconnectConfirm:
      'Да се прекъсне ли връзката с {name}? Тя спира да работи в чатове, приложения и агенти, докато не бъде свързана отново.',
    disconnectKeep: 'Запазване',
    nativeOnlyYou: 'Само вие можете да използвате този акаунт.',
    nativeEveryone: 'Всички в {company} могат да използват този акаунт.',
    providerPermissions: 'Собствените разрешения на акаунта при доставчика продължават да важат.',
    manageDatabase: 'Управление на базата данни',
    activityLoading: 'Зареждане на дейността…',
    activityLoadError: 'Дейността не можа да се зареди.',
    activityRead: 'Четене',
    activityChange: 'Промяна',
    activitySetting: 'Настройка',
    activityDenied: 'Отказано',
    activityFailed: 'Неуспешно',
    activityUnknown: 'Неизвестен резултат',
    activityAll: 'Отваряне на цялата дейност',
    activityToday: 'Днес',
    activityYesterday: 'Вчера',
    eventCreated: 'Връзката е създадена',
    eventShared: 'Споделянето е променено',
    eventPersonAdded: 'Добавен е човек',
    eventPersonRemoved: 'Премахнат е човек',
    eventAccessChanged: 'Достъпът е променен',
    eventRevoked: 'Достъпът е изключен',
    eventRemoved: 'Връзката е премахната',
    eventSettings: 'Ограничението за време на заявките е променено',
    addTitle: 'Добавяне на връзка',
    integrations: 'one:интеграция|other:интеграции',
    searchLabel: 'Търсене на интеграции',
    searchPlaceholder: 'Търсене на интеграции',
    clearSearch: 'Изчистване на търсенето',
    inYours: 'Във вашите връзки',
    addNew: 'Добавяне на нова',
    resultsFor: '{count} за „{query}“',
    resultsNoun: 'one:резултат|other:резултата',
    popularFirst: 'Първо популярните',
    searching: 'Търсене…',
    searchUnavailable: 'Търсенето на връзки временно не е достъпно.',
    noResults:
      'Все още нищо не съвпада. Опитайте с друго име или попитайте Poneva кой инструмент е подходящ.',
    showMore: 'Показване на още',
    showMoreRetry: 'Нов опит за зареждане на още',
    loadingMore: 'Зареждане…',
    moreFailed: 'Още интеграции не можаха да се заредят. Резултатите ви са все още тук.',
    manageMembers: 'Изберете какво могат да свързват членовете',
    manageDone: 'Готово',
    manageHint:
      'Включвайте или изключвайте приложения за членовете. Вече свързаните акаунти остават свързани.',
    allowFor: 'Разрешаване на нови връзки с {name} за {company}',
    tileConnect: 'Свързване',
    tileConnecting: 'Свързване…',
    tileConnected: 'Свързано',
    tileSignIn: 'Влезте отново',
    tileOff: 'Изключено',
    tileUnavailable: 'Не е налично',
    tileNotAllowed: 'Не е разрешено',
    tileNotAllowedNote: 'Администраторът ви е изключил новите връзки',
    tileAccountOnly: 'Само връзка с акаунт · все още без инструменти за чат',
    tileApiKey: 'Изисква се API ключ',
    tileOwnApp: 'Използва ваше собствено приложение за {name}',
    tileAdminSetup: 'Настройва се от собственик или администратор',
    otherWays: 'Други начини за свързване',
    serverTitle: 'Ваш собствен сървър',
    serverBody: 'Добавете инструменти от MCP сървър, който екипът ви поддържа.',
    serverAction: 'Добавяне на сървър',
    databaseTitle: 'База данни',
    databaseBody: 'PostgreSQL, ограничен до схемите или таблиците, които изберете.',
    databaseAction: 'Свързване',
    assistantsTitle: 'Други AI асистенти',
    assistantsBody:
      'Позволете на Claude, ChatGPT или агент за програмиране да чете одобрените знания на компанията.',
    assistantsAction: 'Настройване',
    assistantsSheetIntro:
      'Асистенти извън Poneva, които четат одобрените знания на компанията, и агентите за програмиране, които работят за вас.',
    footnote:
      'Вие одобрявате промените, преди да бъдат изпълнени. Интеграциите се осигуряват от Pipedream; инструментите на всяко приложение са различни.',
    askBody:
      'Не сте сигурни коя ви трябва? Poneva може да предложи връзки според работата, която опишете.',
    askAction: 'Попитайте в чата',
    askPrompt:
      'Кои фирмени източници би било най-полезно да свържем за нашата работа, като се има предвид какво знаеш за нас?',
    compactTitle: 'Вземете и инструментите си.',
    forYou: 'За вас',
    browseAll: 'Разгледайте всички инструменти',
    selectedForFocus: 'Избрани според вашия фокус',
    popupBlocked: 'Браузърът блокира прозореца за вход.',
    popupOpen: 'Отваряне на страницата за вход',
    popupKeepChecking: 'Тази страница продължава да проверява.',
    noticeAdded: 'Връзката е добавена.',
    noticeAddedReturn:
      'Връзката е добавена. Можете да затворите този раздел и да се върнете към работата си.',
    noticeSignInUnfinished:
      'Входът не завърши. Използвайте „Влезте отново“ при сървъра, за да опитате пак.',
    noticeChecked: 'Връзката е проверена успешно.',
    noticeUnreachable: 'Сървърът все още е недостъпен.',
    noticeSignInReady: 'Страница за вход е готова в нов раздел.',
    noticeAuthorized: 'Разрешаването е завършено.',
    noticeDatabaseRemoved: 'Връзката с PostgreSQL е прекъсната.',
    noticeStoreConnected: 'Магазинът в Shopify е свързан.',
    noticeStoreChooseAccess:
      'Магазинът в Shopify е свързан. Отворете го във „Вашите връзки“, за да изберете кой може да го използва.',
    allowFailed: 'Неуспешно обновяване на {name}. Опитайте отново.',
    accessUnknown: 'Подробностите за достъпа не са налични.',
    metaStep: 'Стъпка {step} от {total}',
    stepsLabel: 'Стъпки за настройка',
    stepChoose: 'Избор',
    stepPrepare: 'Подготовка',
    stepAccess: 'Достъп',
    stepSignIn: 'Вход',
    metaChooseTitle: 'Свързване на Meta Ads',
    metaChooseIntro:
      'Добавете рекламните си акаунти в Meta към Poneva. Първо изберете какво може да прави Poneva с тях.',
    metaReporting: 'Отчети',
    metaReportingTag: 'Само четене',
    metaReportingBody:
      'Чете рекламни акаунти, кампании, набори от реклами, реклами и резултати, както и вашите страници във Facebook. Poneva никога не променя нищо в Meta.',
    metaManage: 'Отчети и управление на реклами',
    metaManageTag: 'Четене и писане',
    metaManageBody:
      'Освен това създава и обновява кампании, реклами и каталози със собствените инструменти на Meta. Вие избирате дали Poneva да пита преди всяка промяна.',
    metaOwnApp:
      'Meta изисква всяка фирма да се свързва чрез собствено приложение в Meta. Следващата стъпка ви превежда през настройката му, еднократно.',
    metaConnectedAlready: 'Свързано',
    prepareTitle: 'Подгответе приложението си в Meta',
    prepareIntro:
      'Направете това веднъж в Meta for Developers. Нужен ви е администраторски достъп до бизнес портфолиото на компанията ви в Meta.',
    prepareAppTitle: 'Създайте или отворете приложението си в Meta',
    prepareAppReporting: 'Включете Marketing API и Facebook Login for Business.',
    prepareAppManage: 'Добавете случая на употреба „Create and manage ads with the ads MCP“.',
    prepareAppOpen: 'Отваряне на Meta for Developers',
    prepareRedirectTitle: 'Добавете адреса за вход на Poneva',
    prepareRedirectBody:
      'Във Facebook Login for Business го поставете под Valid OAuth Redirect URIs. Оставете адресите, които вече са там, и запазете.',
    prepareSecretTitle: 'Пригответе App ID и App Secret',
    prepareSecretBody:
      'И двата са в приложението ви под App settings → Basic. В последната стъпка ще ги поставите в защитен прозорец, никога в Poneva.',
    prepareDone: 'Готово',
    copy: 'Копиране',
    copied: 'Копирано',
    copyAddress: 'Копиране на адреса за вход',
    copyManual: 'Маркирайте адреса и го копирайте с клавиатурата.',
    permissionsTitle: 'Какво ще ви помоли Meta да одобрите',
    permissionsNote:
      'Meta може да поиска да потвърди бизнеса ви, преди приложението да има достъп до рекламни акаунти извън вашите собствени.',
    scopeAdsRead: 'Четене на рекламите ви и резултатите им',
    scopeAdsManagement: 'Създаване и промяна на реклами',
    scopeAdsMcp: 'Използване на рекламните инструменти на Meta',
    scopeBusiness: 'Преглед на бизнес портфолиата ви',
    scopePages: 'Списък на страниците, които управлявате',
    scopePageEngagement: 'Четене на данните и ангажираността на страниците',
    scopeInstagram: 'Четене на основните данни за акаунта в Instagram',
    scopeCatalog: 'Управление на продуктови каталози',
    guide: 'Ръководство стъпка по стъпка',
    metaAccessTitle: 'Кой може да използва Meta Ads?',
    metaAccessIntro:
      'Рекламните акаунти обикновено принадлежат на цялата компания. Можете да промените това по всяко време.',
    metaReadOnlyTitle: 'Само четене',
    metaReadOnlyBody:
      'Отчетите никога не променят нищо в Meta. За да позволите на Poneva да управлява реклами, изберете управление на реклами в първата стъпка.',
    signInTitle: 'Вход с Meta',
    signInIntro: 'Отваря се защитен прозорец. Завършете там и тази страница ще се обнови сама.',
    signInWindow: 'В защитения прозорец',
    signInPaste: 'Поставете App ID и App Secret',
    signInChoose: 'Изберете бизнеса си и рекламните му акаунти',
    signInApprove: 'Одобрете достъпа в Meta',
    signInWaiting: 'Изчакване на Meta…',
    signInSecret:
      'Вашият App Secret отива директно в Nango, услугата за вход, която Poneva използва, и се съхранява там криптиран. Poneva никога не го вижда.',
    stopWaiting: 'Спрете да чакате',
    doneTitle: '{name} е свързано',
    doneAccountsYou: 'Вече можете да питате Poneva за {count}.',
    doneAccountsCompany: 'Всички в {company} вече могат да питат Poneva за {count}.',
    doneTools: 'Meta предостави {count} за този акаунт.',
    doneTry: 'Опитайте да попитате',
    doneAsk: 'Въпрос за рекламите ми',
    doneDone: 'Готово',
    doneTryReport: 'Как се представиха кампаниите от миналата седмица?',
    doneTryReturn: 'Кои реклами донесоха най-добра възвръщаемост този месец?',
    continue: 'Продължете',
    continueToMeta: 'Продължете към Meta',
    back: 'Назад',
    cancel: 'Отказ',
    askHelp: 'Поискайте помощ от Poneva',
    askHelpPrompt:
      'Помогни ми да настроя наше собствено приложение в Meta, за да мога да свържа Meta Ads с Poneva.',
    chatTitle: 'Свържете, за да продължите',
    chatIntro: 'Този чат остава там, където е.',
    chatConnectedYou: 'Свързано за вас',
    chatConnectedCompany: 'Свързано за {company}',
    chatUnavailable: 'С активен вход · инструментите не са налични',
    chatFoot: 'Влезте при доставчика. Когато се покаже „Свързано“, помолете Poneva да продължи.',
    chatPopupBlocked: 'Браузърът ви блокира прозореца за вход.',
    chatOpenAuthorization: 'Отваряне на входа',
    chatFallbackBody: 'Използвайте тази услуга в работата си.',
  },
  cs: {
    eyebrow: 'Pracovní prostor {company}',
    title: 'Připojení',
    intro:
      'Připojte své nástroje, abyste mohli hledat informace a vyřizovat práci. U každého připojení zvolte, kdo ho může používat a zda Poneva může číst, nebo i provádět změny.',
    activity: 'Aktivita',
    backToConnections: 'Zpět na Připojení',
    yoursTitle: 'Vaše připojení',
    filterLabel: 'Zobrazit připojení',
    filterAll: 'Vše',
    filterMine: 'Jen já',
    filterShared: 'Sdílená',
    filterAttention: 'Vyžadují pozornost',
    columnConnection: 'Připojení',
    columnWho: 'Kdo ho může používat',
    columnWhat: 'Co může Poneva dělat',
    columnStatus: 'Stav',
    yoursLoading: 'Načítání vašich připojení…',
    yoursEmpty: 'Zatím není nic připojeno. Vyhledejte níže svůj první nástroj a připojte ho.',
    filterEmpty: 'Tady teď nic není.',
    yoursLoadError: 'Vaše připojení se nepodařilo načíst.',
    serversLoadError: 'Vaše vlastní servery se nepodařilo načíst.',
    databasesLoadError: 'Vaše databáze se nepodařilo načíst.',
    retry: 'Zkusit znovu',
    stateConnected: 'Připojeno',
    stateAttention: 'Vyžaduje přihlášení',
    stateReview: 'Zkontrolovat přístup',
    stateUnavailable: 'Není k dispozici',
    stateOff: 'Vypnuto',
    stateConnecting: 'Připojování…',
    signInAgain: 'Znovu se přihlásit',
    attentionBody:
      'Přihlášení ke službě {name} vypršelo nebo bylo odvoláno. Chcete-li ji dál používat, přihlaste se znovu.',
    reviewBody:
      'Účet za službou {name} se změnil. Než se znovu použije, zkontrolujte, kdo ji může používat a co může Poneva dělat.',
    unavailableBody: 'Ke službě {name} jste přihlášeni, ale její nástroje teď nejsou k dispozici.',
    offBody:
      'Služba {name} je vypnutá. Dokud ji znovu nezapnete, nikdo ji nebude moct používat. Nic se nesmaže.',
    kindServer: 'Vlastní server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabulka|few:tabulky|many:tabulky|other:tabulek',
    adAccountsNoun:
      'one:reklamní účet|few:reklamní účty|many:reklamního účtu|other:reklamních účtů',
    toolsNoun: 'one:nástroj|few:nástroje|many:nástroje|other:nástrojů',
    close: 'Zavřít',
    moreActions: 'Další akce pro {name}',
    tabOverview: 'Přehled',
    tabAccess: 'Přístup',
    tabActivity: 'Aktivita',
    connectedOn: 'Od {date}',
    lastUsed: 'Naposledy použito {when}',
    whatTitle: 'Co může Poneva dělat',
    readsLine: 'Čte, když o to požádáte',
    changesAsk: 'Než cokoli změní, zeptá se vás',
    changesAllowed: 'Provádí změny bez ptaní. Každá se zaznamená v Aktivitě.',
    changesNever: 'Nikdy nic nemění',
    readinessReady: 'Připraveno: {count}',
    change: 'Změnit',
    tryTitle: 'Vyzkoušejte to v chatu',
    tryPrompt: 'S čím mi můžeš pomoct přes {name}?',
    addAccount: 'Přidat další účet',
    disconnect: 'Odpojit',
    disconnectConfirm:
      'Odpojit službu {name}? Dokud ji znovu nepřipojíte, přestane fungovat v chatech, aplikacích a agentech.',
    disconnectKeep: 'Ponechat',
    nativeOnlyYou: 'Tento účet můžete používat jen vy.',
    nativeEveryone: 'Tento účet mohou používat všichni ve firmě {company}.',
    providerPermissions: 'Vlastní oprávnění účtu u poskytovatele platí dál.',
    manageDatabase: 'Spravovat databázi',
    activityLoading: 'Načítání aktivity…',
    activityLoadError: 'Aktivitu se nepodařilo načíst.',
    activityRead: 'Čtení',
    activityChange: 'Změna',
    activitySetting: 'Nastavení',
    activityDenied: 'Zamítnuto',
    activityFailed: 'Selhalo',
    activityUnknown: 'Neznámý výsledek',
    activityAll: 'Otevřít veškerou aktivitu',
    activityToday: 'Dnes',
    activityYesterday: 'Včera',
    eventCreated: 'Připojení vytvořeno',
    eventShared: 'Sdílení změněno',
    eventPersonAdded: 'Osoba přidána',
    eventPersonRemoved: 'Osoba odebrána',
    eventAccessChanged: 'Přístup změněn',
    eventRevoked: 'Přístup vypnut',
    eventRemoved: 'Připojení odebráno',
    eventSettings: 'Časový limit dotazů změněn',
    addTitle: 'Přidat připojení',
    integrations: 'one:integrace|few:integrace|many:integrace|other:integrací',
    searchLabel: 'Hledat integrace',
    searchPlaceholder: 'Hledat integrace',
    clearSearch: 'Vymazat hledání',
    inYours: 'Ve vašich připojeních',
    addNew: 'Přidat nové',
    resultsFor: '{count} pro „{query}“',
    resultsNoun: 'one:výsledek|few:výsledky|many:výsledku|other:výsledků',
    popularFirst: 'Nejdřív oblíbené',
    searching: 'Hledání…',
    searchUnavailable: 'Hledání připojení je dočasně nedostupné.',
    noResults:
      'Zatím nic neodpovídá. Zkuste jiný název nebo se zeptejte Poneva, který nástroj se hodí.',
    showMore: 'Zobrazit další',
    showMoreRetry: 'Zkusit znovu načíst další',
    loadingMore: 'Načítání…',
    moreFailed: 'Další integrace se nepodařilo načíst. Vaše výsledky tu zůstávají.',
    manageMembers: 'Vybrat, co mohou členové připojit',
    manageDone: 'Hotovo',
    manageHint: 'Zapněte nebo vypněte aplikace pro členy. Už připojené účty zůstanou připojené.',
    allowFor: 'Povolit nová připojení služby {name} pro firmu {company}',
    tileConnect: 'Připojit',
    tileConnecting: 'Připojování…',
    tileConnected: 'Připojeno',
    tileSignIn: 'Znovu se přihlásit',
    tileOff: 'Vypnuto',
    tileUnavailable: 'Není k dispozici',
    tileNotAllowed: 'Nepovoleno',
    tileNotAllowedNote: 'Váš správce vypnul nová připojení',
    tileAccountOnly: 'Jen připojení účtu · zatím bez nástrojů pro chat',
    tileApiKey: 'Vyžaduje klíč API',
    tileOwnApp: 'Používá vaši vlastní aplikaci {name}',
    tileAdminSetup: 'Nastavuje vlastník nebo správce',
    otherWays: 'Další způsoby připojení',
    serverTitle: 'Vlastní server',
    serverBody: 'Přidejte nástroje ze serveru MCP, který provozuje váš tým.',
    serverAction: 'Přidat server',
    databaseTitle: 'Databáze',
    databaseBody: 'PostgreSQL, omezená na schémata nebo tabulky, které vyberete.',
    databaseAction: 'Připojit',
    assistantsTitle: 'Další asistenti AI',
    assistantsBody:
      'Nechte Claude, ChatGPT nebo programovacího agenta číst schválené firemní znalosti.',
    assistantsAction: 'Nastavit',
    assistantsSheetIntro:
      'Asistenti mimo Poneva, kteří čtou schválené firemní znalosti, a programovací agenti, kteří pracují za vás.',
    footnote:
      'Změny schvalujete dřív, než proběhnou. Integrace zajišťuje Pipedream; nástroje, které jednotlivé aplikace nabízejí, se liší.',
    askBody:
      'Nevíte jistě, které potřebujete? Poneva vám může navrhnout připojení podle práce, kterou popíšete.',
    askAction: 'Zeptat se v chatu',
    askPrompt:
      'Které firemní zdroje by bylo nejužitečnější připojit pro naši práci, podle toho, co o nás víš?',
    compactTitle: 'Vezměte s sebou své nástroje.',
    forYou: 'Pro vás',
    browseAll: 'Procházet všechny nástroje',
    selectedForFocus: 'Vybráno podle vašeho zaměření',
    popupBlocked: 'Prohlížeč zablokoval přihlašovací okno.',
    popupOpen: 'Otevřít přihlašovací stránku',
    popupKeepChecking: 'Tato stránka to dál kontroluje.',
    noticeAdded: 'Připojení bylo přidáno.',
    noticeAddedReturn: 'Připojení bylo přidáno. Tuto kartu můžete zavřít a vrátit se ke své práci.',
    noticeSignInUnfinished:
      'Přihlášení nebylo dokončeno. Zkuste to znovu tlačítkem Znovu se přihlásit u serveru.',
    noticeChecked: 'Připojení bylo úspěšně ověřeno.',
    noticeUnreachable: 'Server se zatím nepodařilo kontaktovat.',
    noticeSignInReady: 'Přihlašovací stránka je připravená v nové kartě.',
    noticeAuthorized: 'Povolení přístupu je dokončeno.',
    noticeDatabaseRemoved: 'Databáze PostgreSQL byla odpojena.',
    noticeStoreConnected: 'Obchod Shopify je připojen.',
    noticeStoreChooseAccess:
      'Obchod Shopify je připojen. Otevřete ho v sekci Vaše připojení a zvolte, kdo ho může používat.',
    allowFailed: 'Aplikaci {name} se nepodařilo aktualizovat. Zkuste to znovu.',
    accessUnknown: 'Podrobnosti o přístupu nejsou k dispozici.',
    metaStep: 'Krok {step} z {total}',
    stepsLabel: 'Kroky nastavení',
    stepChoose: 'Výběr',
    stepPrepare: 'Příprava',
    stepAccess: 'Přístup',
    stepSignIn: 'Přihlášení',
    metaChooseTitle: 'Připojit Meta Ads',
    metaChooseIntro:
      'Přeneste své reklamní účty Meta do Poneva. Nejdřív zvolte, co s nimi Poneva smí dělat.',
    metaReporting: 'Reporty',
    metaReportingTag: 'Jen čtení',
    metaReportingBody:
      'Čte reklamní účty, kampaně, sady reklam, reklamy a výsledky a také vaše stránky na Facebooku. Poneva v Meta nikdy nic nemění.',
    metaManage: 'Reporty a správa reklam',
    metaManageTag: 'Čtení a zápis',
    metaManageBody:
      'Navíc vytváří a upravuje kampaně, reklamy a katalogy pomocí vlastních nástrojů Meta. Vy rozhodnete, zda se Poneva před každou změnou zeptá.',
    metaOwnApp:
      'Meta vyžaduje, aby se každá firma připojila přes vlastní aplikaci Meta. Další krok vás jednou provede jejím nastavením.',
    metaConnectedAlready: 'Připojeno',
    prepareTitle: 'Připravte si aplikaci Meta',
    prepareIntro:
      'Udělejte to jednou v Meta for Developers. Potřebujete správcovský přístup k firemnímu účtu vaší společnosti v Meta.',
    prepareAppTitle: 'Vytvořte nebo otevřete svou aplikaci Meta',
    prepareAppReporting: 'Zapněte Marketing API a Facebook Login for Business.',
    prepareAppManage: 'Přidejte případ použití „Create and manage ads with the ads MCP“.',
    prepareAppOpen: 'Otevřít Meta for Developers',
    prepareRedirectTitle: 'Přidejte přihlašovací adresu Poneva',
    prepareRedirectBody:
      'Ve Facebook Login for Business ji vložte do pole Valid OAuth Redirect URIs. Adresy, které tam už jsou, ponechte a pak uložte.',
    prepareSecretTitle: 'Připravte si App ID a App Secret',
    prepareSecretBody:
      'Obojí najdete v aplikaci v části App settings → Basic. V posledním kroku je vložíte do zabezpečeného okna, nikdy ne do Poneva.',
    prepareDone: 'Hotovo',
    copy: 'Kopírovat',
    copied: 'Zkopírováno',
    copyAddress: 'Kopírovat přihlašovací adresu',
    copyManual: 'Označte adresu a zkopírujte ji klávesnicí.',
    permissionsTitle: 'Co vás Meta požádá schválit',
    permissionsNote:
      'Než aplikace získá přístup k reklamním účtům kromě vašich vlastních, může Meta požádat o ověření vaší firmy.',
    scopeAdsRead: 'Číst vaše reklamy a jejich výsledky',
    scopeAdsManagement: 'Vytvářet a měnit reklamy',
    scopeAdsMcp: 'Používat reklamní nástroje Meta',
    scopeBusiness: 'Zobrazit vaše firemní portfolia',
    scopePages: 'Zobrazit seznam stránek, které spravujete',
    scopePageEngagement: 'Číst údaje o stránkách a interakce',
    scopeInstagram: 'Číst základní údaje o účtu na Instagramu',
    scopeCatalog: 'Spravovat katalogy produktů',
    guide: 'Podrobný návod',
    metaAccessTitle: 'Kdo může používat Meta Ads?',
    metaAccessIntro: 'Reklamní účty obvykle patří celé firmě. Kdykoli to můžete změnit.',
    metaReadOnlyTitle: 'Jen čtení',
    metaReadOnlyBody:
      'Reporty v Meta nikdy nic nemění. Pokud chcete, aby Poneva spravovala reklamy, zvolte v prvním kroku správu reklam.',
    signInTitle: 'Přihlásit se přes Meta',
    signInIntro: 'Otevře se zabezpečené okno. Dokončete to tam a tato stránka se sama aktualizuje.',
    signInWindow: 'V zabezpečeném okně',
    signInPaste: 'Vložte App ID a App Secret',
    signInChoose: 'Vyberte svou firmu a její reklamní účty',
    signInApprove: 'Schvalte přístup v Meta',
    signInWaiting: 'Čekání na Meta…',
    signInSecret:
      'Váš App Secret jde přímo do Nango, přihlašovací služby, kterou Poneva používá, a je tam uložen šifrovaně. Poneva ho nikdy neuvidí.',
    stopWaiting: 'Přestat čekat',
    doneTitle: 'Služba {name} je připojená',
    doneAccountsYou: 'Teď se můžete Poneva ptát na {count}.',
    doneAccountsCompany: 'Všichni ve firmě {company} se teď mohou Poneva ptát na {count}.',
    doneTools: 'Meta pro tento účet zpřístupnila {count}.',
    doneTry: 'Zkuste se zeptat',
    doneAsk: 'Zeptat se na mé reklamy',
    doneDone: 'Hotovo',
    doneTryReport: 'Jak si vedly kampaně z minulého týdne?',
    doneTryReturn: 'Které reklamy tento měsíc přinesly nejlepší návratnost?',
    continue: 'Pokračovat',
    continueToMeta: 'Pokračovat do Meta',
    back: 'Zpět',
    cancel: 'Zrušit',
    askHelp: 'Požádat Poneva o pomoc',
    askHelpPrompt:
      'Pomoz mi nastavit naši vlastní aplikaci Meta, abychom mohli připojit Meta Ads k Poneva.',
    chatTitle: 'Připojte a pokračujte',
    chatIntro: 'Tento chat zůstane, kde je.',
    chatConnectedYou: 'Připojeno pro vás',
    chatConnectedCompany: 'Připojeno pro firmu {company}',
    chatUnavailable: 'Přihlášeno · nástroje nejsou k dispozici',
    chatFoot:
      'Přihlaste se u poskytovatele. Až se zobrazí Připojeno, požádejte Poneva, ať pokračuje.',
    chatPopupBlocked: 'Váš prohlížeč zablokoval přihlašovací okno.',
    chatOpenAuthorization: 'Otevřít přihlášení',
    chatFallbackBody: 'Používejte tuto službu při své práci.',
  },
  da: {
    eyebrow: 'Arbejdsområde for {company}',
    title: 'Forbindelser',
    intro:
      'Forbind dine værktøjer for at finde information og få ting gjort. Vælg, hvem der kan bruge hver forbindelse, og om Poneva kan læse eller lave ændringer.',
    activity: 'Aktivitet',
    backToConnections: 'Tilbage til Forbindelser',
    yoursTitle: 'Dine forbindelser',
    filterLabel: 'Vis forbindelser',
    filterAll: 'Alle',
    filterMine: 'Kun mig',
    filterShared: 'Delte',
    filterAttention: 'Kræver opmærksomhed',
    columnConnection: 'Forbindelse',
    columnWho: 'Hvem kan bruge den',
    columnWhat: 'Hvad Poneva kan gøre',
    columnStatus: 'Status',
    yoursLoading: 'Indlæser dine forbindelser…',
    yoursEmpty: 'Intet er forbundet endnu. Søg herunder for at forbinde dit første værktøj.',
    filterEmpty: 'Intet her lige nu.',
    yoursLoadError: 'Dine forbindelser kunne ikke indlæses.',
    serversLoadError: 'Dine egne servere kunne ikke indlæses.',
    databasesLoadError: 'Dine databaser kunne ikke indlæses.',
    retry: 'Prøv igen',
    stateConnected: 'Forbundet',
    stateAttention: 'Kræver login',
    stateReview: 'Gennemgå adgang',
    stateUnavailable: 'Ikke tilgængelig',
    stateOff: 'Slået fra',
    stateConnecting: 'Forbinder…',
    signInAgain: 'Log ind igen',
    attentionBody:
      'Login til {name} er udløbet eller tilbagekaldt. Log ind igen for at blive ved med at bruge den.',
    reviewBody:
      'Kontoen bag {name} er ændret. Gennemgå, hvem der kan bruge den, og hvad Poneva kan gøre, før den bruges igen.',
    unavailableBody: '{name} er logget ind, men dens værktøjer er ikke tilgængelige lige nu.',
    offBody: '{name} er slået fra. Ingen kan bruge den, før den slås til igen. Intet slettes.',
    kindServer: 'Din egen server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabel|other:tabeller',
    adAccountsNoun: 'one:annoncekonto|other:annoncekonti',
    toolsNoun: 'one:værktøj|other:værktøjer',
    close: 'Luk',
    moreActions: 'Flere handlinger for {name}',
    tabOverview: 'Oversigt',
    tabAccess: 'Adgang',
    tabActivity: 'Aktivitet',
    connectedOn: 'Siden {date}',
    lastUsed: 'Sidst brugt {when}',
    whatTitle: 'Hvad Poneva kan gøre',
    readsLine: 'Læser, når du beder om det',
    changesAsk: 'Spørger dig, før den ændrer noget',
    changesAllowed: 'Laver ændringer uden at spørge. Hver ændring registreres under Aktivitet.',
    changesNever: 'Ændrer aldrig noget',
    readinessReady: '{count} klar',
    change: 'Skift',
    tryTitle: 'Prøv det i en chat',
    tryPrompt: 'Hvad kan du hjælpe mig med via {name}?',
    addAccount: 'Tilføj en konto mere',
    disconnect: 'Afbryd forbindelse',
    disconnectConfirm:
      'Vil du afbryde forbindelsen til {name}? Den holder op med at virke i chats, apps og agenter, indtil den forbindes igen.',
    disconnectKeep: 'Behold',
    nativeOnlyYou: 'Kun du kan bruge denne konto.',
    nativeEveryone: 'Alle i {company} kan bruge denne konto.',
    providerPermissions: 'Udbyderens egne kontotilladelser gælder stadig.',
    manageDatabase: 'Administrer database',
    activityLoading: 'Indlæser aktivitet…',
    activityLoadError: 'Aktiviteten kunne ikke indlæses.',
    activityRead: 'Læsning',
    activityChange: 'Ændring',
    activitySetting: 'Indstilling',
    activityDenied: 'Afvist',
    activityFailed: 'Mislykket',
    activityUnknown: 'Ukendt resultat',
    activityAll: 'Åbn al aktivitet',
    activityToday: 'I dag',
    activityYesterday: 'I går',
    eventCreated: 'Forbindelse oprettet',
    eventShared: 'Deling ændret',
    eventPersonAdded: 'Person tilføjet',
    eventPersonRemoved: 'Person fjernet',
    eventAccessChanged: 'Adgang ændret',
    eventRevoked: 'Adgang slået fra',
    eventRemoved: 'Forbindelse fjernet',
    eventSettings: 'Tidsgrænse for forespørgsler ændret',
    addTitle: 'Tilføj en forbindelse',
    integrations: 'one:integration|other:integrationer',
    searchLabel: 'Søg i integrationer',
    searchPlaceholder: 'Søg i integrationer',
    clearSearch: 'Ryd søgning',
    inYours: 'I dine forbindelser',
    addNew: 'Tilføj ny',
    resultsFor: '{count} for »{query}«',
    resultsNoun: 'one:resultat|other:resultater',
    popularFirst: 'Populære først',
    searching: 'Søger…',
    searchUnavailable: 'Søgning efter forbindelser er midlertidigt utilgængelig.',
    noResults:
      'Intet matcher endnu. Prøv et andet navn, eller spørg Poneva, hvilket værktøj der passer.',
    showMore: 'Vis flere',
    showMoreRetry: 'Prøv at indlæse flere igen',
    loadingMore: 'Indlæser…',
    moreFailed: 'Flere integrationer kunne ikke indlæses. Dine resultater er her stadig.',
    manageMembers: 'Vælg, hvad medlemmer kan forbinde',
    manageDone: 'Færdig',
    manageHint:
      'Slå apps til eller fra for medlemmer. Konti, der allerede er forbundet, forbliver forbundet.',
    allowFor: 'Tillad nye forbindelser til {name} for {company}',
    tileConnect: 'Forbind',
    tileConnecting: 'Forbinder…',
    tileConnected: 'Forbundet',
    tileSignIn: 'Log ind igen',
    tileOff: 'Slået fra',
    tileUnavailable: 'Ikke tilgængelig',
    tileNotAllowed: 'Ikke tilladt',
    tileNotAllowedNote: 'Din administrator har slået nye forbindelser fra',
    tileAccountOnly: 'Kun kontoforbindelse · ingen chatværktøjer endnu',
    tileApiKey: 'API-nøgle påkrævet',
    tileOwnApp: 'Bruger din egen {name}-app',
    tileAdminSetup: 'Sættes op af ejer eller administrator',
    otherWays: 'Andre måder at forbinde på',
    serverTitle: 'Din egen server',
    serverBody: 'Tilføj værktøjer fra en MCP-server, som dit team driver.',
    serverAction: 'Tilføj server',
    databaseTitle: 'En database',
    databaseBody: 'PostgreSQL, begrænset til de skemaer eller tabeller, du vælger.',
    databaseAction: 'Forbind',
    assistantsTitle: 'Andre AI-assistenter',
    assistantsBody: 'Lad Claude, ChatGPT eller en kodeagent læse godkendt virksomhedsviden.',
    assistantsAction: 'Opsæt',
    assistantsSheetIntro:
      'Assistenter uden for Poneva, der læser godkendt virksomhedsviden, og de kodeagenter, der arbejder for dig.',
    footnote:
      'Du godkender ændringer, før de udføres. Integrationer leveres af Pipedream; værktøjerne i hver app varierer.',
    askBody:
      'Ikke sikker på, hvilken du har brug for? Poneva kan foreslå forbindelser ud fra det arbejde, du beskriver.',
    askAction: 'Spørg i chatten',
    askPrompt:
      'Hvilke af virksomhedens kilder ville være mest nyttige at forbinde til vores arbejde, ud fra det du ved om os?',
    compactTitle: 'Tag dine værktøjer med.',
    forYou: 'Til dig',
    browseAll: 'Se alle værktøjer',
    selectedForFocus: 'Udvalgt efter dit fokus',
    popupBlocked: 'Browseren blokerede login-vinduet.',
    popupOpen: 'Åbn login-siden',
    popupKeepChecking: 'Siden bliver ved med at tjekke.',
    noticeAdded: 'Forbindelsen er tilføjet.',
    noticeAddedReturn:
      'Forbindelsen er tilføjet. Du kan lukke denne fane og vende tilbage til dit arbejde.',
    noticeSignInUnfinished:
      'Login blev ikke fuldført. Brug Log ind igen ved serveren for at prøve igen.',
    noticeChecked: 'Forbindelsen er testet og virker.',
    noticeUnreachable: 'Serveren kunne ikke nås endnu.',
    noticeSignInReady: 'En login-side er klar i en ny fane.',
    noticeAuthorized: 'Godkendelsen er fuldført.',
    noticeDatabaseRemoved: 'Forbindelsen til PostgreSQL er afbrudt.',
    noticeStoreConnected: 'Shopify-butikken er forbundet.',
    noticeStoreChooseAccess:
      'Shopify-butikken er forbundet. Åbn den under Dine forbindelser for at vælge, hvem der kan bruge den.',
    allowFailed: '{name} kunne ikke opdateres. Prøv igen.',
    accessUnknown: 'Oplysninger om adgang er ikke tilgængelige.',
    metaStep: 'Trin {step} af {total}',
    stepsLabel: 'Trin i opsætningen',
    stepChoose: 'Vælg',
    stepPrepare: 'Forbered',
    stepAccess: 'Adgang',
    stepSignIn: 'Log ind',
    metaChooseTitle: 'Forbind Meta Ads',
    metaChooseIntro:
      'Få dine annoncekonti fra Meta ind i Poneva. Vælg først, hvad Poneva må gøre med dem.',
    metaReporting: 'Rapportering',
    metaReportingTag: 'Kun læsning',
    metaReportingBody:
      'Læser annoncekonti, kampagner, annoncesæt, annoncer og resultater samt dine Facebook-sider. Poneva ændrer aldrig noget i Meta.',
    metaManage: 'Rapportering og annonceadministration',
    metaManageTag: 'Læsning og skrivning',
    metaManageBody:
      'Opretter og opdaterer også kampagner, annoncer og kataloger med Metas egne værktøjer. Du vælger, om Poneva skal spørge før hver ændring.',
    metaOwnApp:
      'Meta kræver, at hver virksomhed forbinder via sin egen Meta-app. Næste trin guider dig gennem opsætningen, én gang.',
    metaConnectedAlready: 'Forbundet',
    prepareTitle: 'Forbered din Meta-app',
    prepareIntro:
      'Gør det én gang i Meta for Developers. Du skal have administratoradgang til din virksomhed i Meta.',
    prepareAppTitle: 'Opret eller åbn din Meta-app',
    prepareAppReporting: 'Slå Marketing API og Facebook Login for Business til.',
    prepareAppManage: 'Tilføj brugsscenariet »Create and manage ads with the ads MCP«.',
    prepareAppOpen: 'Åbn Meta for Developers',
    prepareRedirectTitle: 'Tilføj Ponevas login-adresse',
    prepareRedirectBody:
      'I Facebook Login for Business indsætter du den under Valid OAuth Redirect URIs. Behold de adresser, der allerede står der, og gem.',
    prepareSecretTitle: 'Hav dit App ID og din App Secret klar',
    prepareSecretBody:
      'Begge findes i din app under App settings → Basic. Du indsætter dem i et sikkert vindue i sidste trin, aldrig i Poneva.',
    prepareDone: 'Udført',
    copy: 'Kopiér',
    copied: 'Kopieret',
    copyAddress: 'Kopiér login-adressen',
    copyManual: 'Markér adressen, og kopiér den med tastaturet.',
    permissionsTitle: 'Det beder Meta dig om at godkende',
    permissionsNote:
      'Meta kan bede om at bekræfte din virksomhed, før appen kan nå andre annoncekonti end dine egne.',
    scopeAdsRead: 'Læse dine annoncer og deres resultater',
    scopeAdsManagement: 'Oprette og ændre annoncer',
    scopeAdsMcp: 'Bruge Metas annonceværktøjer',
    scopeBusiness: 'Se dine virksomhedsporteføljer',
    scopePages: 'Vise de sider, du administrerer',
    scopePageEngagement: 'Læse sideoplysninger og engagement',
    scopeInstagram: 'Læse grundlæggende oplysninger om Instagram-kontoen',
    scopeCatalog: 'Administrere produktkataloger',
    guide: 'Trin-for-trin-vejledning',
    metaAccessTitle: 'Hvem kan bruge Meta Ads?',
    metaAccessIntro: 'Annoncekonti tilhører som regel hele virksomheden. Du kan altid ændre det.',
    metaReadOnlyTitle: 'Kun læsning',
    metaReadOnlyBody:
      'Rapportering ændrer aldrig noget i Meta. Hvis Poneva skal administrere annoncer, skal du vælge annonceadministration i første trin.',
    signInTitle: 'Log ind med Meta',
    signInIntro:
      'Et sikkert vindue åbnes. Gør det færdigt der, så opdateres denne side af sig selv.',
    signInWindow: 'I det sikre vindue',
    signInPaste: 'Indsæt dit App ID og din App Secret',
    signInChoose: 'Vælg din virksomhed og dens annoncekonti',
    signInApprove: 'Godkend adgang i Meta',
    signInWaiting: 'Venter på Meta…',
    signInSecret:
      'Din App Secret går direkte til Nango, den login-tjeneste Poneva bruger, og gemmes krypteret der. Poneva ser den aldrig.',
    stopWaiting: 'Stop med at vente',
    doneTitle: '{name} er forbundet',
    doneAccountsYou: 'Nu kan du spørge Poneva om {count}.',
    doneAccountsCompany: 'Nu kan alle i {company} spørge Poneva om {count}.',
    doneTools: 'Meta stillede {count} til rådighed for denne konto.',
    doneTry: 'Prøv at spørge',
    doneAsk: 'Spørg om mine annoncer',
    doneDone: 'Færdig',
    doneTryReport: 'Hvordan klarede sidste uges kampagner sig?',
    doneTryReturn: 'Hvilke annoncer gav det bedste afkast i denne måned?',
    continue: 'Fortsæt',
    continueToMeta: 'Fortsæt til Meta',
    back: 'Tilbage',
    cancel: 'Annuller',
    askHelp: 'Bed Poneva om hjælp',
    askHelpPrompt:
      'Hjælp mig med at sætte vores egen Meta-app op, så jeg kan forbinde Meta Ads til Poneva.',
    chatTitle: 'Forbind for at fortsætte',
    chatIntro: 'Denne chat bliver, hvor den er.',
    chatConnectedYou: 'Forbundet for dig',
    chatConnectedCompany: 'Forbundet for {company}',
    chatUnavailable: 'Logget ind · værktøjer ikke tilgængelige',
    chatFoot: 'Log ind hos udbyderen. Når der står Forbundet, beder du Poneva om at fortsætte.',
    chatPopupBlocked: 'Din browser blokerede login-vinduet.',
    chatOpenAuthorization: 'Åbn login',
    chatFallbackBody: 'Brug denne tjeneste i dit arbejde.',
  },
  de: {
    eyebrow: 'Arbeitsbereich von {company}',
    title: 'Verbindungen',
    intro:
      'Verbinden Sie Ihre Tools, um Informationen zu finden und Dinge zu erledigen. Legen Sie für jede Verbindung fest, wer sie verwenden kann und ob Poneva lesen oder auch Änderungen vornehmen kann.',
    activity: 'Aktivität',
    backToConnections: 'Zurück zu Verbindungen',
    yoursTitle: 'Ihre Verbindungen',
    filterLabel: 'Verbindungen anzeigen',
    filterAll: 'Alle',
    filterMine: 'Nur ich',
    filterShared: 'Geteilt',
    filterAttention: 'Handlungsbedarf',
    columnConnection: 'Verbindung',
    columnWho: 'Wer sie verwenden kann',
    columnWhat: 'Poneva kann',
    columnStatus: 'Status',
    yoursLoading: 'Ihre Verbindungen werden geladen…',
    yoursEmpty: 'Noch ist nichts verbunden. Suchen Sie unten, um Ihr erstes Tool zu verbinden.',
    filterEmpty: 'Hier ist gerade nichts.',
    yoursLoadError: 'Ihre Verbindungen konnten nicht geladen werden.',
    serversLoadError: 'Ihre eigenen Server konnten nicht geladen werden.',
    databasesLoadError: 'Ihre Datenbanken konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    stateConnected: 'Verbunden',
    stateAttention: 'Anmeldung nötig',
    stateReview: 'Zugriff prüfen',
    stateUnavailable: 'Nicht verfügbar',
    stateOff: 'Ausgeschaltet',
    stateConnecting: 'Wird verbunden…',
    signInAgain: 'Erneut anmelden',
    attentionBody:
      'Die Anmeldung für {name} ist abgelaufen oder wurde widerrufen. Melden Sie sich erneut an, um die Verbindung weiter zu verwenden.',
    reviewBody:
      'Das Konto hinter {name} hat sich geändert. Prüfen Sie, wer die Verbindung verwenden kann und was Poneva tun kann, bevor sie wieder verwendet wird.',
    unavailableBody: 'Bei {name} sind Sie angemeldet, aber die Tools sind gerade nicht verfügbar.',
    offBody:
      '{name} ist ausgeschaltet. Niemand kann die Verbindung verwenden, bis sie wieder eingeschaltet wird. Es wird nichts gelöscht.',
    kindServer: 'Eigener Server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:Tabelle|other:Tabellen',
    adAccountsNoun: 'one:Werbekonto|other:Werbekonten',
    toolsNoun: 'one:Tool|other:Tools',
    close: 'Schließen',
    moreActions: 'Weitere Aktionen für {name}',
    tabOverview: 'Übersicht',
    tabAccess: 'Zugriff',
    tabActivity: 'Aktivität',
    connectedOn: 'Seit {date}',
    lastUsed: 'Zuletzt verwendet {when}',
    whatTitle: 'Was Poneva tun kann',
    readsLine: 'Liest, wenn Sie fragen',
    changesAsk: 'Fragt Sie, bevor etwas geändert wird',
    changesAllowed:
      'Nimmt Änderungen vor, ohne zu fragen. Jede Änderung wird unter „Aktivität“ protokolliert.',
    changesNever: 'Ändert nie etwas',
    readinessReady: '{count} bereit',
    change: 'Ändern',
    tryTitle: 'In einem Chat ausprobieren',
    tryPrompt: 'Wobei können Sie mir mit {name} helfen?',
    addAccount: 'Weiteres Konto hinzufügen',
    disconnect: 'Trennen',
    disconnectConfirm:
      '{name} trennen? Die Verbindung funktioniert dann in Chats, Apps und Agenten nicht mehr, bis sie wieder verbunden wird.',
    disconnectKeep: 'Beibehalten',
    nativeOnlyYou: 'Nur Sie können dieses Konto verwenden.',
    nativeEveryone: 'Alle bei {company} können dieses Konto verwenden.',
    providerPermissions: 'Die Kontoberechtigungen des Anbieters gelten weiterhin.',
    manageDatabase: 'Datenbank verwalten',
    activityLoading: 'Aktivität wird geladen…',
    activityLoadError: 'Die Aktivität konnte nicht geladen werden.',
    activityRead: 'Lesen',
    activityChange: 'Änderung',
    activitySetting: 'Einstellung',
    activityDenied: 'Abgelehnt',
    activityFailed: 'Fehlgeschlagen',
    activityUnknown: 'Ergebnis unbekannt',
    activityAll: 'Gesamte Aktivität öffnen',
    activityToday: 'Heute',
    activityYesterday: 'Gestern',
    eventCreated: 'Verbindung erstellt',
    eventShared: 'Freigabe geändert',
    eventPersonAdded: 'Person hinzugefügt',
    eventPersonRemoved: 'Person entfernt',
    eventAccessChanged: 'Zugriff geändert',
    eventRevoked: 'Zugriff ausgeschaltet',
    eventRemoved: 'Verbindung entfernt',
    eventSettings: 'Zeitlimit für Abfragen geändert',
    addTitle: 'Verbindung hinzufügen',
    integrations: 'one:Integration|other:Integrationen',
    searchLabel: 'Integrationen durchsuchen',
    searchPlaceholder: 'Integrationen durchsuchen',
    clearSearch: 'Suche löschen',
    inYours: 'In Ihren Verbindungen',
    addNew: 'Neu hinzufügen',
    resultsFor: '{count} für „{query}“',
    resultsNoun: 'one:Ergebnis|other:Ergebnisse',
    popularFirst: 'Beliebte zuerst',
    searching: 'Wird gesucht…',
    searchUnavailable: 'Die Suche nach Verbindungen ist vorübergehend nicht verfügbar.',
    noResults:
      'Noch keine Treffer. Versuchen Sie einen anderen Namen oder fragen Sie Poneva, welches Tool passt.',
    showMore: 'Mehr anzeigen',
    showMoreRetry: 'Erneut versuchen, mehr zu laden',
    loadingMore: 'Wird geladen…',
    moreFailed:
      'Weitere Integrationen konnten nicht geladen werden. Ihre Ergebnisse bleiben erhalten.',
    manageMembers: 'Festlegen, was Mitglieder verbinden können',
    manageDone: 'Fertig',
    manageHint:
      'Schalten Sie Apps für Mitglieder ein oder aus. Bereits verbundene Konten bleiben verbunden.',
    allowFor: 'Neue Verbindungen mit {name} für {company} erlauben',
    tileConnect: 'Verbinden',
    tileConnecting: 'Wird verbunden…',
    tileConnected: 'Verbunden',
    tileSignIn: 'Erneut anmelden',
    tileOff: 'Ausgeschaltet',
    tileUnavailable: 'Nicht verfügbar',
    tileNotAllowed: 'Nicht erlaubt',
    tileNotAllowedNote: 'Ihr Admin hat neue Verbindungen ausgeschaltet',
    tileAccountOnly: 'Nur Kontoverbindung · noch keine Chat-Tools',
    tileApiKey: 'API-Schlüssel erforderlich',
    tileOwnApp: 'Nutzt Ihre eigene {name}-App',
    tileAdminSetup: 'Einrichtung durch Inhaber oder Admin',
    otherWays: 'Weitere Verbindungsmöglichkeiten',
    serverTitle: 'Eigener Server',
    serverBody: 'Fügen Sie Tools von einem MCP-Server hinzu, den Ihr Team betreibt.',
    serverAction: 'Server hinzufügen',
    databaseTitle: 'Eine Datenbank',
    databaseBody: 'PostgreSQL, beschränkt auf die Schemas oder Tabellen, die Sie auswählen.',
    databaseAction: 'Verbinden',
    assistantsTitle: 'Andere KI-Assistenten',
    assistantsBody:
      'Lassen Sie Claude, ChatGPT oder einen Coding-Agenten freigegebenes Unternehmenswissen lesen.',
    assistantsAction: 'Einrichten',
    assistantsSheetIntro:
      'Assistenten außerhalb von Poneva, die freigegebenes Unternehmenswissen lesen, und die Coding-Agenten, die für Sie arbeiten.',
    footnote:
      'Sie genehmigen Änderungen, bevor sie ausgeführt werden. Integrationen über Pipedream; welche Tools eine App bietet, ist unterschiedlich.',
    askBody:
      'Nicht sicher, welche Sie brauchen? Poneva kann Verbindungen passend zu der Arbeit vorschlagen, die Sie beschreiben.',
    askAction: 'Im Chat fragen',
    askPrompt:
      'Welche Unternehmensquellen wären für unsere Arbeit am nützlichsten, um sie zu verbinden – basierend auf dem, was Sie über uns wissen?',
    compactTitle: 'Bringen Sie Ihre Tools mit.',
    forYou: 'Für Sie',
    browseAll: 'Alle Tools durchsuchen',
    selectedForFocus: 'Passend zu Ihrem Fokus ausgewählt',
    popupBlocked: 'Der Browser hat das Anmeldefenster blockiert.',
    popupOpen: 'Anmeldeseite öffnen',
    popupKeepChecking: 'Diese Seite prüft weiter.',
    noticeAdded: 'Verbindung hinzugefügt.',
    noticeAddedReturn:
      'Verbindung hinzugefügt. Sie können diesen Tab schließen und zu Ihrer Arbeit zurückkehren.',
    noticeSignInUnfinished:
      'Die Anmeldung wurde nicht abgeschlossen. Wählen Sie beim Server „Erneut anmelden“, um es noch einmal zu versuchen.',
    noticeChecked: 'Verbindung erfolgreich geprüft.',
    noticeUnreachable: 'Der Server war noch nicht erreichbar.',
    noticeSignInReady: 'Eine Anmeldeseite ist in einem neuen Tab bereit.',
    noticeAuthorized: 'Die Autorisierung ist abgeschlossen.',
    noticeDatabaseRemoved: 'PostgreSQL wurde getrennt.',
    noticeStoreConnected: 'Shopify-Shop verbunden.',
    noticeStoreChooseAccess:
      'Shopify-Shop verbunden. Öffnen Sie ihn unter „Ihre Verbindungen“, um festzulegen, wer ihn verwenden kann.',
    allowFailed: '{name} konnte nicht aktualisiert werden. Versuchen Sie es erneut.',
    accessUnknown: 'Zugriffsdetails sind nicht verfügbar.',
    metaStep: 'Schritt {step} von {total}',
    stepsLabel: 'Einrichtungsschritte',
    stepChoose: 'Auswählen',
    stepPrepare: 'Vorbereiten',
    stepAccess: 'Zugriff',
    stepSignIn: 'Anmelden',
    metaChooseTitle: 'Meta Ads verbinden',
    metaChooseIntro:
      'Holen Sie Ihre Meta-Werbekonten in Poneva. Wählen Sie zuerst, was Poneva damit tun darf.',
    metaReporting: 'Berichte',
    metaReportingTag: 'Nur lesen',
    metaReportingBody:
      'Liest Werbekonten, Kampagnen, Anzeigengruppen, Anzeigen und Ergebnisse sowie Ihre Facebook-Seiten. Poneva ändert nie etwas in Meta.',
    metaManage: 'Berichte und Anzeigenverwaltung',
    metaManageTag: 'Lesen und Schreiben',
    metaManageBody:
      'Erstellt und aktualisiert außerdem Kampagnen, Anzeigen und Kataloge mit Metas eigenen Tools. Sie entscheiden, ob Poneva vor jeder Änderung fragt.',
    metaOwnApp:
      'Meta verlangt, dass sich jedes Unternehmen über eine eigene Meta-App verbindet. Der nächste Schritt führt Sie einmalig durch die Einrichtung.',
    metaConnectedAlready: 'Verbunden',
    prepareTitle: 'Meta-App vorbereiten',
    prepareIntro:
      'Das erledigen Sie einmalig in Meta for Developers. Sie brauchen Admin-Zugriff auf das Business-Konto Ihres Unternehmens bei Meta.',
    prepareAppTitle: 'Meta-App erstellen oder öffnen',
    prepareAppReporting: 'Aktivieren Sie die Marketing API und Facebook Login for Business.',
    prepareAppManage:
      'Fügen Sie den Anwendungsfall „Create and manage ads with the ads MCP“ hinzu.',
    prepareAppOpen: 'Meta for Developers öffnen',
    prepareRedirectTitle: 'Anmeldeadresse von Poneva hinzufügen',
    prepareRedirectBody:
      'Fügen Sie sie in Facebook Login for Business unter Valid OAuth Redirect URIs ein. Behalten Sie die vorhandenen Adressen und speichern Sie dann.',
    prepareSecretTitle: 'App ID und App Secret bereithalten',
    prepareSecretBody:
      'Beide finden Sie in Ihrer App unter App settings → Basic. Sie fügen sie im letzten Schritt in ein sicheres Fenster ein, niemals in Poneva.',
    prepareDone: 'Erledigt',
    copy: 'Kopieren',
    copied: 'Kopiert',
    copyAddress: 'Anmeldeadresse kopieren',
    copyManual: 'Markieren Sie die Adresse und kopieren Sie sie mit der Tastatur.',
    permissionsTitle: 'Diese Berechtigungen bestätigen Sie bei Meta',
    permissionsNote:
      'Meta verlangt eventuell eine Verifizierung Ihres Unternehmens, bevor die App Werbekonten außer Ihren eigenen erreichen kann.',
    scopeAdsRead: 'Ihre Anzeigen und deren Ergebnisse lesen',
    scopeAdsManagement: 'Anzeigen erstellen und ändern',
    scopeAdsMcp: 'Metas Werbetools verwenden',
    scopeBusiness: 'Ihre Business-Portfolios sehen',
    scopePages: 'Die von Ihnen verwalteten Seiten auflisten',
    scopePageEngagement: 'Seitendetails und Interaktionen lesen',
    scopeInstagram: 'Grundlegende Instagram-Kontodaten lesen',
    scopeCatalog: 'Produktkataloge verwalten',
    guide: 'Schritt-für-Schritt-Anleitung',
    metaAccessTitle: 'Wer kann Meta Ads verwenden?',
    metaAccessIntro:
      'Werbekonten gehören meist dem ganzen Unternehmen. Sie können das jederzeit ändern.',
    metaReadOnlyTitle: 'Nur lesen',
    metaReadOnlyBody:
      'Berichte ändern nie etwas in Meta. Damit Poneva Anzeigen verwalten kann, wählen Sie im ersten Schritt die Anzeigenverwaltung.',
    signInTitle: 'Mit Meta anmelden',
    signInIntro:
      'Ein sicheres Fenster öffnet sich. Schließen Sie den Vorgang dort ab, diese Seite aktualisiert sich von selbst.',
    signInWindow: 'Im sicheren Fenster',
    signInPaste: 'App ID und App Secret einfügen',
    signInChoose: 'Unternehmen und dessen Werbekonten auswählen',
    signInApprove: 'Zugriff in Meta genehmigen',
    signInWaiting: 'Warten auf Meta…',
    signInSecret:
      'Ihr App Secret geht direkt an Nango, den Anmeldedienst, den Poneva nutzt, und wird dort verschlüsselt gespeichert. Poneva sieht es nie.',
    stopWaiting: 'Nicht mehr warten',
    doneTitle: '{name} ist verbunden',
    doneAccountsYou: 'Sie können Poneva jetzt Fragen zu {count} stellen.',
    doneAccountsCompany: 'Alle bei {company} können Poneva jetzt Fragen zu {count} stellen.',
    doneTools: 'Meta hat für dieses Konto {count} bereitgestellt.',
    doneTry: 'Fragen Sie zum Beispiel',
    doneAsk: 'Nach meinen Anzeigen fragen',
    doneDone: 'Fertig',
    doneTryReport: 'Wie haben die Kampagnen der letzten Woche abgeschnitten?',
    doneTryReturn: 'Welche Anzeigen haben diesen Monat die beste Rendite gebracht?',
    continue: 'Weiter',
    continueToMeta: 'Weiter zu Meta',
    back: 'Zurück',
    cancel: 'Abbrechen',
    askHelp: 'Poneva um Hilfe bitten',
    askHelpPrompt:
      'Hilf mir, unsere eigene Meta-App einzurichten, damit ich Meta Ads mit Poneva verbinden kann.',
    chatTitle: 'Zum Fortfahren verbinden',
    chatIntro: 'Dieser Chat bleibt, wo er ist.',
    chatConnectedYou: 'Für Sie verbunden',
    chatConnectedCompany: 'Für {company} verbunden',
    chatUnavailable: 'Angemeldet · Tools nicht verfügbar',
    chatFoot:
      'Melden Sie sich beim Anbieter an. Sobald „Verbunden“ angezeigt wird, bitten Sie Poneva, fortzufahren.',
    chatPopupBlocked: 'Ihr Browser hat das Anmeldefenster blockiert.',
    chatOpenAuthorization: 'Anmeldung öffnen',
    chatFallbackBody: 'Nutzen Sie diesen Dienst für Ihre Arbeit.',
  },
  el: {
    eyebrow: 'Χώρος εργασίας της {company}',
    title: 'Συνδέσεις',
    intro:
      'Συνδέστε τα εργαλεία σας για να βρίσκετε πληροφορίες και να ολοκληρώνετε εργασίες. Επιλέξτε ποιος μπορεί να χρησιμοποιεί κάθε σύνδεση και αν το Poneva μπορεί να διαβάζει ή και να κάνει αλλαγές.',
    activity: 'Δραστηριότητα',
    backToConnections: 'Πίσω στις Συνδέσεις',
    yoursTitle: 'Οι συνδέσεις σας',
    filterLabel: 'Εμφάνιση συνδέσεων',
    filterAll: 'Όλες',
    filterMine: 'Μόνο εγώ',
    filterShared: 'Κοινόχρηστες',
    filterAttention: 'Χρειάζονται προσοχή',
    columnConnection: 'Σύνδεση',
    columnWho: 'Ποιος μπορεί να τη χρησιμοποιεί',
    columnWhat: 'Τι μπορεί να κάνει το Poneva',
    columnStatus: 'Κατάσταση',
    yoursLoading: 'Φόρτωση των συνδέσεών σας…',
    yoursEmpty:
      'Δεν έχει συνδεθεί τίποτα ακόμη. Κάντε αναζήτηση παρακάτω για να συνδέσετε το πρώτο σας εργαλείο.',
    filterEmpty: 'Δεν υπάρχει τίποτα εδώ αυτή τη στιγμή.',
    yoursLoadError: 'Δεν ήταν δυνατή η φόρτωση των συνδέσεών σας.',
    serversLoadError: 'Δεν ήταν δυνατή η φόρτωση των δικών σας διακομιστών.',
    databasesLoadError: 'Δεν ήταν δυνατή η φόρτωση των βάσεων δεδομένων σας.',
    retry: 'Δοκιμάστε ξανά',
    stateConnected: 'Συνδεδεμένη',
    stateAttention: 'Απαιτείται είσοδος',
    stateReview: 'Ελέγξτε την πρόσβαση',
    stateUnavailable: 'Μη διαθέσιμη',
    stateOff: 'Απενεργοποιημένη',
    stateConnecting: 'Σύνδεση…',
    signInAgain: 'Συνδεθείτε ξανά',
    attentionBody:
      'Η είσοδος στο {name} έληξε ή ανακλήθηκε. Συνδεθείτε ξανά για να συνεχίσετε να το χρησιμοποιείτε.',
    reviewBody:
      'Ο λογαριασμός πίσω από το {name} άλλαξε. Ελέγξτε ποιος μπορεί να το χρησιμοποιεί και τι μπορεί να κάνει το Poneva πριν χρησιμοποιηθεί ξανά.',
    unavailableBody:
      'Έχετε συνδεθεί στο {name}, αλλά τα εργαλεία του δεν είναι διαθέσιμα αυτή τη στιγμή.',
    offBody:
      'Το {name} είναι απενεργοποιημένο. Κανείς δεν μπορεί να το χρησιμοποιήσει μέχρι να ενεργοποιηθεί ξανά. Δεν διαγράφεται τίποτα.',
    kindServer: 'Δικός σας διακομιστής',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:πίνακας|other:πίνακες',
    adAccountsNoun: 'one:διαφημιστικός λογαριασμός|other:διαφημιστικοί λογαριασμοί',
    toolsNoun: 'one:εργαλείο|other:εργαλεία',
    close: 'Κλείσιμο',
    moreActions: 'Περισσότερες ενέργειες για το {name}',
    tabOverview: 'Επισκόπηση',
    tabAccess: 'Πρόσβαση',
    tabActivity: 'Δραστηριότητα',
    connectedOn: 'Από {date}',
    lastUsed: 'Τελευταία χρήση {when}',
    whatTitle: 'Τι μπορεί να κάνει το Poneva',
    readsLine: 'Διαβάζει όταν το ζητάτε',
    changesAsk: 'Σας ρωτά πριν αλλάξει οτιδήποτε',
    changesAllowed: 'Κάνει αλλαγές χωρίς να ρωτά. Κάθε αλλαγή καταγράφεται στη Δραστηριότητα.',
    changesNever: 'Δεν αλλάζει ποτέ τίποτα',
    readinessReady: '{count} προς χρήση',
    change: 'Αλλαγή',
    tryTitle: 'Δοκιμάστε το σε μια συνομιλία',
    tryPrompt: 'Σε τι μπορείς να με βοηθήσεις με το {name};',
    addAccount: 'Προσθήκη άλλου λογαριασμού',
    disconnect: 'Αποσύνδεση',
    disconnectConfirm:
      'Αποσύνδεση του {name}; Θα σταματήσει να λειτουργεί σε συνομιλίες, εφαρμογές και πράκτορες μέχρι να συνδεθεί ξανά.',
    disconnectKeep: 'Διατήρηση',
    nativeOnlyYou: 'Μόνο εσείς μπορείτε να χρησιμοποιείτε αυτόν τον λογαριασμό.',
    nativeEveryone: 'Όλοι στην εταιρεία {company} μπορούν να χρησιμοποιούν αυτόν τον λογαριασμό.',
    providerPermissions: 'Τα δικαιώματα του λογαριασμού στον πάροχο εξακολουθούν να ισχύουν.',
    manageDatabase: 'Διαχείριση βάσης δεδομένων',
    activityLoading: 'Φόρτωση δραστηριότητας…',
    activityLoadError: 'Δεν ήταν δυνατή η φόρτωση της δραστηριότητας.',
    activityRead: 'Ανάγνωση',
    activityChange: 'Αλλαγή',
    activitySetting: 'Ρύθμιση',
    activityDenied: 'Απορρίφθηκε',
    activityFailed: 'Απέτυχε',
    activityUnknown: 'Άγνωστο αποτέλεσμα',
    activityAll: 'Άνοιγμα όλης της δραστηριότητας',
    activityToday: 'Σήμερα',
    activityYesterday: 'Χθες',
    eventCreated: 'Η σύνδεση δημιουργήθηκε',
    eventShared: 'Η κοινοποίηση άλλαξε',
    eventPersonAdded: 'Προστέθηκε άτομο',
    eventPersonRemoved: 'Αφαιρέθηκε άτομο',
    eventAccessChanged: 'Η πρόσβαση άλλαξε',
    eventRevoked: 'Η πρόσβαση απενεργοποιήθηκε',
    eventRemoved: 'Η σύνδεση καταργήθηκε',
    eventSettings: 'Το χρονικό όριο ερωτημάτων άλλαξε',
    addTitle: 'Προσθήκη σύνδεσης',
    integrations: 'one:ενσωμάτωση|other:ενσωματώσεις',
    searchLabel: 'Αναζήτηση ενσωματώσεων',
    searchPlaceholder: 'Αναζήτηση ενσωματώσεων',
    clearSearch: 'Απαλοιφή αναζήτησης',
    inYours: 'Στις συνδέσεις σας',
    addNew: 'Προσθήκη νέας',
    resultsFor: '{count} για «{query}»',
    resultsNoun: 'one:αποτέλεσμα|other:αποτελέσματα',
    popularFirst: 'Πρώτα τα δημοφιλή',
    searching: 'Αναζήτηση…',
    searchUnavailable: 'Η αναζήτηση συνδέσεων δεν είναι προσωρινά διαθέσιμη.',
    noResults:
      'Δεν ταιριάζει τίποτα ακόμη. Δοκιμάστε άλλο όνομα ή ρωτήστε το Poneva ποιο εργαλείο ταιριάζει.',
    showMore: 'Εμφάνιση περισσότερων',
    showMoreRetry: 'Νέα προσπάθεια φόρτωσης περισσότερων',
    loadingMore: 'Φόρτωση…',
    moreFailed:
      'Δεν ήταν δυνατή η φόρτωση περισσότερων ενσωματώσεων. Τα αποτελέσματά σας παραμένουν εδώ.',
    manageMembers: 'Επιλέξτε τι μπορούν να συνδέουν τα μέλη',
    manageDone: 'Τέλος',
    manageHint:
      'Ενεργοποιήστε ή απενεργοποιήστε εφαρμογές για τα μέλη. Οι λογαριασμοί που είναι ήδη συνδεδεμένοι παραμένουν συνδεδεμένοι.',
    allowFor: 'Να επιτρέπονται νέες συνδέσεις με το {name} για την εταιρεία {company}',
    tileConnect: 'Σύνδεση',
    tileConnecting: 'Σύνδεση…',
    tileConnected: 'Συνδεδεμένη',
    tileSignIn: 'Συνδεθείτε ξανά',
    tileOff: 'Απενεργοποιημένη',
    tileUnavailable: 'Μη διαθέσιμη',
    tileNotAllowed: 'Δεν επιτρέπεται',
    tileNotAllowedNote: 'Ο διαχειριστής σας απενεργοποίησε τις νέες συνδέσεις',
    tileAccountOnly: 'Μόνο σύνδεση λογαριασμού · χωρίς εργαλεία συνομιλίας ακόμη',
    tileApiKey: 'Απαιτείται κλειδί API',
    tileOwnApp: 'Χρησιμοποιεί τη δική σας εφαρμογή {name}',
    tileAdminSetup: 'Ρύθμιση από κάτοχο ή διαχειριστή',
    otherWays: 'Άλλοι τρόποι σύνδεσης',
    serverTitle: 'Δικός σας διακομιστής',
    serverBody: 'Προσθέστε εργαλεία από έναν διακομιστή MCP που διαχειρίζεται η ομάδα σας.',
    serverAction: 'Προσθήκη διακομιστή',
    databaseTitle: 'Μια βάση δεδομένων',
    databaseBody: 'PostgreSQL, περιορισμένη στα σχήματα ή στους πίνακες που επιλέγετε.',
    databaseAction: 'Σύνδεση',
    assistantsTitle: 'Άλλοι βοηθοί AI',
    assistantsBody:
      'Επιτρέψτε στο Claude, στο ChatGPT ή σε έναν πράκτορα προγραμματισμού να διαβάζει εγκεκριμένη εταιρική γνώση.',
    assistantsAction: 'Ρύθμιση',
    assistantsSheetIntro:
      'Βοηθοί εκτός Poneva που διαβάζουν εγκεκριμένη εταιρική γνώση και οι πράκτορες προγραμματισμού που δουλεύουν για εσάς.',
    footnote:
      'Εγκρίνετε τις αλλαγές πριν εκτελεστούν. Οι ενσωματώσεις παρέχονται από το Pipedream· τα εργαλεία κάθε εφαρμογής διαφέρουν.',
    askBody:
      'Δεν είστε σίγουροι ποια χρειάζεστε; Το Poneva μπορεί να προτείνει συνδέσεις με βάση την εργασία που περιγράφετε.',
    askAction: 'Ρωτήστε στη συνομιλία',
    askPrompt:
      'Ποιες εταιρικές πηγές θα ήταν πιο χρήσιμο να συνδέσουμε για τη δουλειά μας, με βάση όσα ξέρεις για εμάς;',
    compactTitle: 'Φέρτε μαζί τα εργαλεία σας.',
    forYou: 'Για εσάς',
    browseAll: 'Περιήγηση σε όλα τα εργαλεία',
    selectedForFocus: 'Επιλεγμένα για την εστίασή σας',
    popupBlocked: 'Το πρόγραμμα περιήγησης μπλόκαρε το παράθυρο σύνδεσης.',
    popupOpen: 'Άνοιγμα της σελίδας σύνδεσης',
    popupKeepChecking: 'Αυτή η σελίδα συνεχίζει να ελέγχει.',
    noticeAdded: 'Η σύνδεση προστέθηκε.',
    noticeAddedReturn:
      'Η σύνδεση προστέθηκε. Μπορείτε να κλείσετε αυτή την καρτέλα και να επιστρέψετε στη δουλειά σας.',
    noticeSignInUnfinished:
      'Η είσοδος δεν ολοκληρώθηκε. Επιλέξτε «Συνδεθείτε ξανά» στον διακομιστή για να δοκιμάσετε ξανά.',
    noticeChecked: 'Ο έλεγχος της σύνδεσης ολοκληρώθηκε με επιτυχία.',
    noticeUnreachable: 'Δεν ήταν ακόμη δυνατή η πρόσβαση στον διακομιστή.',
    noticeSignInReady: 'Μια σελίδα σύνδεσης είναι έτοιμη σε νέα καρτέλα.',
    noticeAuthorized: 'Η εξουσιοδότηση ολοκληρώθηκε.',
    noticeDatabaseRemoved: 'Το PostgreSQL αποσυνδέθηκε.',
    noticeStoreConnected: 'Το κατάστημα Shopify συνδέθηκε.',
    noticeStoreChooseAccess:
      'Το κατάστημα Shopify συνδέθηκε. Ανοίξτε το από την ενότητα «Οι συνδέσεις σας» για να επιλέξετε ποιος μπορεί να το χρησιμοποιεί.',
    allowFailed: 'Δεν ήταν δυνατή η ενημέρωση του {name}. Δοκιμάστε ξανά.',
    accessUnknown: 'Τα στοιχεία πρόσβασης δεν είναι διαθέσιμα.',
    metaStep: 'Βήμα {step} από {total}',
    stepsLabel: 'Βήματα ρύθμισης',
    stepChoose: 'Επιλογή',
    stepPrepare: 'Προετοιμασία',
    stepAccess: 'Πρόσβαση',
    stepSignIn: 'Σύνδεση',
    metaChooseTitle: 'Σύνδεση Meta Ads',
    metaChooseIntro:
      'Φέρτε τους διαφημιστικούς λογαριασμούς σας από τη Meta στο Poneva. Πρώτα, επιλέξτε τι μπορεί να κάνει το Poneva με αυτούς.',
    metaReporting: 'Αναφορές',
    metaReportingTag: 'Μόνο ανάγνωση',
    metaReportingBody:
      'Διαβάζει διαφημιστικούς λογαριασμούς, καμπάνιες, σύνολα διαφημίσεων, διαφημίσεις και αποτελέσματα, καθώς και τις Σελίδες σας στο Facebook. Το Poneva δεν αλλάζει ποτέ τίποτα στη Meta.',
    metaManage: 'Αναφορές και διαχείριση διαφημίσεων',
    metaManageTag: 'Ανάγνωση και εγγραφή',
    metaManageBody:
      'Επιπλέον δημιουργεί και ενημερώνει καμπάνιες, διαφημίσεις και καταλόγους με τα εργαλεία της ίδιας της Meta. Εσείς επιλέγετε αν το Poneva θα ρωτά πριν από κάθε αλλαγή.',
    metaOwnApp:
      'Η Meta ζητά από κάθε επιχείρηση να συνδέεται μέσω της δικής της εφαρμογής Meta. Το επόμενο βήμα σάς καθοδηγεί στη ρύθμισή της, μία φορά.',
    metaConnectedAlready: 'Συνδεδεμένη',
    prepareTitle: 'Προετοιμάστε την εφαρμογή σας στη Meta',
    prepareIntro:
      'Κάντε το μία φορά στο Meta for Developers. Χρειάζεστε πρόσβαση διαχειριστή στο επιχειρηματικό χαρτοφυλάκιο της εταιρείας σας στη Meta.',
    prepareAppTitle: 'Δημιουργήστε ή ανοίξτε την εφαρμογή σας στη Meta',
    prepareAppReporting: 'Ενεργοποιήστε το Marketing API και το Facebook Login for Business.',
    prepareAppManage: 'Προσθέστε την περίπτωση χρήσης «Create and manage ads with the ads MCP».',
    prepareAppOpen: 'Άνοιγμα του Meta for Developers',
    prepareRedirectTitle: 'Προσθέστε τη διεύθυνση σύνδεσης του Poneva',
    prepareRedirectBody:
      'Στο Facebook Login for Business, επικολλήστε τη στο Valid OAuth Redirect URIs. Κρατήστε τις διευθύνσεις που υπάρχουν ήδη και, στη συνέχεια, αποθηκεύστε.',
    prepareSecretTitle: 'Έχετε έτοιμα το App ID και το App Secret',
    prepareSecretBody:
      'Βρίσκονται και τα δύο στην εφαρμογή σας, στο App settings → Basic. Θα τα επικολλήσετε σε ένα ασφαλές παράθυρο στο τελευταίο βήμα, ποτέ στο Poneva.',
    prepareDone: 'Έγινε',
    copy: 'Αντιγραφή',
    copied: 'Αντιγράφηκε',
    copyAddress: 'Αντιγραφή της διεύθυνσης σύνδεσης',
    copyManual: 'Επιλέξτε τη διεύθυνση και αντιγράψτε τη με το πληκτρολόγιο.',
    permissionsTitle: 'Τι θα σας ζητήσει να εγκρίνετε η Meta',
    permissionsNote:
      'Η Meta μπορεί να ζητήσει επαλήθευση της επιχείρησής σας προτού η εφαρμογή αποκτήσει πρόσβαση σε διαφημιστικούς λογαριασμούς πέρα από τους δικούς σας.',
    scopeAdsRead: 'Ανάγνωση των διαφημίσεών σας και των αποτελεσμάτων τους',
    scopeAdsManagement: 'Δημιουργία και αλλαγή διαφημίσεων',
    scopeAdsMcp: 'Χρήση των διαφημιστικών εργαλείων της Meta',
    scopeBusiness: 'Προβολή των επιχειρηματικών σας χαρτοφυλακίων',
    scopePages: 'Λίστα των Σελίδων που διαχειρίζεστε',
    scopePageEngagement: 'Ανάγνωση στοιχείων και αλληλεπίδρασης Σελίδων',
    scopeInstagram: 'Ανάγνωση βασικών στοιχείων λογαριασμού Instagram',
    scopeCatalog: 'Διαχείριση καταλόγων προϊόντων',
    guide: 'Οδηγός βήμα προς βήμα',
    metaAccessTitle: 'Ποιος μπορεί να χρησιμοποιεί το Meta Ads;',
    metaAccessIntro:
      'Οι διαφημιστικοί λογαριασμοί συνήθως ανήκουν σε ολόκληρη την εταιρεία. Μπορείτε να το αλλάξετε οποιαδήποτε στιγμή.',
    metaReadOnlyTitle: 'Μόνο ανάγνωση',
    metaReadOnlyBody:
      'Οι αναφορές δεν αλλάζουν ποτέ τίποτα στη Meta. Για να επιτρέψετε στο Poneva να διαχειρίζεται διαφημίσεις, επιλέξτε διαχείριση διαφημίσεων στο πρώτο βήμα.',
    signInTitle: 'Σύνδεση με τη Meta',
    signInIntro:
      'Ανοίγει ένα ασφαλές παράθυρο. Ολοκληρώστε εκεί και αυτή η σελίδα θα ενημερωθεί μόνη της.',
    signInWindow: 'Στο ασφαλές παράθυρο',
    signInPaste: 'Επικολλήστε το App ID και το App Secret',
    signInChoose: 'Επιλέξτε την επιχείρησή σας και τους διαφημιστικούς λογαριασμούς της',
    signInApprove: 'Εγκρίνετε την πρόσβαση στη Meta',
    signInWaiting: 'Αναμονή για τη Meta…',
    signInSecret:
      'Το App Secret σας πηγαίνει απευθείας στο Nango, την υπηρεσία σύνδεσης που χρησιμοποιεί το Poneva, και αποθηκεύεται εκεί κρυπτογραφημένο. Το Poneva δεν το βλέπει ποτέ.',
    stopWaiting: 'Διακοπή αναμονής',
    doneTitle: 'Το {name} συνδέθηκε',
    doneAccountsYou: '{count} είναι πλέον στη διάθεσή σας για ερωτήσεις στο Poneva.',
    doneAccountsCompany:
      '{count} είναι πλέον στη διάθεση όλων στην εταιρεία {company} για ερωτήσεις στο Poneva.',
    doneTools: 'Η Meta παρείχε {count} για αυτόν τον λογαριασμό.',
    doneTry: 'Δοκιμάστε να ρωτήσετε',
    doneAsk: 'Ερώτηση για τις διαφημίσεις μου',
    doneDone: 'Τέλος',
    doneTryReport: 'Πώς πήγαν οι καμπάνιες της περασμένης εβδομάδας;',
    doneTryReturn: 'Ποιες διαφημίσεις έφεραν την καλύτερη απόδοση αυτόν τον μήνα;',
    continue: 'Συνέχεια',
    continueToMeta: 'Συνέχεια στη Meta',
    back: 'Πίσω',
    cancel: 'Ακύρωση',
    askHelp: 'Ζητήστε βοήθεια από το Poneva',
    askHelpPrompt:
      'Βοήθησέ με να ρυθμίσω τη δική μας εφαρμογή Meta, ώστε να μπορέσω να συνδέσω το Meta Ads με το Poneva.',
    chatTitle: 'Συνδέστε για να συνεχίσετε',
    chatIntro: 'Αυτή η συνομιλία μένει εκεί που είναι.',
    chatConnectedYou: 'Συνδέθηκε για εσάς',
    chatConnectedCompany: 'Συνδέθηκε για την εταιρεία {company}',
    chatUnavailable: 'Έχετε συνδεθεί · τα εργαλεία δεν είναι διαθέσιμα',
    chatFoot:
      'Συνδεθείτε στον πάροχο. Όταν εμφανιστεί «Συνδεδεμένη», ζητήστε από το Poneva να συνεχίσει.',
    chatPopupBlocked: 'Το πρόγραμμα περιήγησής σας μπλόκαρε το παράθυρο σύνδεσης.',
    chatOpenAuthorization: 'Άνοιγμα σύνδεσης',
    chatFallbackBody: 'Χρησιμοποιήστε αυτή την υπηρεσία στη δουλειά σας.',
  },
  en: {
    // Page
    eyebrow: '{company} workspace',
    title: 'Connections',
    intro:
      'Connect your tools to find information and get things done. Choose who can use each connection and whether Poneva can read or make changes.',
    activity: 'Activity',
    backToConnections: 'Back to Connections',
    // Your connections
    yoursTitle: 'Your connections',
    filterLabel: 'Show connections',
    filterAll: 'All',
    filterMine: 'Only me',
    filterShared: 'Shared',
    filterAttention: 'Needs attention',
    columnConnection: 'Connection',
    columnWho: 'Who can use it',
    columnWhat: 'Poneva can',
    columnStatus: 'Status',
    yoursLoading: 'Loading your connections…',
    yoursEmpty: 'Nothing is connected yet. Search below to connect your first tool.',
    filterEmpty: 'Nothing here right now.',
    yoursLoadError: 'Your connections could not be loaded.',
    serversLoadError: 'Your own servers could not be loaded.',
    databasesLoadError: 'Your databases could not be loaded.',
    retry: 'Try again',
    // States, the one word for each everywhere
    stateConnected: 'Connected',
    stateAttention: 'Needs sign-in',
    stateReview: 'Review access',
    stateUnavailable: 'Unavailable',
    stateOff: 'Turned off',
    stateConnecting: 'Connecting…',
    signInAgain: 'Sign in again',
    attentionBody: 'The sign-in for {name} expired or was revoked. Sign in again to keep using it.',
    reviewBody:
      'The account behind {name} changed. Review who can use it and what Poneva can do before it is used again.',
    unavailableBody: '{name} is signed in, but its tools aren’t available right now.',
    offBody:
      '{name} is turned off. Nobody can use it until it’s turned back on. Nothing is deleted.',
    // Identity lines
    kindServer: 'Your own server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:table|other:tables',
    adAccountsNoun: 'one:ad account|other:ad accounts',
    toolsNoun: 'one:tool|other:tools',
    // The connection panel
    close: 'Close',
    moreActions: 'More actions for {name}',
    tabOverview: 'Overview',
    tabAccess: 'Access',
    tabActivity: 'Activity',
    connectedOn: 'Since {date}',
    lastUsed: 'Last used {when}',
    whatTitle: 'What Poneva can do',
    readsLine: 'Reads when you ask',
    changesAsk: 'Asks you before it changes anything',
    changesAllowed: 'Makes changes without asking. Each one is recorded in Activity.',
    changesNever: 'Never changes anything',
    readinessReady: '{count} ready',
    change: 'Change',
    tryTitle: 'Try it in a chat',
    tryPrompt: 'What can you help me with using {name}?',
    addAccount: 'Add another account',
    disconnect: 'Disconnect',
    disconnectConfirm:
      'Disconnect {name}? It stops working in chats, Apps and Agents until it is connected again.',
    disconnectKeep: 'Keep',
    nativeOnlyYou: 'Only you can use this account.',
    nativeEveryone: 'Everyone at {company} can use this account.',
    providerPermissions: 'The provider’s own account permissions still apply.',
    manageDatabase: 'Manage database',
    activityLoading: 'Loading activity…',
    activityLoadError: 'Activity could not be loaded.',
    activityRead: 'Read',
    activityChange: 'Change',
    activitySetting: 'Setting',
    activityDenied: 'Denied',
    activityFailed: 'Failed',
    activityUnknown: 'Unknown result',
    activityAll: 'Open all activity',
    activityToday: 'Today',
    activityYesterday: 'Yesterday',
    eventCreated: 'Connection created',
    eventShared: 'Sharing changed',
    eventPersonAdded: 'Person added',
    eventPersonRemoved: 'Person removed',
    eventAccessChanged: 'Access changed',
    eventRevoked: 'Access turned off',
    eventRemoved: 'Connection removed',
    eventSettings: 'Query time limit changed',
    // Add a connection (the catalogue)
    addTitle: 'Add a connection',
    integrations: 'one:integration|other:integrations',
    searchLabel: 'Search integrations',
    searchPlaceholder: 'Search integrations',
    clearSearch: 'Clear search',
    inYours: 'In your connections',
    addNew: 'Add new',
    resultsFor: '{count} for “{query}”',
    resultsNoun: 'one:result|other:results',
    popularFirst: 'Popular first',
    searching: 'Searching…',
    searchUnavailable: 'Connection search is temporarily unavailable.',
    noResults: 'Nothing matches that yet. Try another name, or ask Poneva which tool fits.',
    showMore: 'Show more',
    showMoreRetry: 'Try loading more again',
    loadingMore: 'Loading…',
    moreFailed: 'More integrations couldn’t load. Your results are still here.',
    manageMembers: 'Choose what members can connect',
    manageDone: 'Done',
    manageHint:
      'Turn apps on or off for members. Accounts that are already connected stay connected.',
    allowFor: 'Allow new {name} connections for {company}',
    tileConnect: 'Connect',
    tileConnecting: 'Connecting…',
    tileConnected: 'Connected',
    tileSignIn: 'Sign in again',
    tileOff: 'Turned off',
    tileUnavailable: 'Unavailable',
    tileNotAllowed: 'Not allowed',
    tileNotAllowedNote: 'Your admin turned off new connections',
    tileAccountOnly: 'Account connection only · no chat tools yet',
    tileApiKey: 'API key required',
    tileOwnApp: 'Uses your own {name} app',
    tileAdminSetup: 'Owner or Admin setup',
    // Other ways to connect
    otherWays: 'Other ways to connect',
    serverTitle: 'Your own server',
    serverBody: 'Add tools from an MCP server your team runs.',
    serverAction: 'Add server',
    databaseTitle: 'A database',
    databaseBody: 'PostgreSQL, limited to the schemas or tables you choose.',
    databaseAction: 'Connect',
    assistantsTitle: 'Other AI assistants',
    assistantsBody: 'Let Claude, ChatGPT or a coding agent read approved company knowledge.',
    assistantsAction: 'Set up',
    assistantsSheetIntro:
      'Assistants outside Poneva that read approved company knowledge, and the coding agents that work for you.',
    footnote:
      'You approve changes before they run. Integrations powered by Pipedream; the tools each app offers vary.',
    askBody:
      'Not sure which one you need? Poneva can suggest connections from the work you describe.',
    askAction: 'Ask in chat',
    askPrompt:
      'Which company sources would be most useful to connect for our work, based on what you know about us?',
    // Onboarding (the compact catalogue)
    compactTitle: 'Bring your tools along.',
    forYou: 'For you',
    browseAll: 'Browse all tools',
    selectedForFocus: 'Selected for your focus',
    // Connecting
    popupBlocked: 'The browser blocked the sign-in window.',
    popupOpen: 'Open the sign-in page',
    popupKeepChecking: 'This page keeps checking.',
    // Notices after an action
    noticeAdded: 'Connection added.',
    noticeAddedReturn: 'Connection added. You can close this tab and return to your work.',
    noticeSignInUnfinished: 'Sign-in did not finish. Use Sign in again on the server to try again.',
    noticeChecked: 'Connection checked successfully.',
    noticeUnreachable: 'The server could not be reached yet.',
    noticeSignInReady: 'A sign-in page is ready in a new tab.',
    noticeAuthorized: 'Authorization is complete.',
    noticeDatabaseRemoved: 'PostgreSQL was disconnected.',
    noticeStoreConnected: 'Shopify store connected.',
    noticeStoreChooseAccess:
      'Shopify store connected. Open it in Your connections to choose who can use it.',
    allowFailed: 'Could not update {name}. Try again.',
    accessUnknown: 'Access details are unavailable.',
    // Meta Ads setup
    metaStep: 'Step {step} of {total}',
    stepsLabel: 'Setup steps',
    stepChoose: 'Choose',
    stepPrepare: 'Prepare',
    stepAccess: 'Access',
    stepSignIn: 'Sign in',
    metaChooseTitle: 'Connect Meta Ads',
    metaChooseIntro:
      'Bring your Meta ad accounts into Poneva. First, choose what Poneva may do with them.',
    metaReporting: 'Reporting',
    metaReportingTag: 'Read only',
    metaReportingBody:
      'Read ad accounts, campaigns, ad sets, ads and results, plus your Facebook Pages. Poneva never changes anything in Meta.',
    metaManage: 'Reporting and ad management',
    metaManageTag: 'Read and write',
    metaManageBody:
      'Also create and update campaigns, ads and catalogs with Meta’s own tools. You choose whether Poneva asks before each change.',
    metaOwnApp:
      'Meta asks each business to connect through its own Meta app. The next step walks you through setting it up, once.',
    metaConnectedAlready: 'Connected',
    prepareTitle: 'Prepare your Meta app',
    prepareIntro:
      'Do this once in Meta for Developers. You need admin access to your company’s Meta business.',
    prepareAppTitle: 'Create or open your Meta app',
    prepareAppReporting: 'Turn on the Marketing API and Facebook Login for Business.',
    prepareAppManage: 'Add the “Create and manage ads with the ads MCP” use case.',
    prepareAppOpen: 'Open Meta for Developers',
    prepareRedirectTitle: 'Add Poneva’s sign-in address',
    prepareRedirectBody:
      'In Facebook Login for Business, paste it under Valid OAuth Redirect URIs. Keep the addresses already there, then save.',
    prepareSecretTitle: 'Have your App ID and App Secret ready',
    prepareSecretBody:
      'Both are in your app under App settings → Basic. You’ll paste them into a secure window in the last step, never into Poneva.',
    prepareDone: 'Done',
    copy: 'Copy',
    copied: 'Copied',
    copyAddress: 'Copy the sign-in address',
    copyManual: 'Select the address and copy it with your keyboard.',
    permissionsTitle: 'What Meta will ask you to approve',
    permissionsNote:
      'Meta may ask to verify your business before the app can reach ad accounts beyond your own.',
    scopeAdsRead: 'Read your ads and their results',
    scopeAdsManagement: 'Create and change ads',
    scopeAdsMcp: 'Use Meta’s ads tools',
    scopeBusiness: 'See your business portfolios',
    scopePages: 'List the Pages you manage',
    scopePageEngagement: 'Read Page details and engagement',
    scopeInstagram: 'Read basic Instagram account details',
    scopeCatalog: 'Manage product catalogs',
    guide: 'Step-by-step guide',
    metaAccessTitle: 'Who can use Meta Ads?',
    metaAccessIntro:
      'Ad accounts usually belong to the whole company. You can change this at any time.',
    metaReadOnlyTitle: 'Read only',
    metaReadOnlyBody:
      'Reporting never changes anything in Meta. To let Poneva manage ads, choose ad management in the first step.',
    signInTitle: 'Sign in with Meta',
    signInIntro: 'A secure window opens. Finish there, and this page updates on its own.',
    signInWindow: 'In the secure window',
    signInPaste: 'Paste your App ID and App Secret',
    signInChoose: 'Choose your business and its ad accounts',
    signInApprove: 'Approve access in Meta',
    signInWaiting: 'Waiting for Meta…',
    signInSecret:
      'Your App Secret goes straight to Nango, the sign-in service Poneva uses, and is stored encrypted there. Poneva never sees it.',
    stopWaiting: 'Stop waiting',
    doneTitle: '{name} is connected',
    doneAccountsYou: 'You can now ask Poneva about {count}.',
    doneAccountsCompany: 'Everyone at {company} can now ask Poneva about {count}.',
    doneTools: 'Meta provided {count} for this account.',
    doneTry: 'Try asking',
    doneAsk: 'Ask about my ads',
    doneDone: 'Done',
    doneTryReport: 'How did last week’s campaigns perform?',
    doneTryReturn: 'Which ads brought the best return this month?',
    continue: 'Continue',
    continueToMeta: 'Continue to Meta',
    back: 'Back',
    cancel: 'Cancel',
    askHelp: 'Ask Poneva for help',
    askHelpPrompt: 'Help me set up our own Meta app so I can connect Meta Ads to Poneva.',
    // In a chat
    chatTitle: 'Connect to continue',
    chatIntro: 'This chat stays where it is.',
    chatConnectedYou: 'Connected for you',
    chatConnectedCompany: 'Connected for {company}',
    chatUnavailable: 'Signed in · tools unavailable',
    chatFoot: 'Sign in with the provider. When it shows Connected, ask Poneva to continue.',
    chatPopupBlocked: 'Your browser blocked the sign-in window.',
    chatOpenAuthorization: 'Open sign-in',
    chatFallbackBody: 'Use this service in your work.',
  },
  es: {
    eyebrow: 'Espacio de trabajo de {company}',
    title: 'Conexiones',
    intro:
      'Conecta tus herramientas para encontrar información y sacar el trabajo adelante. Elige quién puede usar cada conexión y si Poneva puede leer o también hacer cambios.',
    activity: 'Actividad',
    backToConnections: 'Volver a Conexiones',
    yoursTitle: 'Tus conexiones',
    filterLabel: 'Mostrar conexiones',
    filterAll: 'Todas',
    filterMine: 'Solo yo',
    filterShared: 'Compartidas',
    filterAttention: 'Requieren atención',
    columnConnection: 'Conexión',
    columnWho: 'Quién puede usarla',
    columnWhat: 'Qué puede hacer Poneva',
    columnStatus: 'Estado',
    yoursLoading: 'Cargando tus conexiones…',
    yoursEmpty: 'Aún no hay nada conectado. Busca abajo para conectar tu primera herramienta.',
    filterEmpty: 'No hay nada aquí ahora mismo.',
    yoursLoadError: 'No se pudieron cargar tus conexiones.',
    serversLoadError: 'No se pudieron cargar tus servidores propios.',
    databasesLoadError: 'No se pudieron cargar tus bases de datos.',
    retry: 'Reintentar',
    stateConnected: 'Conectada',
    stateAttention: 'Requiere inicio de sesión',
    stateReview: 'Revisar acceso',
    stateUnavailable: 'No disponible',
    stateOff: 'Desactivada',
    stateConnecting: 'Conectando…',
    signInAgain: 'Volver a iniciar sesión',
    attentionBody:
      'El inicio de sesión de {name} caducó o se revocó. Vuelve a iniciar sesión para seguir usándola.',
    reviewBody:
      'La cuenta detrás de {name} ha cambiado. Revisa quién puede usarla y qué puede hacer Poneva antes de que se vuelva a usar.',
    unavailableBody:
      'Has iniciado sesión en {name}, pero sus herramientas no están disponibles ahora mismo.',
    offBody:
      '{name} está desactivada. Nadie podrá usarla hasta que se vuelva a activar. No se elimina nada.',
    kindServer: 'Servidor propio',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabla|many:de tablas|other:tablas',
    adAccountsNoun:
      'one:cuenta publicitaria|many:de cuentas publicitarias|other:cuentas publicitarias',
    toolsNoun: 'one:herramienta|many:de herramientas|other:herramientas',
    close: 'Cerrar',
    moreActions: 'Más acciones para {name}',
    tabOverview: 'Resumen',
    tabAccess: 'Acceso',
    tabActivity: 'Actividad',
    connectedOn: 'Desde el {date}',
    lastUsed: 'Último uso {when}',
    whatTitle: 'Qué puede hacer Poneva',
    readsLine: 'Lee cuando se lo pides',
    changesAsk: 'Te pregunta antes de cambiar nada',
    changesAllowed: 'Hace cambios sin preguntar. Cada uno queda registrado en Actividad.',
    changesNever: 'Nunca cambia nada',
    readinessReady: '{count} a tu disposición',
    change: 'Cambiar',
    tryTitle: 'Pruébalo en un chat',
    tryPrompt: '¿En qué puedes ayudarme con {name}?',
    addAccount: 'Añadir otra cuenta',
    disconnect: 'Desconectar',
    disconnectConfirm:
      '¿Desconectar {name}? Dejará de funcionar en chats, apps y agentes hasta que se vuelva a conectar.',
    disconnectKeep: 'Mantener',
    nativeOnlyYou: 'Solo tú puedes usar esta cuenta.',
    nativeEveryone: 'Todos en {company} pueden usar esta cuenta.',
    providerPermissions: 'Los permisos propios de la cuenta en el proveedor se siguen aplicando.',
    manageDatabase: 'Gestionar base de datos',
    activityLoading: 'Cargando actividad…',
    activityLoadError: 'No se pudo cargar la actividad.',
    activityRead: 'Lectura',
    activityChange: 'Cambio',
    activitySetting: 'Ajuste',
    activityDenied: 'Denegado',
    activityFailed: 'Fallido',
    activityUnknown: 'Resultado desconocido',
    activityAll: 'Abrir toda la actividad',
    activityToday: 'Hoy',
    activityYesterday: 'Ayer',
    eventCreated: 'Conexión creada',
    eventShared: 'Uso compartido modificado',
    eventPersonAdded: 'Persona añadida',
    eventPersonRemoved: 'Persona quitada',
    eventAccessChanged: 'Acceso modificado',
    eventRevoked: 'Acceso desactivado',
    eventRemoved: 'Conexión quitada',
    eventSettings: 'Límite de tiempo de consultas modificado',
    addTitle: 'Añadir una conexión',
    integrations: 'one:integración|many:de integraciones|other:integraciones',
    searchLabel: 'Buscar integraciones',
    searchPlaceholder: 'Buscar integraciones',
    clearSearch: 'Borrar búsqueda',
    inYours: 'En tus conexiones',
    addNew: 'Añadir nueva',
    resultsFor: '{count} para «{query}»',
    resultsNoun: 'one:resultado|many:de resultados|other:resultados',
    popularFirst: 'Populares primero',
    searching: 'Buscando…',
    searchUnavailable: 'La búsqueda de conexiones no está disponible temporalmente.',
    noResults:
      'Todavía no hay coincidencias. Prueba con otro nombre o pregunta a Poneva qué herramienta encaja.',
    showMore: 'Mostrar más',
    showMoreRetry: 'Volver a intentar cargar más',
    loadingMore: 'Cargando…',
    moreFailed: 'No se pudieron cargar más integraciones. Tus resultados siguen aquí.',
    manageMembers: 'Elegir qué pueden conectar los miembros',
    manageDone: 'Hecho',
    manageHint:
      'Activa o desactiva apps para los miembros. Las cuentas ya conectadas siguen conectadas.',
    allowFor: 'Permitir nuevas conexiones de {name} para {company}',
    tileConnect: 'Conectar',
    tileConnecting: 'Conectando…',
    tileConnected: 'Conectada',
    tileSignIn: 'Volver a iniciar sesión',
    tileOff: 'Desactivada',
    tileUnavailable: 'No disponible',
    tileNotAllowed: 'No permitida',
    tileNotAllowedNote: 'Tu administrador desactivó las conexiones nuevas',
    tileAccountOnly: 'Solo conexión de cuenta · aún sin herramientas de chat',
    tileApiKey: 'Requiere clave de API',
    tileOwnApp: 'Usa tu propia app de {name}',
    tileAdminSetup: 'Configuración por propietario o administrador',
    otherWays: 'Otras formas de conectar',
    serverTitle: 'Servidor propio',
    serverBody: 'Añade herramientas de un servidor MCP que gestiona tu equipo.',
    serverAction: 'Añadir servidor',
    databaseTitle: 'Una base de datos',
    databaseBody: 'PostgreSQL, limitada a los esquemas o tablas que elijas.',
    databaseAction: 'Conectar',
    assistantsTitle: 'Otros asistentes de IA',
    assistantsBody:
      'Deja que Claude, ChatGPT o un agente de programación lean el conocimiento aprobado de la empresa.',
    assistantsAction: 'Configurar',
    assistantsSheetIntro:
      'Asistentes fuera de Poneva que leen el conocimiento aprobado de la empresa y los agentes de programación que trabajan para ti.',
    footnote:
      'Apruebas los cambios antes de que se ejecuten. Integraciones con tecnología de Pipedream; las herramientas de cada app varían.',
    askBody:
      '¿No sabes cuál necesitas? Poneva puede sugerirte conexiones a partir del trabajo que describas.',
    askAction: 'Preguntar en el chat',
    askPrompt:
      '¿Qué fuentes de la empresa sería más útil conectar para nuestro trabajo, según lo que sabes de nosotros?',
    compactTitle: 'Trae tus herramientas.',
    forYou: 'Para ti',
    browseAll: 'Ver todas las herramientas',
    selectedForFocus: 'Seleccionadas según tu enfoque',
    popupBlocked: 'El navegador bloqueó la ventana de inicio de sesión.',
    popupOpen: 'Abrir la página de inicio de sesión',
    popupKeepChecking: 'Esta página sigue comprobando.',
    noticeAdded: 'Conexión añadida.',
    noticeAddedReturn: 'Conexión añadida. Puedes cerrar esta pestaña y volver a tu trabajo.',
    noticeSignInUnfinished:
      'No se completó el inicio de sesión. Usa Volver a iniciar sesión en el servidor para intentarlo de nuevo.',
    noticeChecked: 'La conexión se comprobó correctamente.',
    noticeUnreachable: 'Todavía no se ha podido acceder al servidor.',
    noticeSignInReady: 'Hay una página de inicio de sesión lista en una pestaña nueva.',
    noticeAuthorized: 'La autorización se ha completado.',
    noticeDatabaseRemoved: 'Se ha desconectado PostgreSQL.',
    noticeStoreConnected: 'Tienda de Shopify conectada.',
    noticeStoreChooseAccess:
      'Tienda de Shopify conectada. Ábrela en Tus conexiones para elegir quién puede usarla.',
    allowFailed: 'No se pudo actualizar {name}. Inténtalo de nuevo.',
    accessUnknown: 'Los detalles del acceso no están disponibles.',
    metaStep: 'Paso {step} de {total}',
    stepsLabel: 'Pasos de configuración',
    stepChoose: 'Elegir',
    stepPrepare: 'Preparar',
    stepAccess: 'Acceso',
    stepSignIn: 'Iniciar sesión',
    metaChooseTitle: 'Conectar Meta Ads',
    metaChooseIntro:
      'Trae tus cuentas publicitarias de Meta a Poneva. Primero, elige qué puede hacer Poneva con ellas.',
    metaReporting: 'Informes',
    metaReportingTag: 'Solo lectura',
    metaReportingBody:
      'Lee cuentas publicitarias, campañas, conjuntos de anuncios, anuncios y resultados, además de tus páginas de Facebook. Poneva nunca cambia nada en Meta.',
    metaManage: 'Informes y gestión de anuncios',
    metaManageTag: 'Lectura y escritura',
    metaManageBody:
      'También crea y actualiza campañas, anuncios y catálogos con las propias herramientas de Meta. Tú eliges si Poneva pregunta antes de cada cambio.',
    metaOwnApp:
      'Meta pide que cada empresa se conecte con su propia app de Meta. El siguiente paso te guía para configurarla, una sola vez.',
    metaConnectedAlready: 'Conectada',
    prepareTitle: 'Prepara tu app de Meta',
    prepareIntro:
      'Hazlo una vez en Meta for Developers. Necesitas acceso de administrador al portfolio comercial de tu empresa en Meta.',
    prepareAppTitle: 'Crea o abre tu app de Meta',
    prepareAppReporting: 'Activa Marketing API y Facebook Login for Business.',
    prepareAppManage: 'Añade el caso de uso «Create and manage ads with the ads MCP».',
    prepareAppOpen: 'Abrir Meta for Developers',
    prepareRedirectTitle: 'Añade la dirección de inicio de sesión de Poneva',
    prepareRedirectBody:
      'En Facebook Login for Business, pégala en Valid OAuth Redirect URIs. Mantén las direcciones que ya hay y guarda.',
    prepareSecretTitle: 'Ten a mano el App ID y el App Secret',
    prepareSecretBody:
      'Los dos están en tu app, en App settings → Basic. Los pegarás en una ventana segura en el último paso, nunca en Poneva.',
    prepareDone: 'Hecho',
    copy: 'Copiar',
    copied: 'Copiado',
    copyAddress: 'Copiar la dirección de inicio de sesión',
    copyManual: 'Selecciona la dirección y cópiala con el teclado.',
    permissionsTitle: 'Qué te pedirá Meta que apruebes',
    permissionsNote:
      'Puede que Meta pida verificar tu negocio antes de que la app pueda acceder a cuentas publicitarias distintas de las tuyas.',
    scopeAdsRead: 'Leer tus anuncios y sus resultados',
    scopeAdsManagement: 'Crear y cambiar anuncios',
    scopeAdsMcp: 'Usar las herramientas de anuncios de Meta',
    scopeBusiness: 'Ver tus portfolios comerciales',
    scopePages: 'Ver la lista de páginas que administras',
    scopePageEngagement: 'Leer los detalles y la interacción de las páginas',
    scopeInstagram: 'Leer los datos básicos de la cuenta de Instagram',
    scopeCatalog: 'Gestionar catálogos de productos',
    guide: 'Guía paso a paso',
    metaAccessTitle: '¿Quién puede usar Meta Ads?',
    metaAccessIntro:
      'Las cuentas publicitarias suelen pertenecer a toda la empresa. Puedes cambiarlo cuando quieras.',
    metaReadOnlyTitle: 'Solo lectura',
    metaReadOnlyBody:
      'Los informes nunca cambian nada en Meta. Para que Poneva gestione anuncios, elige la gestión de anuncios en el primer paso.',
    signInTitle: 'Iniciar sesión con Meta',
    signInIntro: 'Se abre una ventana segura. Termina allí y esta página se actualizará sola.',
    signInWindow: 'En la ventana segura',
    signInPaste: 'Pega el App ID y el App Secret',
    signInChoose: 'Elige tu negocio y sus cuentas publicitarias',
    signInApprove: 'Aprueba el acceso en Meta',
    signInWaiting: 'Esperando a Meta…',
    signInSecret:
      'Tu App Secret va directamente a Nango, el servicio de inicio de sesión que usa Poneva, y se guarda allí cifrado. Poneva nunca lo ve.',
    stopWaiting: 'Dejar de esperar',
    doneTitle: '{name} está conectado',
    doneAccountsYou: 'Ya puedes preguntar a Poneva sobre {count}.',
    doneAccountsCompany: 'Todos en {company} ya pueden preguntar a Poneva sobre {count}.',
    doneTools: 'Meta proporcionó {count} para esta cuenta.',
    doneTry: 'Prueba a preguntar',
    doneAsk: 'Preguntar por mis anuncios',
    doneDone: 'Hecho',
    doneTryReport: '¿Qué tal funcionaron las campañas de la semana pasada?',
    doneTryReturn: '¿Qué anuncios dieron el mejor retorno este mes?',
    continue: 'Continuar',
    continueToMeta: 'Continuar a Meta',
    back: 'Atrás',
    cancel: 'Cancelar',
    askHelp: 'Pedir ayuda a Poneva',
    askHelpPrompt:
      'Ayúdame a configurar nuestra propia app de Meta para poder conectar Meta Ads a Poneva.',
    chatTitle: 'Conecta para continuar',
    chatIntro: 'Este chat se queda donde está.',
    chatConnectedYou: 'Conectada para ti',
    chatConnectedCompany: 'Conectada para {company}',
    chatUnavailable: 'Sesión iniciada · herramientas no disponibles',
    chatFoot:
      'Inicia sesión con el proveedor. Cuando aparezca Conectada, pide a Poneva que continúe.',
    chatPopupBlocked: 'Tu navegador bloqueó la ventana de inicio de sesión.',
    chatOpenAuthorization: 'Abrir inicio de sesión',
    chatFallbackBody: 'Usa este servicio en tu trabajo.',
  },
  et: {
    eyebrow: 'Ettevõtte {company} tööruum',
    title: 'Ühendused',
    intro:
      'Ühenda oma tööriistad, et leida teavet ja asjad tehtud saada. Vali iga ühenduse puhul, kes saavad seda kasutada ja kas Poneva saab lugeda või ka muudatusi teha.',
    activity: 'Tegevus',
    backToConnections: 'Tagasi ühenduste juurde',
    yoursTitle: 'Sinu ühendused',
    filterLabel: 'Näita ühendusi',
    filterAll: 'Kõik',
    filterMine: 'Ainult mina',
    filterShared: 'Jagatud',
    filterAttention: 'Vajavad tähelepanu',
    columnConnection: 'Ühendus',
    columnWho: 'Kes saavad seda kasutada',
    columnWhat: 'Mida Poneva teha saab',
    columnStatus: 'Olek',
    yoursLoading: 'Sinu ühenduste laadimine…',
    yoursEmpty: 'Midagi pole veel ühendatud. Otsi allpool, et ühendada oma esimene tööriist.',
    filterEmpty: 'Siin pole praegu midagi.',
    yoursLoadError: 'Sinu ühendusi ei õnnestunud laadida.',
    serversLoadError: 'Sinu oma servereid ei õnnestunud laadida.',
    databasesLoadError: 'Sinu andmebaase ei õnnestunud laadida.',
    retry: 'Proovi uuesti',
    stateConnected: 'Ühendatud',
    stateAttention: 'Vajab sisselogimist',
    stateReview: 'Vaata juurdepääs üle',
    stateUnavailable: 'Pole saadaval',
    stateOff: 'Välja lülitatud',
    stateConnecting: 'Ühendamine…',
    signInAgain: 'Logi uuesti sisse',
    attentionBody:
      'Teenuse {name} sisselogimine aegus või tühistati. Selle edasiseks kasutamiseks logi uuesti sisse.',
    reviewBody:
      'Teenuse {name} taga olev konto muutus. Enne kui seda uuesti kasutatakse, vaata üle, kes saavad seda kasutada ja mida Poneva teha saab.',
    unavailableBody:
      'Teenusesse {name} on sisse logitud, kuid selle tööriistad pole praegu saadaval.',
    offBody:
      'Teenus {name} on välja lülitatud. Keegi ei saa seda kasutada, kuni see uuesti sisse lülitatakse. Midagi ei kustutata.',
    kindServer: 'Sinu oma server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabel|other:tabelit',
    adAccountsNoun: 'one:reklaamikonto|other:reklaamikontot',
    toolsNoun: 'one:tööriist|other:tööriista',
    close: 'Sulge',
    moreActions: 'Rohkem toiminguid: {name}',
    tabOverview: 'Ülevaade',
    tabAccess: 'Juurdepääs',
    tabActivity: 'Tegevus',
    connectedOn: 'Alates {date}',
    lastUsed: 'Viimati kasutatud {when}',
    whatTitle: 'Mida Poneva teha saab',
    readsLine: 'Loeb, kui sa palud',
    changesAsk: 'Küsib sinult enne, kui midagi muudab',
    changesAllowed: 'Teeb muudatusi küsimata. Iga muudatus salvestatakse jaotisesse „Tegevus”.',
    changesNever: 'Ei muuda kunagi midagi',
    readinessReady: '{count} valmis',
    change: 'Muuda',
    tryTitle: 'Proovi vestluses',
    tryPrompt: 'Millega saad mind teenuse {name} abil aidata?',
    addAccount: 'Lisa veel üks konto',
    disconnect: 'Katkesta ühendus',
    disconnectConfirm:
      'Kas katkestada ühendus teenusega {name}? See lakkab töötamast vestlustes, rakendustes ja agentides, kuni see uuesti ühendatakse.',
    disconnectKeep: 'Jäta alles',
    nativeOnlyYou: 'Seda kontot saad kasutada ainult sina.',
    nativeEveryone: 'Seda kontot saavad kasutada kõik ettevõttes {company}.',
    providerPermissions: 'Teenusepakkuja enda kontoõigused kehtivad endiselt.',
    manageDatabase: 'Halda andmebaasi',
    activityLoading: 'Tegevuse laadimine…',
    activityLoadError: 'Tegevust ei õnnestunud laadida.',
    activityRead: 'Lugemine',
    activityChange: 'Muudatus',
    activitySetting: 'Seade',
    activityDenied: 'Keelatud',
    activityFailed: 'Ebaõnnestus',
    activityUnknown: 'Teadmata tulemus',
    activityAll: 'Ava kogu tegevus',
    activityToday: 'Täna',
    activityYesterday: 'Eile',
    eventCreated: 'Ühendus loodud',
    eventShared: 'Jagamist muudeti',
    eventPersonAdded: 'Inimene lisatud',
    eventPersonRemoved: 'Inimene eemaldatud',
    eventAccessChanged: 'Juurdepääsu muudeti',
    eventRevoked: 'Juurdepääs välja lülitatud',
    eventRemoved: 'Ühendus eemaldatud',
    eventSettings: 'Päringu ajapiirangut muudeti',
    addTitle: 'Lisa ühendus',
    integrations: 'one:integratsioon|other:integratsiooni',
    searchLabel: 'Otsi integratsioone',
    searchPlaceholder: 'Otsi integratsioone',
    clearSearch: 'Tühjenda otsing',
    inYours: 'Sinu ühendustes',
    addNew: 'Lisa uus',
    resultsFor: '{count} päringule „{query}”',
    resultsNoun: 'one:tulemus|other:tulemust',
    popularFirst: 'Populaarsemad eespool',
    searching: 'Otsimine…',
    searchUnavailable: 'Ühenduste otsing pole ajutiselt saadaval.',
    noResults:
      'Midagi ei vasta veel. Proovi mõnda teist nime või küsi Ponevalt, milline tööriist sobib.',
    showMore: 'Näita rohkem',
    showMoreRetry: 'Proovi uuesti rohkem laadida',
    loadingMore: 'Laadimine…',
    moreFailed: 'Rohkem integratsioone ei õnnestunud laadida. Sinu tulemused on endiselt siin.',
    manageMembers: 'Vali, mida liikmed saavad ühendada',
    manageDone: 'Valmis',
    manageHint:
      'Lülita rakendusi liikmete jaoks sisse või välja. Juba ühendatud kontod jäävad ühendatuks.',
    allowFor: 'Luba ettevõttes {company} uued ühendused teenusega {name}',
    tileConnect: 'Ühenda',
    tileConnecting: 'Ühendamine…',
    tileConnected: 'Ühendatud',
    tileSignIn: 'Logi uuesti sisse',
    tileOff: 'Välja lülitatud',
    tileUnavailable: 'Pole saadaval',
    tileNotAllowed: 'Pole lubatud',
    tileNotAllowedNote: 'Sinu administraator lülitas uued ühendused välja',
    tileAccountOnly: 'Ainult kontoühendus · vestlustööriistu veel pole',
    tileApiKey: 'Vaja on API-võtit',
    tileOwnApp: 'Kasutab sinu enda rakendust {name}',
    tileAdminSetup: 'Seadistab omanik või administraator',
    otherWays: 'Muud ühendamise viisid',
    serverTitle: 'Sinu oma server',
    serverBody: 'Lisa tööriistu MCP-serverist, mida sinu meeskond haldab.',
    serverAction: 'Lisa server',
    databaseTitle: 'Andmebaas',
    databaseBody: 'PostgreSQL, piiratud sinu valitud skeemide või tabelitega.',
    databaseAction: 'Ühenda',
    assistantsTitle: 'Teised AI-assistendid',
    assistantsBody:
      'Lase Claude’il, ChatGPT-l või programmeerimisagendil lugeda heakskiidetud ettevõtte teadmisi.',
    assistantsAction: 'Seadista',
    assistantsSheetIntro:
      'Väljaspool Ponevat olevad assistendid, mis loevad heakskiidetud ettevõtte teadmisi, ja programmeerimisagendid, mis töötavad sinu heaks.',
    footnote:
      'Kinnitad muudatused enne, kui need tehakse. Integratsioone pakub Pipedream; iga rakenduse tööriistad on erinevad.',
    askBody:
      'Pole kindel, millist vajad? Poneva oskab soovitada ühendusi sinu kirjeldatud töö põhjal.',
    askAction: 'Küsi vestluses',
    askPrompt:
      'Milliseid ettevõtte allikaid oleks meie töö jaoks kõige kasulikum ühendada, arvestades seda, mida sa meist tead?',
    compactTitle: 'Võta oma tööriistad kaasa.',
    forYou: 'Sulle',
    browseAll: 'Sirvi kõiki tööriistu',
    selectedForFocus: 'Valitud sinu fookuse järgi',
    popupBlocked: 'Brauser blokeeris sisselogimisakna.',
    popupOpen: 'Ava sisselogimisleht',
    popupKeepChecking: 'See leht kontrollib edasi.',
    noticeAdded: 'Ühendus on lisatud.',
    noticeAddedReturn: 'Ühendus on lisatud. Võid selle vahelehe sulgeda ja oma töö juurde naasta.',
    noticeSignInUnfinished:
      'Sisselogimine jäi pooleli. Uuesti proovimiseks vali serveri juures „Logi uuesti sisse”.',
    noticeChecked: 'Ühenduse kontroll õnnestus.',
    noticeUnreachable: 'Serveriga ei õnnestunud veel ühendust saada.',
    noticeSignInReady: 'Sisselogimisleht on uuel vahelehel valmis.',
    noticeAuthorized: 'Autoriseerimine on lõpetatud.',
    noticeDatabaseRemoved: 'PostgreSQL on lahti ühendatud.',
    noticeStoreConnected: 'Shopify pood on ühendatud.',
    noticeStoreChooseAccess:
      'Shopify pood on ühendatud. Ava see jaotises „Sinu ühendused” ja vali, kes saavad seda kasutada.',
    allowFailed: 'Rakendust {name} ei õnnestunud uuendada. Proovi uuesti.',
    accessUnknown: 'Juurdepääsu üksikasjad pole saadaval.',
    metaStep: 'Samm {step}/{total}',
    stepsLabel: 'Seadistamise sammud',
    stepChoose: 'Vali',
    stepPrepare: 'Valmista ette',
    stepAccess: 'Juurdepääs',
    stepSignIn: 'Logi sisse',
    metaChooseTitle: 'Ühenda Meta Ads',
    metaChooseIntro:
      'Too oma Meta reklaamikontod Ponevasse. Esmalt vali, mida Poneva nendega teha tohib.',
    metaReporting: 'Aruandlus',
    metaReportingTag: 'Ainult lugemine',
    metaReportingBody:
      'Loeb reklaamikontosid, kampaaniaid, reklaamikomplekte, reklaame ja tulemusi ning sinu Facebooki lehti. Poneva ei muuda Metas kunagi midagi.',
    metaManage: 'Aruandlus ja reklaamide haldus',
    metaManageTag: 'Lugemine ja kirjutamine',
    metaManageBody:
      'Lisaks loob ja uuendab kampaaniaid, reklaame ja katalooge Meta enda tööriistadega. Sina valid, kas Poneva küsib enne iga muudatust.',
    metaOwnApp:
      'Meta nõuab, et iga ettevõte ühenduks oma Meta rakenduse kaudu. Järgmine samm juhendab sind selle seadistamisel, ühe korra.',
    metaConnectedAlready: 'Ühendatud',
    prepareTitle: 'Valmista ette oma Meta rakendus',
    prepareIntro:
      'Tee seda üks kord keskkonnas Meta for Developers. Sul on vaja administraatori juurdepääsu oma ettevõtte Meta ärikontole.',
    prepareAppTitle: 'Loo või ava oma Meta rakendus',
    prepareAppReporting: 'Lülita sisse Marketing API ja Facebook Login for Business.',
    prepareAppManage: 'Lisa kasutusjuht „Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Ava Meta for Developers',
    prepareRedirectTitle: 'Lisa Poneva sisselogimisaadress',
    prepareRedirectBody:
      'Kleebi see jaotises Facebook Login for Business välja Valid OAuth Redirect URIs alla. Jäta seal juba olevad aadressid alles ja salvesta.',
    prepareSecretTitle: 'Hoia App ID ja App Secret käepärast',
    prepareSecretBody:
      'Mõlemad leiad oma rakenduses jaotisest App settings → Basic. Viimases sammus kleebid need turvalisse aknasse, mitte kunagi Ponevasse.',
    prepareDone: 'Tehtud',
    copy: 'Kopeeri',
    copied: 'Kopeeritud',
    copyAddress: 'Kopeeri sisselogimisaadress',
    copyManual: 'Vali aadress ja kopeeri see klaviatuuriga.',
    permissionsTitle: 'Mida Meta palub sul kinnitada',
    permissionsNote:
      'Meta võib paluda sinu ettevõtte kinnitamist, enne kui rakendus pääseb ligi teistele reklaamikontodele peale sinu omade.',
    scopeAdsRead: 'Lugeda sinu reklaame ja nende tulemusi',
    scopeAdsManagement: 'Luua ja muuta reklaame',
    scopeAdsMcp: 'Kasutada Meta reklaamitööriistu',
    scopeBusiness: 'Näha sinu äriportfelle',
    scopePages: 'Loetleda lehti, mida sa haldad',
    scopePageEngagement: 'Lugeda lehtede andmeid ja kaasatust',
    scopeInstagram: 'Lugeda Instagrami konto põhiandmeid',
    scopeCatalog: 'Hallata tootekatalooge',
    guide: 'Samm-sammuline juhend',
    metaAccessTitle: 'Kes saavad kasutada teenust Meta Ads?',
    metaAccessIntro:
      'Reklaamikontod kuuluvad tavaliselt kogu ettevõttele. Saad seda igal ajal muuta.',
    metaReadOnlyTitle: 'Ainult lugemine',
    metaReadOnlyBody:
      'Aruandlus ei muuda Metas kunagi midagi. Kui soovid, et Poneva haldaks reklaame, vali esimeses sammus reklaamide haldus.',
    signInTitle: 'Logi sisse Metaga',
    signInIntro: 'Avaneb turvaline aken. Lõpeta seal ja see leht uueneb ise.',
    signInWindow: 'Turvalises aknas',
    signInPaste: 'Kleebi App ID ja App Secret',
    signInChoose: 'Vali oma ettevõte ja selle reklaamikontod',
    signInApprove: 'Kinnita juurdepääs Metas',
    signInWaiting: 'Meta ootamine…',
    signInSecret:
      'Sinu App Secret läheb otse Nangosse, sisselogimisteenusesse, mida Poneva kasutab, ja salvestatakse seal krüpteeritult. Poneva ei näe seda kunagi.',
    stopWaiting: 'Lõpeta ootamine',
    doneTitle: 'Teenus {name} on ühendatud',
    doneAccountsYou: 'Nüüd saad Ponevalt küsida järgmise kohta: {count}.',
    doneAccountsCompany:
      'Kõik ettevõttes {company} saavad nüüd Ponevalt küsida järgmise kohta: {count}.',
    doneTools: 'Meta kaudu on selle konto jaoks saadaval {count}.',
    doneTry: 'Proovi küsida',
    doneAsk: 'Küsi minu reklaamide kohta',
    doneDone: 'Valmis',
    doneTryReport: 'Kuidas eelmise nädala kampaaniad toimisid?',
    doneTryReturn: 'Millised reklaamid tõid sel kuul parima tootluse?',
    continue: 'Jätka',
    continueToMeta: 'Jätka Metasse',
    back: 'Tagasi',
    cancel: 'Loobu',
    askHelp: 'Küsi Ponevalt abi',
    askHelpPrompt:
      'Aita mul seadistada meie oma Meta rakendus, et saaksin Meta Adsi Ponevaga ühendada.',
    chatTitle: 'Ühenda, et jätkata',
    chatIntro: 'See vestlus jääb sinna, kus see on.',
    chatConnectedYou: 'Ühendatud sinu jaoks',
    chatConnectedCompany: 'Ühendatud ettevõtte {company} jaoks',
    chatUnavailable: 'Sisse logitud · tööriistad pole saadaval',
    chatFoot: 'Logi teenusepakkuja juures sisse. Kui kuvatakse „Ühendatud”, palu Poneval jätkata.',
    chatPopupBlocked: 'Sinu brauser blokeeris sisselogimisakna.',
    chatOpenAuthorization: 'Ava sisselogimine',
    chatFallbackBody: 'Kasuta seda teenust oma töös.',
  },
  fi: {
    eyebrow: 'Työtila: {company}',
    title: 'Yhteydet',
    intro:
      'Yhdistä työkalusi, niin löydät tietoa ja saat asiat hoidettua. Valitse kullekin yhteydelle, kuka voi käyttää sitä ja voiko Poneva lukea vai myös tehdä muutoksia.',
    activity: 'Toiminta',
    backToConnections: 'Takaisin yhteyksiin',
    yoursTitle: 'Yhteytesi',
    filterLabel: 'Näytä yhteydet',
    filterAll: 'Kaikki',
    filterMine: 'Vain minä',
    filterShared: 'Jaetut',
    filterAttention: 'Vaativat huomiota',
    columnConnection: 'Yhteys',
    columnWho: 'Kuka voi käyttää sitä',
    columnWhat: 'Mitä Poneva voi tehdä',
    columnStatus: 'Tila',
    yoursLoading: 'Ladataan yhteyksiäsi…',
    yoursEmpty: 'Mitään ei ole vielä yhdistetty. Hae alta ja yhdistä ensimmäinen työkalusi.',
    filterEmpty: 'Täällä ei ole juuri nyt mitään.',
    yoursLoadError: 'Yhteyksiäsi ei voitu ladata.',
    serversLoadError: 'Omia palvelimiasi ei voitu ladata.',
    databasesLoadError: 'Tietokantojasi ei voitu ladata.',
    retry: 'Yritä uudelleen',
    stateConnected: 'Yhdistetty',
    stateAttention: 'Vaatii kirjautumisen',
    stateReview: 'Tarkista käyttöoikeus',
    stateUnavailable: 'Ei käytettävissä',
    stateOff: 'Poistettu käytöstä',
    stateConnecting: 'Yhdistetään…',
    signInAgain: 'Kirjaudu uudelleen',
    attentionBody:
      'Kirjautuminen palveluun {name} on vanhentunut tai peruttu. Kirjaudu uudelleen, jotta voit jatkaa sen käyttöä.',
    reviewBody:
      'Palvelun {name} taustalla oleva tili on muuttunut. Tarkista, kuka voi käyttää sitä ja mitä Poneva voi tehdä, ennen kuin sitä käytetään uudelleen.',
    unavailableBody:
      'Palveluun {name} on kirjauduttu, mutta sen työkalut eivät ole juuri nyt käytettävissä.',
    offBody:
      'Palvelu {name} on poistettu käytöstä. Kukaan ei voi käyttää sitä, ennen kuin se otetaan uudelleen käyttöön. Mitään ei poisteta.',
    kindServer: 'Oma palvelin',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:taulukko|other:taulukkoa',
    adAccountsNoun: 'one:mainostili|other:mainostiliä',
    toolsNoun: 'one:työkalu|other:työkalua',
    close: 'Sulje',
    moreActions: 'Lisää toimintoja: {name}',
    tabOverview: 'Yleiskatsaus',
    tabAccess: 'Käyttöoikeus',
    tabActivity: 'Toiminta',
    connectedOn: '{date} alkaen',
    lastUsed: 'Viimeksi käytetty {when}',
    whatTitle: 'Mitä Poneva voi tehdä',
    readsLine: 'Lukee, kun pyydät',
    changesAsk: 'Kysyy sinulta ennen kuin muuttaa mitään',
    changesAllowed: 'Tekee muutoksia kysymättä. Jokainen muutos kirjataan Toimintaan.',
    changesNever: 'Ei koskaan muuta mitään',
    readinessReady: '{count} valmiina',
    change: 'Muuta',
    tryTitle: 'Kokeile keskustelussa',
    tryPrompt: 'Missä voit auttaa minua palvelun {name} avulla?',
    addAccount: 'Lisää toinen tili',
    disconnect: 'Katkaise yhteys',
    disconnectConfirm:
      'Katkaistaanko yhteys palveluun {name}? Se lakkaa toimimasta keskusteluissa, sovelluksissa ja agenteissa, kunnes se yhdistetään uudelleen.',
    disconnectKeep: 'Pidä',
    nativeOnlyYou: 'Vain sinä voit käyttää tätä tiliä.',
    nativeEveryone: 'Kaikki organisaatiossa {company} voivat käyttää tätä tiliä.',
    providerPermissions: 'Palveluntarjoajan omat tilin käyttöoikeudet ovat edelleen voimassa.',
    manageDatabase: 'Hallinnoi tietokantaa',
    activityLoading: 'Ladataan toimintaa…',
    activityLoadError: 'Toimintaa ei voitu ladata.',
    activityRead: 'Lukeminen',
    activityChange: 'Muutos',
    activitySetting: 'Asetus',
    activityDenied: 'Estetty',
    activityFailed: 'Epäonnistui',
    activityUnknown: 'Tulos ei tiedossa',
    activityAll: 'Avaa kaikki toiminta',
    activityToday: 'Tänään',
    activityYesterday: 'Eilen',
    eventCreated: 'Yhteys luotu',
    eventShared: 'Jakamista muutettu',
    eventPersonAdded: 'Henkilö lisätty',
    eventPersonRemoved: 'Henkilö poistettu',
    eventAccessChanged: 'Käyttöoikeutta muutettu',
    eventRevoked: 'Käyttöoikeus poistettu käytöstä',
    eventRemoved: 'Yhteys poistettu',
    eventSettings: 'Kyselyn aikarajaa muutettu',
    addTitle: 'Lisää yhteys',
    integrations: 'one:integraatio|other:integraatiota',
    searchLabel: 'Hae integraatioita',
    searchPlaceholder: 'Hae integraatioita',
    clearSearch: 'Tyhjennä haku',
    inYours: 'Yhteyksissäsi',
    addNew: 'Lisää uusi',
    resultsFor: '{count} haulle ”{query}”',
    resultsNoun: 'one:tulos|other:tulosta',
    popularFirst: 'Suosituimmat ensin',
    searching: 'Haetaan…',
    searchUnavailable: 'Yhteyksien haku ei ole tilapäisesti käytettävissä.',
    noResults:
      'Mikään ei vielä vastaa hakua. Kokeile toista nimeä tai kysy Ponevalta, mikä työkalu sopii.',
    showMore: 'Näytä lisää',
    showMoreRetry: 'Yritä ladata lisää uudelleen',
    loadingMore: 'Ladataan…',
    moreFailed: 'Lisää integraatioita ei voitu ladata. Tuloksesi ovat yhä tässä.',
    manageMembers: 'Valitse, mitä jäsenet voivat yhdistää',
    manageDone: 'Valmis',
    manageHint:
      'Ota sovelluksia käyttöön tai poista niitä käytöstä jäseniltä. Jo yhdistetyt tilit pysyvät yhdistettyinä.',
    allowFor: 'Salli uudet yhteydet palveluun {name} organisaatiossa {company}',
    tileConnect: 'Yhdistä',
    tileConnecting: 'Yhdistetään…',
    tileConnected: 'Yhdistetty',
    tileSignIn: 'Kirjaudu uudelleen',
    tileOff: 'Poistettu käytöstä',
    tileUnavailable: 'Ei käytettävissä',
    tileNotAllowed: 'Ei sallittu',
    tileNotAllowedNote: 'Ylläpitäjäsi on poistanut uudet yhteydet käytöstä',
    tileAccountOnly: 'Vain tiliyhteys · ei vielä keskustelutyökaluja',
    tileApiKey: 'API-avain vaaditaan',
    tileOwnApp: 'Käyttää omaa {name}-sovellustasi',
    tileAdminSetup: 'Omistaja tai ylläpitäjä määrittää',
    otherWays: 'Muita tapoja yhdistää',
    serverTitle: 'Oma palvelin',
    serverBody: 'Lisää työkaluja MCP-palvelimelta, jota tiimisi ylläpitää.',
    serverAction: 'Lisää palvelin',
    databaseTitle: 'Tietokanta',
    databaseBody: 'PostgreSQL, rajattuna valitsemiisi skeemoihin tai taulukoihin.',
    databaseAction: 'Yhdistä',
    assistantsTitle: 'Muut tekoälyavustajat',
    assistantsBody: 'Anna Clauden, ChatGPT:n tai koodausagentin lukea hyväksyttyä yritystietoa.',
    assistantsAction: 'Määritä',
    assistantsSheetIntro:
      'Ponevan ulkopuoliset avustajat, jotka lukevat hyväksyttyä yritystietoa, ja koodausagentit, jotka työskentelevät puolestasi.',
    footnote:
      'Hyväksyt muutokset ennen kuin ne tehdään. Integraatiot tarjoaa Pipedream; kunkin sovelluksen työkalut vaihtelevat.',
    askBody:
      'Etkö ole varma, minkä tarvitset? Poneva voi ehdottaa yhteyksiä kuvailemasi työn perusteella.',
    askAction: 'Kysy keskustelussa',
    askPrompt:
      'Mitkä yrityksen lähteet olisi hyödyllisintä yhdistää työtämme varten sen perusteella, mitä tiedät meistä?',
    compactTitle: 'Ota työkalusi mukaan.',
    forYou: 'Sinulle',
    browseAll: 'Selaa kaikkia työkaluja',
    selectedForFocus: 'Valittu painopisteesi mukaan',
    popupBlocked: 'Selain esti kirjautumisikkunan.',
    popupOpen: 'Avaa kirjautumissivu',
    popupKeepChecking: 'Tämä sivu jatkaa tarkistamista.',
    noticeAdded: 'Yhteys lisätty.',
    noticeAddedReturn: 'Yhteys lisätty. Voit sulkea tämän välilehden ja palata työhösi.',
    noticeSignInUnfinished:
      'Kirjautuminen ei valmistunut. Yritä uudelleen valitsemalla palvelimen kohdalla Kirjaudu uudelleen.',
    noticeChecked: 'Yhteys tarkistettiin onnistuneesti.',
    noticeUnreachable: 'Palvelimeen ei vielä saatu yhteyttä.',
    noticeSignInReady: 'Kirjautumissivu odottaa uudessa välilehdessä.',
    noticeAuthorized: 'Valtuutus on valmis.',
    noticeDatabaseRemoved: 'PostgreSQL-yhteys katkaistiin.',
    noticeStoreConnected: 'Shopify-kauppa yhdistetty.',
    noticeStoreChooseAccess:
      'Shopify-kauppa yhdistetty. Avaa se kohdassa Yhteytesi ja valitse, kuka voi käyttää sitä.',
    allowFailed: 'Sovellusta {name} ei voitu päivittää. Yritä uudelleen.',
    accessUnknown: 'Käyttöoikeuden tiedot eivät ole saatavilla.',
    metaStep: 'Vaihe {step}/{total}',
    stepsLabel: 'Määritysvaiheet',
    stepChoose: 'Valitse',
    stepPrepare: 'Valmistele',
    stepAccess: 'Käyttöoikeus',
    stepSignIn: 'Kirjaudu',
    metaChooseTitle: 'Yhdistä Meta Ads',
    metaChooseIntro:
      'Tuo Metan mainostilisi Ponevaan. Valitse ensin, mitä Poneva saa tehdä niillä.',
    metaReporting: 'Raportointi',
    metaReportingTag: 'Vain lukeminen',
    metaReportingBody:
      'Lukee mainostilejä, kampanjoita, mainossarjoja, mainoksia ja tuloksia sekä Facebook-sivujasi. Poneva ei koskaan muuta mitään Metassa.',
    metaManage: 'Raportointi ja mainosten hallinta',
    metaManageTag: 'Lukeminen ja kirjoittaminen',
    metaManageBody:
      'Lisäksi luo ja päivittää kampanjoita, mainoksia ja luetteloita Metan omilla työkaluilla. Sinä valitset, kysyykö Poneva ennen jokaista muutosta.',
    metaOwnApp:
      'Meta edellyttää, että jokainen yritys yhdistää oman Meta-sovelluksensa kautta. Seuraava vaihe opastaa sinut sen määrittämisessä, kerran.',
    metaConnectedAlready: 'Yhdistetty',
    prepareTitle: 'Valmistele Meta-sovelluksesi',
    prepareIntro:
      'Tee tämä kerran Meta for Developers -palvelussa. Tarvitset ylläpitäjän oikeudet yrityksesi Meta-liiketoimintatiliin.',
    prepareAppTitle: 'Luo tai avaa Meta-sovelluksesi',
    prepareAppReporting: 'Ota käyttöön Marketing API ja Facebook Login for Business.',
    prepareAppManage: 'Lisää käyttötapaus ”Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Avaa Meta for Developers',
    prepareRedirectTitle: 'Lisää Ponevan kirjautumisosoite',
    prepareRedirectBody:
      'Liitä se Facebook Login for Business -kohdassa kenttään Valid OAuth Redirect URIs. Säilytä siellä jo olevat osoitteet ja tallenna sitten.',
    prepareSecretTitle: 'Pidä App ID ja App Secret valmiina',
    prepareSecretBody:
      'Molemmat löytyvät sovelluksestasi kohdasta App settings → Basic. Liität ne viimeisessä vaiheessa suojattuun ikkunaan, et koskaan Ponevaan.',
    prepareDone: 'Tehty',
    copy: 'Kopioi',
    copied: 'Kopioitu',
    copyAddress: 'Kopioi kirjautumisosoite',
    copyManual: 'Valitse osoite ja kopioi se näppäimistöllä.',
    permissionsTitle: 'Mitä Meta pyytää sinua hyväksymään',
    permissionsNote:
      'Meta voi pyytää vahvistamaan yrityksesi, ennen kuin sovellus pääsee muihin kuin omiin mainostileihisi.',
    scopeAdsRead: 'Lukea mainoksiasi ja niiden tuloksia',
    scopeAdsManagement: 'Luoda ja muuttaa mainoksia',
    scopeAdsMcp: 'Käyttää Metan mainostyökaluja',
    scopeBusiness: 'Nähdä yritysportfoliosi',
    scopePages: 'Luetella hallinnoimasi sivut',
    scopePageEngagement: 'Lukea sivujen tietoja ja sitoutumista',
    scopeInstagram: 'Lukea Instagram-tilin perustiedot',
    scopeCatalog: 'Hallinnoida tuoteluetteloita',
    guide: 'Vaiheittainen opas',
    metaAccessTitle: 'Kuka voi käyttää palvelua Meta Ads?',
    metaAccessIntro:
      'Mainostilit kuuluvat yleensä koko yritykselle. Voit muuttaa tätä milloin tahansa.',
    metaReadOnlyTitle: 'Vain lukeminen',
    metaReadOnlyBody:
      'Raportointi ei koskaan muuta mitään Metassa. Jos haluat Ponevan hallinnoivan mainoksia, valitse ensimmäisessä vaiheessa mainosten hallinta.',
    signInTitle: 'Kirjaudu Metalla',
    signInIntro: 'Suojattu ikkuna avautuu. Viimeistele siellä, niin tämä sivu päivittyy itsestään.',
    signInWindow: 'Suojatussa ikkunassa',
    signInPaste: 'Liitä App ID ja App Secret',
    signInChoose: 'Valitse yrityksesi ja sen mainostilit',
    signInApprove: 'Hyväksy käyttöoikeus Metassa',
    signInWaiting: 'Odotetaan Metaa…',
    signInSecret:
      'App Secret menee suoraan Nangolle, Ponevan käyttämälle kirjautumispalvelulle, ja se tallennetaan sinne salattuna. Poneva ei koskaan näe sitä.',
    stopWaiting: 'Lopeta odottaminen',
    doneTitle: '{name} on yhdistetty',
    doneAccountsYou: '{count} on nyt käytettävissä Ponevassa kysymyksiäsi varten.',
    doneAccountsCompany:
      '{count} on nyt Ponevassa kaikkien käytettävissä organisaatiossa {company}.',
    doneTools: 'Tälle tilille on Metan kautta käytettävissä {count}.',
    doneTry: 'Kokeile kysyä',
    doneAsk: 'Kysy mainoksistani',
    doneDone: 'Valmis',
    doneTryReport: 'Miten viime viikon kampanjat menestyivät?',
    doneTryReturn: 'Mitkä mainokset toivat parhaan tuoton tässä kuussa?',
    continue: 'Jatka',
    continueToMeta: 'Jatka Metaan',
    back: 'Takaisin',
    cancel: 'Peruuta',
    askHelp: 'Pyydä apua Ponevalta',
    askHelpPrompt:
      'Auta minua määrittämään oma Meta-sovelluksemme, jotta voin yhdistää Meta Adsin Ponevaan.',
    chatTitle: 'Yhdistä jatkaaksesi',
    chatIntro: 'Tämä keskustelu pysyy paikallaan.',
    chatConnectedYou: 'Yhdistetty sinulle',
    chatConnectedCompany: 'Yhdistetty organisaatiolle {company}',
    chatUnavailable: 'Kirjautunut · työkalut eivät ole käytettävissä',
    chatFoot: 'Kirjaudu palveluntarjoajalle. Kun näkyvissä on Yhdistetty, pyydä Ponevaa jatkamaan.',
    chatPopupBlocked: 'Selaimesi esti kirjautumisikkunan.',
    chatOpenAuthorization: 'Avaa kirjautuminen',
    chatFallbackBody: 'Käytä tätä palvelua työssäsi.',
  },
  fr: {
    eyebrow: 'Espace de travail {company}',
    title: 'Connexions',
    intro:
      'Connectez vos outils pour trouver des informations et faire avancer le travail. Choisissez qui peut utiliser chaque connexion et si Poneva peut lire ou aussi apporter des modifications.',
    activity: 'Activité',
    backToConnections: 'Retour aux connexions',
    yoursTitle: 'Vos connexions',
    filterLabel: 'Afficher les connexions',
    filterAll: 'Toutes',
    filterMine: 'Moi uniquement',
    filterShared: 'Partagées',
    filterAttention: 'Action requise',
    columnConnection: 'Connexion',
    columnWho: 'Qui peut l’utiliser',
    columnWhat: 'Ce que Poneva peut faire',
    columnStatus: 'État',
    yoursLoading: 'Chargement de vos connexions…',
    yoursEmpty:
      'Rien n’est encore connecté. Faites une recherche ci-dessous pour connecter votre premier outil.',
    filterEmpty: 'Rien ici pour le moment.',
    yoursLoadError: 'Impossible de charger vos connexions.',
    serversLoadError: 'Impossible de charger vos propres serveurs.',
    databasesLoadError: 'Impossible de charger vos bases de données.',
    retry: 'Réessayer',
    stateConnected: 'Connectée',
    stateAttention: 'Authentification requise',
    stateReview: 'Vérifier l’accès',
    stateUnavailable: 'Indisponible',
    stateOff: 'Désactivée',
    stateConnecting: 'Connexion…',
    signInAgain: 'Se reconnecter',
    attentionBody:
      'L’authentification pour {name} a expiré ou a été révoquée. Reconnectez-vous pour continuer à l’utiliser.',
    reviewBody:
      'Le compte associé à {name} a changé. Vérifiez qui peut l’utiliser et ce que Poneva peut faire avant toute nouvelle utilisation.',
    unavailableBody:
      'La session {name} est ouverte, mais ses outils ne sont pas disponibles pour le moment.',
    offBody:
      'La connexion {name} est désactivée. Personne ne pourra l’utiliser tant qu’elle n’est pas réactivée. Rien n’est supprimé.',
    kindServer: 'Votre propre serveur',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:table|many:de tables|other:tables',
    adAccountsNoun:
      'one:compte publicitaire|many:de comptes publicitaires|other:comptes publicitaires',
    toolsNoun: 'one:outil|many:d’outils|other:outils',
    close: 'Fermer',
    moreActions: 'Plus d’actions pour {name}',
    tabOverview: 'Aperçu',
    tabAccess: 'Accès',
    tabActivity: 'Activité',
    connectedOn: 'Depuis le {date}',
    lastUsed: 'Dernière utilisation {when}',
    whatTitle: 'Ce que Poneva peut faire',
    readsLine: 'Lit quand vous le demandez',
    changesAsk: 'Vous demande avant de modifier quoi que ce soit',
    changesAllowed:
      'Apporte des modifications sans demander. Chacune est enregistrée dans Activité.',
    changesNever: 'Ne modifie jamais rien',
    readinessReady: '{count} à disposition',
    change: 'Modifier',
    tryTitle: 'Essayer dans un chat',
    tryPrompt: 'En quoi peux-tu m’aider avec {name} ?',
    addAccount: 'Ajouter un autre compte',
    disconnect: 'Déconnecter',
    disconnectConfirm:
      'Déconnecter {name} ? La connexion cessera de fonctionner dans les chats, les apps et les agents jusqu’à sa reconnexion.',
    disconnectKeep: 'Conserver',
    nativeOnlyYou: 'Vous seul pouvez utiliser ce compte.',
    nativeEveryone: 'Tout le monde chez {company} peut utiliser ce compte.',
    providerPermissions:
      'Les autorisations du compte chez le fournisseur continuent de s’appliquer.',
    manageDatabase: 'Gérer la base de données',
    activityLoading: 'Chargement de l’activité…',
    activityLoadError: 'Impossible de charger l’activité.',
    activityRead: 'Lecture',
    activityChange: 'Modification',
    activitySetting: 'Réglage',
    activityDenied: 'Refusé',
    activityFailed: 'Échec',
    activityUnknown: 'Résultat inconnu',
    activityAll: 'Ouvrir toute l’activité',
    activityToday: 'Aujourd’hui',
    activityYesterday: 'Hier',
    eventCreated: 'Connexion créée',
    eventShared: 'Partage modifié',
    eventPersonAdded: 'Personne ajoutée',
    eventPersonRemoved: 'Personne retirée',
    eventAccessChanged: 'Accès modifié',
    eventRevoked: 'Accès désactivé',
    eventRemoved: 'Connexion supprimée',
    eventSettings: 'Délai maximal des requêtes modifié',
    addTitle: 'Ajouter une connexion',
    integrations: 'one:intégration|many:d’intégrations|other:intégrations',
    searchLabel: 'Rechercher des intégrations',
    searchPlaceholder: 'Rechercher des intégrations',
    clearSearch: 'Effacer la recherche',
    inYours: 'Dans vos connexions',
    addNew: 'À ajouter',
    resultsFor: '{count} pour « {query} »',
    resultsNoun: 'one:résultat|many:de résultats|other:résultats',
    popularFirst: 'Les plus populaires d’abord',
    searching: 'Recherche…',
    searchUnavailable: 'La recherche de connexions est temporairement indisponible.',
    noResults:
      'Aucun résultat pour l’instant. Essayez un autre nom ou demandez à Poneva quel outil convient.',
    showMore: 'Afficher plus',
    showMoreRetry: 'Réessayer de charger plus',
    loadingMore: 'Chargement…',
    moreFailed: 'Impossible de charger d’autres intégrations. Vos résultats sont toujours là.',
    manageMembers: 'Choisir ce que les membres peuvent connecter',
    manageDone: 'Terminé',
    manageHint:
      'Activez ou désactivez des apps pour les membres. Les comptes déjà connectés le restent.',
    allowFor: 'Autoriser les nouvelles connexions à {name} pour {company}',
    tileConnect: 'Connecter',
    tileConnecting: 'Connexion…',
    tileConnected: 'Connectée',
    tileSignIn: 'Se reconnecter',
    tileOff: 'Désactivée',
    tileUnavailable: 'Indisponible',
    tileNotAllowed: 'Non autorisée',
    tileNotAllowedNote: 'Votre administrateur a désactivé les nouvelles connexions',
    tileAccountOnly: 'Connexion de compte uniquement · pas encore d’outils de chat',
    tileApiKey: 'Clé API requise',
    tileOwnApp: 'Utilise votre propre app {name}',
    tileAdminSetup: 'Configuration par un propriétaire ou un administrateur',
    otherWays: 'Autres façons de se connecter',
    serverTitle: 'Votre propre serveur',
    serverBody: 'Ajoutez des outils depuis un serveur MCP géré par votre équipe.',
    serverAction: 'Ajouter un serveur',
    databaseTitle: 'Une base de données',
    databaseBody: 'PostgreSQL, limitée aux schémas ou tables que vous choisissez.',
    databaseAction: 'Connecter',
    assistantsTitle: 'Autres assistants IA',
    assistantsBody:
      'Laissez Claude, ChatGPT ou un agent de code lire les connaissances approuvées de l’entreprise.',
    assistantsAction: 'Configurer',
    assistantsSheetIntro:
      'Les assistants hors de Poneva qui lisent les connaissances approuvées de l’entreprise, et les agents de code qui travaillent pour vous.',
    footnote:
      'Vous approuvez les modifications avant leur exécution. Intégrations fournies par Pipedream ; les outils proposés varient selon l’app.',
    askBody:
      'Vous ne savez pas laquelle choisir ? Poneva peut suggérer des connexions d’après le travail que vous décrivez.',
    askAction: 'Demander dans le chat',
    askPrompt:
      'Quelles sources de l’entreprise serait-il le plus utile de connecter pour notre travail, d’après ce que tu sais de nous ?',
    compactTitle: 'Apportez vos outils.',
    forYou: 'Pour vous',
    browseAll: 'Parcourir tous les outils',
    selectedForFocus: 'Sélectionnés selon vos priorités',
    popupBlocked: 'Le navigateur a bloqué la fenêtre de connexion.',
    popupOpen: 'Ouvrir la page de connexion',
    popupKeepChecking: 'Cette page continue de vérifier.',
    noticeAdded: 'Connexion ajoutée.',
    noticeAddedReturn:
      'Connexion ajoutée. Vous pouvez fermer cet onglet et reprendre votre travail.',
    noticeSignInUnfinished:
      'L’authentification n’a pas abouti. Utilisez Se reconnecter sur le serveur pour réessayer.',
    noticeChecked: 'Connexion vérifiée avec succès.',
    noticeUnreachable: 'Le serveur n’a pas encore pu être joint.',
    noticeSignInReady: 'Une page de connexion est prête dans un nouvel onglet.',
    noticeAuthorized: 'L’autorisation est terminée.',
    noticeDatabaseRemoved: 'PostgreSQL a été déconnecté.',
    noticeStoreConnected: 'Boutique Shopify connectée.',
    noticeStoreChooseAccess:
      'Boutique Shopify connectée. Ouvrez-la dans Vos connexions pour choisir qui peut l’utiliser.',
    allowFailed: 'Impossible de mettre à jour {name}. Réessayez.',
    accessUnknown: 'Les détails de l’accès ne sont pas disponibles.',
    metaStep: 'Étape {step} sur {total}',
    stepsLabel: 'Étapes de configuration',
    stepChoose: 'Choisir',
    stepPrepare: 'Préparer',
    stepAccess: 'Accès',
    stepSignIn: 'Connexion',
    metaChooseTitle: 'Connecter Meta Ads',
    metaChooseIntro:
      'Faites entrer vos comptes publicitaires Meta dans Poneva. Choisissez d’abord ce que Poneva peut en faire.',
    metaReporting: 'Rapports',
    metaReportingTag: 'Lecture seule',
    metaReportingBody:
      'Lit les comptes publicitaires, campagnes, ensembles de publicités, publicités et résultats, ainsi que vos Pages Facebook. Poneva ne modifie jamais rien dans Meta.',
    metaManage: 'Rapports et gestion des publicités',
    metaManageTag: 'Lecture et écriture',
    metaManageBody:
      'Crée et met aussi à jour des campagnes, publicités et catalogues avec les propres outils de Meta. Vous choisissez si Poneva demande avant chaque modification.',
    metaOwnApp:
      'Meta demande à chaque entreprise de se connecter via sa propre app Meta. L’étape suivante vous guide pour la configurer, une seule fois.',
    metaConnectedAlready: 'Connectée',
    prepareTitle: 'Préparez votre app Meta',
    prepareIntro:
      'À faire une seule fois dans Meta for Developers. Vous avez besoin d’un accès administrateur au compte Meta Business de votre entreprise.',
    prepareAppTitle: 'Créez ou ouvrez votre app Meta',
    prepareAppReporting: 'Activez Marketing API et Facebook Login for Business.',
    prepareAppManage: 'Ajoutez le cas d’utilisation « Create and manage ads with the ads MCP ».',
    prepareAppOpen: 'Ouvrir Meta for Developers',
    prepareRedirectTitle: 'Ajoutez l’adresse de connexion de Poneva',
    prepareRedirectBody:
      'Dans Facebook Login for Business, collez-la sous Valid OAuth Redirect URIs. Conservez les adresses déjà présentes, puis enregistrez.',
    prepareSecretTitle: 'Gardez votre App ID et votre App Secret à portée de main',
    prepareSecretBody:
      'Vous les trouverez tous deux dans votre app, sous App settings → Basic. Vous les collerez dans une fenêtre sécurisée à la dernière étape, jamais dans Poneva.',
    prepareDone: 'Fait',
    copy: 'Copier',
    copied: 'Copié',
    copyAddress: 'Copier l’adresse de connexion',
    copyManual: 'Sélectionnez l’adresse et copiez-la avec votre clavier.',
    permissionsTitle: 'Ce que Meta vous demandera d’approuver',
    permissionsNote:
      'Meta peut demander à vérifier votre entreprise avant que l’app puisse accéder à d’autres comptes publicitaires que les vôtres.',
    scopeAdsRead: 'Lire vos publicités et leurs résultats',
    scopeAdsManagement: 'Créer et modifier des publicités',
    scopeAdsMcp: 'Utiliser les outils publicitaires de Meta',
    scopeBusiness: 'Voir vos portefeuilles business',
    scopePages: 'Lister les Pages que vous gérez',
    scopePageEngagement: 'Lire les informations et l’engagement des Pages',
    scopeInstagram: 'Lire les informations de base du compte Instagram',
    scopeCatalog: 'Gérer les catalogues de produits',
    guide: 'Guide pas à pas',
    metaAccessTitle: 'Qui peut utiliser Meta Ads ?',
    metaAccessIntro:
      'Les comptes publicitaires appartiennent généralement à toute l’entreprise. Vous pouvez modifier ce choix à tout moment.',
    metaReadOnlyTitle: 'Lecture seule',
    metaReadOnlyBody:
      'Les rapports ne modifient jamais rien dans Meta. Pour que Poneva gère les publicités, choisissez la gestion des publicités à la première étape.',
    signInTitle: 'Se connecter avec Meta',
    signInIntro:
      'Une fenêtre sécurisée s’ouvre. Terminez-y la procédure et cette page se mettra à jour d’elle-même.',
    signInWindow: 'Dans la fenêtre sécurisée',
    signInPaste: 'Collez votre App ID et votre App Secret',
    signInChoose: 'Choisissez votre entreprise et ses comptes publicitaires',
    signInApprove: 'Approuvez l’accès dans Meta',
    signInWaiting: 'En attente de Meta…',
    signInSecret:
      'Votre App Secret est envoyé directement à Nango, le service de connexion utilisé par Poneva, et y est stocké chiffré. Poneva ne le voit jamais.',
    stopWaiting: 'Arrêter d’attendre',
    doneTitle: '{name} est connecté',
    doneAccountsYou: 'Vous pouvez maintenant interroger Poneva sur {count}.',
    doneAccountsCompany:
      'Tout le monde chez {company} peut maintenant interroger Poneva sur {count}.',
    doneTools: 'Meta a fourni {count} pour ce compte.',
    doneTry: 'Essayez de demander',
    doneAsk: 'Poser une question sur mes publicités',
    doneDone: 'Terminé',
    doneTryReport: 'Comment se sont comportées les campagnes de la semaine dernière ?',
    doneTryReturn: 'Quelles publicités ont eu le meilleur retour ce mois-ci ?',
    continue: 'Continuer',
    continueToMeta: 'Continuer vers Meta',
    back: 'Retour',
    cancel: 'Annuler',
    askHelp: 'Demander de l’aide à Poneva',
    askHelpPrompt:
      'Aide-moi à configurer notre propre app Meta pour que je puisse connecter Meta Ads à Poneva.',
    chatTitle: 'Connectez pour continuer',
    chatIntro: 'Ce chat reste où il est.',
    chatConnectedYou: 'Connectée pour vous',
    chatConnectedCompany: 'Connectée pour {company}',
    chatUnavailable: 'Session ouverte · outils indisponibles',
    chatFoot:
      'Connectez-vous auprès du fournisseur. Quand « Connectée » s’affiche, demandez à Poneva de continuer.',
    chatPopupBlocked: 'Votre navigateur a bloqué la fenêtre de connexion.',
    chatOpenAuthorization: 'Ouvrir la connexion',
    chatFallbackBody: 'Utilisez ce service dans votre travail.',
  },
  ga: {
    eyebrow: 'Spás oibre {company}',
    title: 'Ceangail',
    intro:
      'Ceangail d’uirlisí chun faisnéis a aimsiú agus obair a chur i gcrích. Roghnaigh cé a fhéadann gach ceangal a úsáid agus an féidir le Poneva léamh nó athruithe a dhéanamh freisin.',
    activity: 'Gníomhaíocht',
    backToConnections: 'Ar ais chuig Ceangail',
    yoursTitle: 'Do cheangail',
    filterLabel: 'Taispeáin ceangail',
    filterAll: 'Gach ceann',
    filterMine: 'Mise amháin',
    filterShared: 'Comhroinnte',
    filterAttention: 'Aird ag teastáil',
    columnConnection: 'Ceangal',
    columnWho: 'Cé a fhéadann é a úsáid',
    columnWhat: 'Cad is féidir le Poneva a dhéanamh',
    columnStatus: 'Stádas',
    yoursLoading: 'Do cheangail á lódáil…',
    yoursEmpty: 'Níl aon rud ceangailte fós. Cuardaigh thíos chun do chéad uirlis a cheangal.',
    filterEmpty: 'Níl aon rud anseo faoi láthair.',
    yoursLoadError: 'Níorbh fhéidir do cheangail a lódáil.',
    serversLoadError: 'Níorbh fhéidir do fhreastalaithe féin a lódáil.',
    databasesLoadError: 'Níorbh fhéidir do bhunachair sonraí a lódáil.',
    retry: 'Bain triail eile as',
    stateConnected: 'Ceangailte',
    stateAttention: 'Síniú isteach ag teastáil',
    stateReview: 'Athbhreithnigh rochtain',
    stateUnavailable: 'Níl sé ar fáil',
    stateOff: 'Múchta',
    stateConnecting: 'Á cheangal…',
    signInAgain: 'Sínigh isteach arís',
    attentionBody:
      'Chuaigh an síniú isteach do {name} in éag nó cúlghaireadh é. Sínigh isteach arís chun leanúint á úsáid.',
    reviewBody:
      'D’athraigh an cuntas taobh thiar de {name}. Athbhreithnigh cé a fhéadann é a úsáid agus cad is féidir le Poneva a dhéanamh sula n-úsáidtear arís é.',
    unavailableBody: 'Tá tú sínithe isteach in {name}, ach níl a uirlisí ar fáil faoi láthair.',
    offBody:
      'Tá {name} múchta. Ní féidir le duine ar bith é a úsáid go dtí go gcastar air arís é. Ní scriostar aon rud.',
    kindServer: 'Do fhreastalaí féin',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tábla|two:thábla|few:thábla|many:dtábla|other:tábla',
    adAccountsNoun:
      'one:chuntas fógraíochta|two:chuntas fógraíochta|few:chuntas fógraíochta|many:gcuntas fógraíochta|other:cuntas fógraíochta',
    toolsNoun: 'one:uirlis|two:uirlis|few:uirlis|many:n-uirlis|other:uirlis',
    close: 'Dún',
    moreActions: 'Tuilleadh gníomhartha do {name}',
    tabOverview: 'Forléargas',
    tabAccess: 'Rochtain',
    tabActivity: 'Gníomhaíocht',
    connectedOn: 'Ó {date}',
    lastUsed: 'Úsáidte go deireanach {when}',
    whatTitle: 'Cad is féidir le Poneva a dhéanamh',
    readsLine: 'Léann sé nuair a iarrann tú',
    changesAsk: 'Fiafraíonn sé díot sula n-athraíonn sé aon rud',
    changesAllowed: 'Déanann sé athruithe gan fiafraí. Taifeadtar gach ceann in Gníomhaíocht.',
    changesNever: 'Ní athraíonn sé aon rud riamh',
    readinessReady: '{count} réidh',
    change: 'Athraigh',
    tryTitle: 'Bain triail as i gcomhrá',
    tryPrompt: 'Conas is féidir leat cabhrú liom le {name}?',
    addAccount: 'Cuir cuntas eile leis',
    disconnect: 'Dícheangail',
    disconnectConfirm:
      'Dícheangail {name}? Stopfaidh sé ag obair i gcomhráite, in aipeanna agus i ngníomhairí go dtí go gceanglaítear arís é.',
    disconnectKeep: 'Coinnigh',
    nativeOnlyYou: 'Is tusa amháin a fhéadann an cuntas seo a úsáid.',
    nativeEveryone: 'Is féidir le gach duine in {company} an cuntas seo a úsáid.',
    providerPermissions: 'Tá ceadanna cuntais an tsoláthraí féin i bhfeidhm fós.',
    manageDatabase: 'Bainistigh an bunachar sonraí',
    activityLoading: 'Gníomhaíocht á lódáil…',
    activityLoadError: 'Níorbh fhéidir an ghníomhaíocht a lódáil.',
    activityRead: 'Léamh',
    activityChange: 'Athrú',
    activitySetting: 'Socrú',
    activityDenied: 'Diúltaithe',
    activityFailed: 'Theip air',
    activityUnknown: 'Toradh anaithnid',
    activityAll: 'Oscail an ghníomhaíocht go léir',
    activityToday: 'Inniu',
    activityYesterday: 'Inné',
    eventCreated: 'Cruthaíodh ceangal',
    eventShared: 'Athraíodh an chomhroinnt',
    eventPersonAdded: 'Cuireadh duine leis',
    eventPersonRemoved: 'Baineadh duine',
    eventAccessChanged: 'Athraíodh an rochtain',
    eventRevoked: 'Múchadh an rochtain',
    eventRemoved: 'Baineadh an ceangal',
    eventSettings: 'Athraíodh teorainn ama na gceisteanna',
    addTitle: 'Cuir ceangal leis',
    integrations:
      'one:chomhtháthú|two:chomhtháthú|few:chomhtháthú|many:gcomhtháthú|other:comhtháthú',
    searchLabel: 'Cuardaigh comhtháthuithe',
    searchPlaceholder: 'Cuardaigh comhtháthuithe',
    clearSearch: 'Glan an cuardach',
    inYours: 'I do cheangail',
    addNew: 'Cuir ceann nua leis',
    resultsFor: '{count} do “{query}”',
    resultsNoun: 'one:toradh|two:thoradh|few:thoradh|many:dtoradh|other:toradh',
    popularFirst: 'Na cinn is coitianta ar dtús',
    searching: 'Á chuardach…',
    searchUnavailable: 'Níl cuardach ceangal ar fáil faoi láthair.',
    noResults:
      'Níl aon rud a mheaitseálann fós. Bain triail as ainm eile, nó fiafraigh de Poneva cén uirlis a oireann.',
    showMore: 'Taispeáin tuilleadh',
    showMoreRetry: 'Bain triail eile as tuilleadh a lódáil',
    loadingMore: 'Á lódáil…',
    moreFailed: 'Níorbh fhéidir tuilleadh comhtháthuithe a lódáil. Tá do thorthaí fós anseo.',
    manageMembers: 'Roghnaigh cad is féidir le baill a cheangal',
    manageDone: 'Déanta',
    manageHint:
      'Cuir aipeanna ar siúl nó múch iad do bhaill. Fanann cuntais atá ceangailte cheana ceangailte.',
    allowFor: 'Ceadaigh ceangail nua le {name} do {company}',
    tileConnect: 'Ceangail',
    tileConnecting: 'Á cheangal…',
    tileConnected: 'Ceangailte',
    tileSignIn: 'Sínigh isteach arís',
    tileOff: 'Múchta',
    tileUnavailable: 'Níl sé ar fáil',
    tileNotAllowed: 'Níl sé ceadaithe',
    tileNotAllowedNote: 'Mhúch do riarthóir ceangail nua',
    tileAccountOnly: 'Ceangal cuntais amháin · gan uirlisí comhrá fós',
    tileApiKey: 'Eochair API ag teastáil',
    tileOwnApp: 'Úsáideann sé d’aip {name} féin',
    tileAdminSetup: 'Socrú ag Úinéir nó ag Riarthóir',
    otherWays: 'Bealaí eile le ceangal',
    serverTitle: 'Do fhreastalaí féin',
    serverBody: 'Cuir uirlisí leis ó fhreastalaí MCP a ritheann d’fhoireann.',
    serverAction: 'Cuir freastalaí leis',
    databaseTitle: 'Bunachar sonraí',
    databaseBody: 'PostgreSQL, teoranta do na scéimeanna nó na táblaí a roghnaíonn tú.',
    databaseAction: 'Ceangail',
    assistantsTitle: 'Cúntóirí AI eile',
    assistantsBody:
      'Lig do Claude, ChatGPT nó gníomhaire códaithe eolas ceadaithe na cuideachta a léamh.',
    assistantsAction: 'Socraigh',
    assistantsSheetIntro:
      'Cúntóirí lasmuigh de Poneva a léann eolas ceadaithe na cuideachta, agus na gníomhairí códaithe a oibríonn duit.',
    footnote:
      'Ceadaíonn tú athruithe sula ndéantar iad. Comhtháthuithe le Pipedream; athraíonn na huirlisí a thairgeann gach aip.',
    askBody:
      'Níl tú cinnte cé acu a theastaíonn uait? Is féidir le Poneva ceangail a mholadh bunaithe ar an obair a dhéanann tú cur síos uirthi.',
    askAction: 'Fiafraigh sa chomhrá',
    askPrompt:
      'Cé na foinsí cuideachta ba úsáidí le ceangal dár gcuid oibre, bunaithe ar a bhfuil ar eolas agat fúinn?',
    compactTitle: 'Tabhair d’uirlisí leat.',
    forYou: 'Duitse',
    browseAll: 'Brabhsáil na huirlisí go léir',
    selectedForFocus: 'Roghnaithe do d’fhócas',
    popupBlocked: 'Chuir an brabhsálaí bac ar an bhfuinneog síniú isteach.',
    popupOpen: 'Oscail an leathanach síniú isteach',
    popupKeepChecking: 'Leanann an leathanach seo ag seiceáil.',
    noticeAdded: 'Cuireadh an ceangal leis.',
    noticeAddedReturn:
      'Cuireadh an ceangal leis. Is féidir leat an cluaisín seo a dhúnadh agus filleadh ar do chuid oibre.',
    noticeSignInUnfinished:
      'Níor críochnaíodh an síniú isteach. Úsáid Sínigh isteach arís ar an bhfreastalaí chun triail eile a bhaint as.',
    noticeChecked: 'Seiceáladh an ceangal go rathúil.',
    noticeUnreachable: 'Níorbh fhéidir teacht ar an bhfreastalaí fós.',
    noticeSignInReady: 'Tá leathanach síniú isteach réidh i gcluaisín nua.',
    noticeAuthorized: 'Tá an t-údarú críochnaithe.',
    noticeDatabaseRemoved: 'Dícheanglaíodh PostgreSQL.',
    noticeStoreConnected: 'Tá an siopa Shopify ceangailte.',
    noticeStoreChooseAccess:
      'Tá an siopa Shopify ceangailte. Oscail é in Do cheangail chun a roghnú cé a fhéadann é a úsáid.',
    allowFailed: 'Níorbh fhéidir {name} a nuashonrú. Bain triail eile as.',
    accessUnknown: 'Níl sonraí rochtana ar fáil.',
    metaStep: 'Céim {step} as {total}',
    stepsLabel: 'Céimeanna socraithe',
    stepChoose: 'Roghnaigh',
    stepPrepare: 'Ullmhaigh',
    stepAccess: 'Rochtain',
    stepSignIn: 'Sínigh isteach',
    metaChooseTitle: 'Ceangail Meta Ads',
    metaChooseIntro:
      'Tabhair do chuntais fógraíochta Meta isteach in Poneva. Ar dtús, roghnaigh cad is féidir le Poneva a dhéanamh leo.',
    metaReporting: 'Tuairisciú',
    metaReportingTag: 'Léamh amháin',
    metaReportingBody:
      'Léann sé cuntais fógraíochta, feachtais, tacair fógraí, fógraí agus torthaí, chomh maith le do Leathanaigh Facebook. Ní athraíonn Poneva aon rud in Meta riamh.',
    metaManage: 'Tuairisciú agus bainistiú fógraí',
    metaManageTag: 'Léamh agus scríobh',
    metaManageBody:
      'Cruthaíonn agus nuashonraíonn sé feachtais, fógraí agus catalóga freisin le huirlisí Meta féin. Roghnaíonn tusa an bhfiafraíonn Poneva roimh gach athrú.',
    metaOwnApp:
      'Iarrann Meta ar gach gnó ceangal trína aip Meta féin. Treoróidh an chéad chéim eile thú tríd an socrú, uair amháin.',
    metaConnectedAlready: 'Ceangailte',
    prepareTitle: 'Ullmhaigh d’aip Meta',
    prepareIntro:
      'Déan é seo uair amháin in Meta for Developers. Teastaíonn rochtain riarthóra uait ar ghnó Meta do chuideachta.',
    prepareAppTitle: 'Cruthaigh nó oscail d’aip Meta',
    prepareAppReporting: 'Cuir Marketing API agus Facebook Login for Business ar siúl.',
    prepareAppManage: 'Cuir an cás úsáide “Create and manage ads with the ads MCP” leis.',
    prepareAppOpen: 'Oscail Meta for Developers',
    prepareRedirectTitle: 'Cuir seoladh síniú isteach Poneva leis',
    prepareRedirectBody:
      'In Facebook Login for Business, greamaigh é faoi Valid OAuth Redirect URIs. Coinnigh na seoltaí atá ann cheana, ansin sábháil.',
    prepareSecretTitle: 'Bíodh d’App ID agus d’App Secret réidh agat',
    prepareSecretBody:
      'Tá an dá cheann i d’aip faoi App settings → Basic. Greamóidh tú iad i bhfuinneog shlán sa chéim dheireanach, ní in Poneva choíche.',
    prepareDone: 'Déanta',
    copy: 'Cóipeáil',
    copied: 'Cóipeáilte',
    copyAddress: 'Cóipeáil an seoladh síniú isteach',
    copyManual: 'Roghnaigh an seoladh agus cóipeáil é le do mhéarchlár.',
    permissionsTitle: 'Cad a iarrfaidh Meta ort a cheadú',
    permissionsNote:
      'D’fhéadfadh Meta iarraidh do ghnó a fhíorú sular féidir leis an aip cuntais fógraíochta seachas do chuid féin a bhaint amach.',
    scopeAdsRead: 'Do chuid fógraí agus a dtorthaí a léamh',
    scopeAdsManagement: 'Fógraí a chruthú agus a athrú',
    scopeAdsMcp: 'Uirlisí fógraíochta Meta a úsáid',
    scopeBusiness: 'Do phunanna gnó a fheiceáil',
    scopePages: 'Liosta de na Leathanaigh a bhainistíonn tú',
    scopePageEngagement: 'Sonraí agus rannpháirtíocht Leathanaigh a léamh',
    scopeInstagram: 'Bunsonraí cuntais Instagram a léamh',
    scopeCatalog: 'Catalóga táirgí a bhainistiú',
    guide: 'Treoir céim ar chéim',
    metaAccessTitle: 'Cé a fhéadann Meta Ads a úsáid?',
    metaAccessIntro:
      'Is leis an gcuideachta ar fad cuntais fógraíochta de ghnáth. Is féidir leat é seo a athrú am ar bith.',
    metaReadOnlyTitle: 'Léamh amháin',
    metaReadOnlyBody:
      'Ní athraíonn tuairisciú aon rud in Meta riamh. Chun ligean do Poneva fógraí a bhainistiú, roghnaigh bainistiú fógraí sa chéad chéim.',
    signInTitle: 'Sínigh isteach le Meta',
    signInIntro:
      'Osclaíonn fuinneog shlán. Críochnaigh ansin, agus nuashonróidh an leathanach seo é féin.',
    signInWindow: 'Sa fhuinneog shlán',
    signInPaste: 'Greamaigh d’App ID agus d’App Secret',
    signInChoose: 'Roghnaigh do ghnó agus a chuntais fógraíochta',
    signInApprove: 'Ceadaigh rochtain in Meta',
    signInWaiting: 'Ag fanacht le Meta…',
    signInSecret:
      'Téann d’App Secret díreach chuig Nango, an tseirbhís síniú isteach a úsáideann Poneva, agus stóráiltear criptithe ansin é. Ní fheiceann Poneva riamh é.',
    stopWaiting: 'Stop ag fanacht',
    doneTitle: 'Tá {name} ceangailte',
    doneAccountsYou: 'Is féidir leat ceisteanna a chur ar Poneva anois faoi {count}.',
    doneAccountsCompany:
      'Is féidir le gach duine in {company} ceisteanna a chur ar Poneva anois faoi {count}.',
    doneTools: 'Sholáthair Meta {count} don chuntas seo.',
    doneTry: 'Bain triail as ceist',
    doneAsk: 'Fiafraigh faoi mo chuid fógraí',
    doneDone: 'Déanta',
    doneTryReport: 'Conas a d’éirigh le feachtais na seachtaine seo caite?',
    doneTryReturn: 'Cé na fógraí is mó a thug toradh ar infheistíocht an mhí seo?',
    continue: 'Lean ar aghaidh',
    continueToMeta: 'Lean ar aghaidh chuig Meta',
    back: 'Siar',
    cancel: 'Cealaigh',
    askHelp: 'Iarr cabhair ar Poneva',
    askHelpPrompt:
      'Cabhraigh liom ár n-aip Meta féin a shocrú ionas gur féidir liom Meta Ads a cheangal le Poneva.',
    chatTitle: 'Ceangail chun leanúint ar aghaidh',
    chatIntro: 'Fanann an comhrá seo mar atá.',
    chatConnectedYou: 'Ceangailte duitse',
    chatConnectedCompany: 'Ceangailte do {company}',
    chatUnavailable: 'Sínithe isteach · níl uirlisí ar fáil',
    chatFoot:
      'Sínigh isteach leis an soláthraí. Nuair a thaispeánann sé Ceangailte, iarr ar Poneva leanúint ar aghaidh.',
    chatPopupBlocked: 'Chuir do bhrabhsálaí bac ar an bhfuinneog síniú isteach.',
    chatOpenAuthorization: 'Oscail síniú isteach',
    chatFallbackBody: 'Úsáid an tseirbhís seo i do chuid oibre.',
  },
  hr: {
    eyebrow: 'Radni prostor tvrtke {company}',
    title: 'Veze',
    intro:
      'Povežite svoje alate kako biste pronalazili informacije i obavljali posao. Odaberite tko može koristiti svaku vezu i smije li Poneva čitati ili i unositi promjene.',
    activity: 'Aktivnost',
    backToConnections: 'Natrag na Veze',
    yoursTitle: 'Vaše veze',
    filterLabel: 'Prikaži veze',
    filterAll: 'Sve',
    filterMine: 'Samo ja',
    filterShared: 'Dijeljene',
    filterAttention: 'Traže pozornost',
    columnConnection: 'Veza',
    columnWho: 'Tko je može koristiti',
    columnWhat: 'Što Poneva može raditi',
    columnStatus: 'Status',
    yoursLoading: 'Učitavanje vaših veza…',
    yoursEmpty: 'Još ništa nije povezano. Pretražite ispod kako biste povezali svoj prvi alat.',
    filterEmpty: 'Ovdje trenutačno nema ničega.',
    yoursLoadError: 'Vaše veze nije moguće učitati.',
    serversLoadError: 'Vaše vlastite poslužitelje nije moguće učitati.',
    databasesLoadError: 'Vaše baze podataka nije moguće učitati.',
    retry: 'Pokušaj ponovno',
    stateConnected: 'Povezano',
    stateAttention: 'Potrebna prijava',
    stateReview: 'Pregledajte pristup',
    stateUnavailable: 'Nije dostupno',
    stateOff: 'Isključeno',
    stateConnecting: 'Povezivanje…',
    signInAgain: 'Ponovno se prijavite',
    attentionBody:
      'Prijava za uslugu {name} je istekla ili je opozvana. Ponovno se prijavite kako biste je nastavili koristiti.',
    reviewBody:
      'Račun iza usluge {name} se promijenio. Prije ponovne upotrebe provjerite tko je može koristiti i što Poneva može raditi.',
    unavailableBody: 'U usluzi {name} ste prijavljeni, ali njezini alati trenutačno nisu dostupni.',
    offBody:
      'Usluga {name} je isključena. Nitko je ne može koristiti dok se ponovno ne uključi. Ništa se ne briše.',
    kindServer: 'Vlastiti poslužitelj',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tablica|few:tablice|other:tablica',
    adAccountsNoun: 'one:oglasni račun|few:oglasna računa|other:oglasnih računa',
    toolsNoun: 'one:alat|few:alata|other:alata',
    close: 'Zatvori',
    moreActions: 'Više radnji za uslugu {name}',
    tabOverview: 'Pregled',
    tabAccess: 'Pristup',
    tabActivity: 'Aktivnost',
    connectedOn: 'Od {date}',
    lastUsed: 'Zadnji put korišteno {when}',
    whatTitle: 'Što Poneva može raditi',
    readsLine: 'Čita kad to zatražite',
    changesAsk: 'Pita vas prije nego što išta promijeni',
    changesAllowed: 'Unosi promjene bez pitanja. Svaka se bilježi u Aktivnosti.',
    changesNever: 'Nikad ništa ne mijenja',
    readinessReady: 'Dostupno: {count}',
    change: 'Promijeni',
    tryTitle: 'Isprobajte u razgovoru',
    tryPrompt: 'U čemu mi možeš pomoći uz {name}?',
    addAccount: 'Dodaj još jedan račun',
    disconnect: 'Prekini vezu',
    disconnectConfirm:
      'Prekinuti vezu s uslugom {name}? Prestat će raditi u chatovima, aplikacijama i agentima dok se ponovno ne poveže.',
    disconnectKeep: 'Zadrži',
    nativeOnlyYou: 'Samo vi možete koristiti ovaj račun.',
    nativeEveryone: 'Svi u tvrtki {company} mogu koristiti ovaj račun.',
    providerPermissions: 'Vlastite dozvole računa kod pružatelja usluge i dalje vrijede.',
    manageDatabase: 'Upravljaj bazom podataka',
    activityLoading: 'Učitavanje aktivnosti…',
    activityLoadError: 'Aktivnost nije moguće učitati.',
    activityRead: 'Čitanje',
    activityChange: 'Promjena',
    activitySetting: 'Postavka',
    activityDenied: 'Odbijeno',
    activityFailed: 'Neuspješno',
    activityUnknown: 'Nepoznat ishod',
    activityAll: 'Otvori svu aktivnost',
    activityToday: 'Danas',
    activityYesterday: 'Jučer',
    eventCreated: 'Veza je stvorena',
    eventShared: 'Dijeljenje je promijenjeno',
    eventPersonAdded: 'Osoba je dodana',
    eventPersonRemoved: 'Osoba je uklonjena',
    eventAccessChanged: 'Pristup je promijenjen',
    eventRevoked: 'Pristup je isključen',
    eventRemoved: 'Veza je uklonjena',
    eventSettings: 'Vremensko ograničenje upita je promijenjeno',
    addTitle: 'Dodajte vezu',
    integrations: 'one:integracija|few:integracije|other:integracija',
    searchLabel: 'Pretraži integracije',
    searchPlaceholder: 'Pretraži integracije',
    clearSearch: 'Očisti pretragu',
    inYours: 'U vašim vezama',
    addNew: 'Dodaj novu',
    resultsFor: '{count} za „{query}”',
    resultsNoun: 'one:rezultat|few:rezultata|other:rezultata',
    popularFirst: 'Najprije popularne',
    searching: 'Pretraživanje…',
    searchUnavailable: 'Pretraživanje veza privremeno nije dostupno.',
    noResults:
      'Još ništa ne odgovara. Pokušajte s drugim nazivom ili pitajte Ponevu koji alat odgovara.',
    showMore: 'Prikaži više',
    showMoreRetry: 'Ponovno pokušaj učitati više',
    loadingMore: 'Učitavanje…',
    moreFailed: 'Dodatne integracije nije moguće učitati. Vaši rezultati i dalje su ovdje.',
    manageMembers: 'Odaberite što članovi mogu povezati',
    manageDone: 'Gotovo',
    manageHint:
      'Uključite ili isključite aplikacije za članove. Već povezani računi ostaju povezani.',
    allowFor: 'Dopusti nove veze s uslugom {name} za tvrtku {company}',
    tileConnect: 'Poveži',
    tileConnecting: 'Povezivanje…',
    tileConnected: 'Povezano',
    tileSignIn: 'Ponovno se prijavite',
    tileOff: 'Isključeno',
    tileUnavailable: 'Nije dostupno',
    tileNotAllowed: 'Nije dopušteno',
    tileNotAllowedNote: 'Vaš administrator isključio je nove veze',
    tileAccountOnly: 'Samo veza s računom · još bez alata za chat',
    tileApiKey: 'Potreban je API ključ',
    tileOwnApp: 'Koristi vašu vlastitu aplikaciju {name}',
    tileAdminSetup: 'Postavlja vlasnik ili administrator',
    otherWays: 'Drugi načini povezivanja',
    serverTitle: 'Vlastiti poslužitelj',
    serverBody: 'Dodajte alate s MCP poslužitelja kojim upravlja vaš tim.',
    serverAction: 'Dodaj poslužitelj',
    databaseTitle: 'Baza podataka',
    databaseBody: 'PostgreSQL, ograničen na sheme ili tablice koje odaberete.',
    databaseAction: 'Poveži',
    assistantsTitle: 'Drugi AI asistenti',
    assistantsBody:
      'Dopustite da Claude, ChatGPT ili agent za programiranje čitaju odobreno znanje tvrtke.',
    assistantsAction: 'Postavi',
    assistantsSheetIntro:
      'Asistenti izvan Poneve koji čitaju odobreno znanje tvrtke i agenti za programiranje koji rade za vas.',
    footnote:
      'Promjene odobravate prije nego što se izvrše. Integracije pokreće Pipedream; alati koje svaka aplikacija nudi razlikuju se.',
    askBody:
      'Niste sigurni koja vam treba? Poneva može predložiti veze na temelju posla koji opišete.',
    askAction: 'Pitaj u razgovoru',
    askPrompt:
      'Koje bi izvore tvrtke bilo najkorisnije povezati za naš posao, s obzirom na ono što znaš o nama?',
    compactTitle: 'Ponesite svoje alate.',
    forYou: 'Za vas',
    browseAll: 'Pregledajte sve alate',
    selectedForFocus: 'Odabrano prema vašem fokusu',
    popupBlocked: 'Preglednik je blokirao prozor za prijavu.',
    popupOpen: 'Otvori stranicu za prijavu',
    popupKeepChecking: 'Ova stranica nastavlja provjeravati.',
    noticeAdded: 'Veza je dodana.',
    noticeAddedReturn: 'Veza je dodana. Možete zatvoriti ovu karticu i vratiti se svom poslu.',
    noticeSignInUnfinished:
      'Prijava nije dovršena. Za ponovni pokušaj odaberite Ponovno se prijavite kod poslužitelja.',
    noticeChecked: 'Veza je uspješno provjerena.',
    noticeUnreachable: 'Poslužitelj još nije dostupan.',
    noticeSignInReady: 'Stranica za prijavu spremna je u novoj kartici.',
    noticeAuthorized: 'Odobrenje je dovršeno.',
    noticeDatabaseRemoved: 'Veza s bazom PostgreSQL je prekinuta.',
    noticeStoreConnected: 'Trgovina Shopify je povezana.',
    noticeStoreChooseAccess:
      'Trgovina Shopify je povezana. Otvorite je u odjeljku Vaše veze kako biste odabrali tko je može koristiti.',
    allowFailed: 'Ažuriranje usluge {name} nije uspjelo. Pokušajte ponovno.',
    accessUnknown: 'Pojedinosti o pristupu nisu dostupne.',
    metaStep: 'Korak {step} od {total}',
    stepsLabel: 'Koraci postavljanja',
    stepChoose: 'Odabir',
    stepPrepare: 'Priprema',
    stepAccess: 'Pristup',
    stepSignIn: 'Prijava',
    metaChooseTitle: 'Poveži Meta Ads',
    metaChooseIntro:
      'Dovedite svoje Meta oglasne račune u Ponevu. Najprije odaberite što Poneva smije raditi s njima.',
    metaReporting: 'Izvješća',
    metaReportingTag: 'Samo čitanje',
    metaReportingBody:
      'Čita oglasne račune, kampanje, skupove oglasa, oglase i rezultate, kao i vaše Facebook stranice. Poneva nikad ništa ne mijenja u Meti.',
    metaManage: 'Izvješća i upravljanje oglasima',
    metaManageTag: 'Čitanje i pisanje',
    metaManageBody:
      'Također stvara i ažurira kampanje, oglase i kataloge Metinim vlastitim alatima. Vi birate hoće li Poneva pitati prije svake promjene.',
    metaOwnApp:
      'Meta traži da se svaka tvrtka poveže putem vlastite Meta aplikacije. Sljedeći korak vodi vas kroz njezino postavljanje, jednom.',
    metaConnectedAlready: 'Povezano',
    prepareTitle: 'Pripremite svoju Meta aplikaciju',
    prepareIntro:
      'Učinite to jednom u Meta for Developers. Potreban vam je administratorski pristup poslovnom računu vaše tvrtke u Meti.',
    prepareAppTitle: 'Stvorite ili otvorite svoju Meta aplikaciju',
    prepareAppReporting: 'Uključite Marketing API i Facebook Login for Business.',
    prepareAppManage: 'Dodajte slučaj upotrebe „Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Otvori Meta for Developers',
    prepareRedirectTitle: 'Dodajte Ponevinu adresu za prijavu',
    prepareRedirectBody:
      'U Facebook Login for Business zalijepite je pod Valid OAuth Redirect URIs. Zadržite adrese koje su već ondje, a zatim spremite.',
    prepareSecretTitle: 'Pripremite App ID i App Secret',
    prepareSecretBody:
      'Oboje se nalazi u vašoj aplikaciji pod App settings → Basic. Zalijepit ćete ih u sigurni prozor u zadnjem koraku, nikad u Ponevu.',
    prepareDone: 'Gotovo',
    copy: 'Kopiraj',
    copied: 'Kopirano',
    copyAddress: 'Kopiraj adresu za prijavu',
    copyManual: 'Označite adresu i kopirajte je tipkovnicom.',
    permissionsTitle: 'Što će vas Meta tražiti da odobrite',
    permissionsNote:
      'Meta može zatražiti potvrdu vaše tvrtke prije nego što aplikacija dobije pristup oglasnim računima izvan vaših vlastitih.',
    scopeAdsRead: 'Čitanje vaših oglasa i njihovih rezultata',
    scopeAdsManagement: 'Stvaranje i mijenjanje oglasa',
    scopeAdsMcp: 'Upotreba Metinih alata za oglase',
    scopeBusiness: 'Pregled vaših poslovnih portfelja',
    scopePages: 'Popis stranica kojima upravljate',
    scopePageEngagement: 'Čitanje podataka i angažmana stranica',
    scopeInstagram: 'Čitanje osnovnih podataka Instagram računa',
    scopeCatalog: 'Upravljanje katalozima proizvoda',
    guide: 'Vodič korak po korak',
    metaAccessTitle: 'Tko može koristiti Meta Ads?',
    metaAccessIntro:
      'Oglasni računi obično pripadaju cijeloj tvrtki. To možete promijeniti bilo kada.',
    metaReadOnlyTitle: 'Samo čitanje',
    metaReadOnlyBody:
      'Izvješća nikad ništa ne mijenjaju u Meti. Ako želite da Poneva upravlja oglasima, u prvom koraku odaberite upravljanje oglasima.',
    signInTitle: 'Prijava putem Mete',
    signInIntro: 'Otvara se sigurni prozor. Dovršite ondje, a ova će se stranica sama ažurirati.',
    signInWindow: 'U sigurnom prozoru',
    signInPaste: 'Zalijepite App ID i App Secret',
    signInChoose: 'Odaberite svoju tvrtku i njezine oglasne račune',
    signInApprove: 'Odobrite pristup u Meti',
    signInWaiting: 'Čeka se Meta…',
    signInSecret:
      'Vaš App Secret ide izravno u Nango, uslugu za prijavu koju Poneva koristi, i ondje se pohranjuje šifriran. Poneva ga nikad ne vidi.',
    stopWaiting: 'Prestani čekati',
    doneTitle: 'Usluga {name} je povezana',
    doneAccountsYou: 'Sada Ponevu možete pitati za {count}.',
    doneAccountsCompany: 'Svi u tvrtki {company} sada mogu pitati Ponevu za {count}.',
    doneTools: 'Meta je za ovaj račun omogućila {count}.',
    doneTry: 'Pokušajte pitati',
    doneAsk: 'Pitaj o mojim oglasima',
    doneDone: 'Gotovo',
    doneTryReport: 'Kako su se pokazale kampanje iz prošlog tjedna?',
    doneTryReturn: 'Koji su oglasi ovaj mjesec donijeli najbolji povrat?',
    continue: 'Nastavi',
    continueToMeta: 'Nastavi na Metu',
    back: 'Natrag',
    cancel: 'Odustani',
    askHelp: 'Zatraži pomoć od Poneve',
    askHelpPrompt:
      'Pomozi mi postaviti našu vlastitu Meta aplikaciju kako bismo mogli povezati Meta Ads s Ponevom.',
    chatTitle: 'Povežite za nastavak',
    chatIntro: 'Ovaj razgovor ostaje gdje jest.',
    chatConnectedYou: 'Povezano za vas',
    chatConnectedCompany: 'Povezano za tvrtku {company}',
    chatUnavailable: 'Prijavljeno · alati nisu dostupni',
    chatFoot:
      'Prijavite se kod pružatelja usluge. Kad se prikaže Povezano, zamolite Ponevu da nastavi.',
    chatPopupBlocked: 'Vaš je preglednik blokirao prozor za prijavu.',
    chatOpenAuthorization: 'Otvori prijavu',
    chatFallbackBody: 'Koristite ovu uslugu u svom radu.',
  },
  hu: {
    eyebrow: '{company} munkaterülete',
    title: 'Kapcsolatok',
    intro:
      'Kapcsolja össze eszközeit, hogy információt találjon és elvégezze a munkát. Minden kapcsolatnál válassza ki, ki használhatja, és hogy a Poneva csak olvashat-e, vagy módosíthat is.',
    activity: 'Tevékenység',
    backToConnections: 'Vissza a Kapcsolatokhoz',
    yoursTitle: 'Az Ön kapcsolatai',
    filterLabel: 'Kapcsolatok megjelenítése',
    filterAll: 'Mind',
    filterMine: 'Csak én',
    filterShared: 'Megosztott',
    filterAttention: 'Figyelmet igényel',
    columnConnection: 'Kapcsolat',
    columnWho: 'Ki használhatja',
    columnWhat: 'Mit tehet a Poneva',
    columnStatus: 'Állapot',
    yoursLoading: 'Kapcsolatok betöltése…',
    yoursEmpty:
      'Még nincs semmi csatlakoztatva. Keressen lent, és csatlakoztassa az első eszközét.',
    filterEmpty: 'Itt most nincs semmi.',
    yoursLoadError: 'Nem sikerült betölteni a kapcsolatait.',
    serversLoadError: 'Nem sikerült betölteni a saját szervereit.',
    databasesLoadError: 'Nem sikerült betölteni az adatbázisait.',
    retry: 'Próbálja újra',
    stateConnected: 'Csatlakoztatva',
    stateAttention: 'Bejelentkezés szükséges',
    stateReview: 'Hozzáférés ellenőrzése',
    stateUnavailable: 'Nem érhető el',
    stateOff: 'Kikapcsolva',
    stateConnecting: 'Csatlakozás…',
    signInAgain: 'Újbóli bejelentkezés',
    attentionBody:
      'A(z) {name} bejelentkezése lejárt, vagy visszavonták. A további használathoz jelentkezzen be újra.',
    reviewBody:
      'Megváltozott a(z) {name} mögötti fiók. Mielőtt újra használatba kerül, ellenőrizze, ki használhatja, és mit tehet a Poneva.',
    unavailableBody:
      'A(z) {name} szolgáltatásba be van jelentkezve, de az eszközei most nem érhetők el.',
    offBody:
      'A(z) {name} ki van kapcsolva. Senki sem használhatja, amíg újra be nem kapcsolják. Semmi sem törlődik.',
    kindServer: 'Saját szerver',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tábla|other:tábla',
    adAccountsNoun: 'one:hirdetési fiók|other:hirdetési fiók',
    toolsNoun: 'one:eszköz|other:eszköz',
    close: 'Bezárás',
    moreActions: 'További műveletek: {name}',
    tabOverview: 'Áttekintés',
    tabAccess: 'Hozzáférés',
    tabActivity: 'Tevékenység',
    connectedOn: '{date} óta',
    lastUsed: 'Utoljára használva: {when}',
    whatTitle: 'Mit tehet a Poneva',
    readsLine: 'Olvas, amikor kéri',
    changesAsk: 'Rákérdez, mielőtt bármit módosítana',
    changesAllowed: 'Rákérdezés nélkül módosít. Minden módosítás rögzül a Tevékenységben.',
    changesNever: 'Soha nem módosít semmit',
    readinessReady: '{count} használatra kész',
    change: 'Módosítás',
    tryTitle: 'Próbálja ki egy csevegésben',
    tryPrompt: 'Miben tudsz segíteni a(z) {name} használatával?',
    addAccount: 'Másik fiók hozzáadása',
    disconnect: 'Leválasztás',
    disconnectConfirm:
      'Leválasztja a(z) {name} szolgáltatást? Nem fog működni a csevegésekben, alkalmazásokban és ügynökökben, amíg újra nem csatlakoztatják.',
    disconnectKeep: 'Megtartás',
    nativeOnlyYou: 'Ezt a fiókot csak Ön használhatja.',
    nativeEveryone: 'Ezt a fiókot mindenki használhatja itt: {company}.',
    providerPermissions: 'A szolgáltató saját fiókjogosultságai továbbra is érvényesek.',
    manageDatabase: 'Adatbázis kezelése',
    activityLoading: 'Tevékenység betöltése…',
    activityLoadError: 'Nem sikerült betölteni a tevékenységet.',
    activityRead: 'Olvasás',
    activityChange: 'Változtatás',
    activitySetting: 'Beállítás',
    activityDenied: 'Elutasítva',
    activityFailed: 'Sikertelen',
    activityUnknown: 'Ismeretlen eredmény',
    activityAll: 'Összes tevékenység megnyitása',
    activityToday: 'Ma',
    activityYesterday: 'Tegnap',
    eventCreated: 'Kapcsolat létrehozva',
    eventShared: 'Megosztás módosítva',
    eventPersonAdded: 'Személy hozzáadva',
    eventPersonRemoved: 'Személy eltávolítva',
    eventAccessChanged: 'Hozzáférés módosítva',
    eventRevoked: 'Hozzáférés kikapcsolva',
    eventRemoved: 'Kapcsolat eltávolítva',
    eventSettings: 'Lekérdezési időkorlát módosítva',
    addTitle: 'Kapcsolat hozzáadása',
    integrations: 'one:integráció|other:integráció',
    searchLabel: 'Integrációk keresése',
    searchPlaceholder: 'Integrációk keresése',
    clearSearch: 'Keresés törlése',
    inYours: 'Az Ön kapcsolatai között',
    addNew: 'Új hozzáadása',
    resultsFor: '{count} erre: „{query}”',
    resultsNoun: 'one:találat|other:találat',
    popularFirst: 'Népszerűek elöl',
    searching: 'Keresés…',
    searchUnavailable: 'A kapcsolatkeresés átmenetileg nem érhető el.',
    noResults:
      'Még nincs egyezés. Próbáljon másik nevet, vagy kérdezze meg a Ponevát, melyik eszköz illik.',
    showMore: 'Több megjelenítése',
    showMoreRetry: 'További elemek betöltésének újrapróbálása',
    loadingMore: 'Betöltés…',
    moreFailed: 'Nem sikerült további integrációkat betölteni. A találatai továbbra is itt vannak.',
    manageMembers: 'Annak kiválasztása, mit csatlakoztathatnak a tagok',
    manageDone: 'Kész',
    manageHint:
      'Kapcsoljon be vagy ki alkalmazásokat a tagok számára. A már csatlakoztatott fiókok csatlakoztatva maradnak.',
    allowFor: 'Új {name}-kapcsolatok engedélyezése {company} számára',
    tileConnect: 'Csatlakoztatás',
    tileConnecting: 'Csatlakozás…',
    tileConnected: 'Csatlakoztatva',
    tileSignIn: 'Újbóli bejelentkezés',
    tileOff: 'Kikapcsolva',
    tileUnavailable: 'Nem érhető el',
    tileNotAllowed: 'Nem engedélyezett',
    tileNotAllowedNote: 'Az adminisztrátor kikapcsolta az új kapcsolatokat',
    tileAccountOnly: 'Csak fiókkapcsolat · még nincsenek csevegőeszközök',
    tileApiKey: 'API-kulcs szükséges',
    tileOwnApp: 'A saját {name}-alkalmazását használja',
    tileAdminSetup: 'Tulajdonos vagy adminisztrátor állítja be',
    otherWays: 'További csatlakozási módok',
    serverTitle: 'Saját szerver',
    serverBody: 'Adjon hozzá eszközöket egy MCP-szerverről, amelyet a csapata üzemeltet.',
    serverAction: 'Szerver hozzáadása',
    databaseTitle: 'Adatbázis',
    databaseBody: 'PostgreSQL, az Ön által választott sémákra vagy táblákra korlátozva.',
    databaseAction: 'Csatlakoztatás',
    assistantsTitle: 'Más MI-asszisztensek',
    assistantsBody:
      'Engedje, hogy a Claude, a ChatGPT vagy egy kódolóügynök olvassa a jóváhagyott céges tudást.',
    assistantsAction: 'Beállítás',
    assistantsSheetIntro:
      'A Poneván kívüli asszisztensek, amelyek a jóváhagyott céges tudást olvassák, és az Ön helyett dolgozó kódolóügynökök.',
    footnote:
      'A módosításokat Ön hagyja jóvá a végrehajtásuk előtt. Az integrációkat a Pipedream biztosítja; az egyes alkalmazások eszközei eltérőek.',
    askBody:
      'Nem biztos benne, melyikre van szüksége? A Poneva a leírt munka alapján kapcsolatokat javasolhat.',
    askAction: 'Kérdezzen a csevegésben',
    askPrompt:
      'Mely céges források összekapcsolása lenne a leghasznosabb a munkánkhoz az alapján, amit rólunk tudsz?',
    compactTitle: 'Hozza magával az eszközeit.',
    forYou: 'Önnek',
    browseAll: 'Összes eszköz böngészése',
    selectedForFocus: 'Az Ön fókuszához választva',
    popupBlocked: 'A böngésző letiltotta a bejelentkezési ablakot.',
    popupOpen: 'Bejelentkezési oldal megnyitása',
    popupKeepChecking: 'Ez az oldal tovább ellenőriz.',
    noticeAdded: 'Kapcsolat hozzáadva.',
    noticeAddedReturn: 'Kapcsolat hozzáadva. Bezárhatja ezt a lapot, és visszatérhet a munkájához.',
    noticeSignInUnfinished:
      'A bejelentkezés nem fejeződött be. Az újrapróbáláshoz válassza a szervernél az Újbóli bejelentkezés lehetőséget.',
    noticeChecked: 'A kapcsolat ellenőrzése sikeres.',
    noticeUnreachable: 'A szerver még nem érhető el.',
    noticeSignInReady: 'Egy bejelentkezési oldal vár egy új lapon.',
    noticeAuthorized: 'Az engedélyezés befejeződött.',
    noticeDatabaseRemoved: 'A PostgreSQL leválasztva.',
    noticeStoreConnected: 'Shopify-áruház csatlakoztatva.',
    noticeStoreChooseAccess:
      'Shopify-áruház csatlakoztatva. Nyissa meg Az Ön kapcsolatai részben, és válassza ki, ki használhatja.',
    allowFailed: 'Nem sikerült frissíteni: {name}. Próbálja újra.',
    accessUnknown: 'A hozzáférés részletei nem érhetők el.',
    metaStep: '{step}/{total}. lépés',
    stepsLabel: 'Beállítási lépések',
    stepChoose: 'Választás',
    stepPrepare: 'Előkészítés',
    stepAccess: 'Hozzáférés',
    stepSignIn: 'Bejelentkezés',
    metaChooseTitle: 'Meta Ads csatlakoztatása',
    metaChooseIntro:
      'Hozza be Meta-hirdetési fiókjait a Ponevába. Először válassza ki, mit tehet velük a Poneva.',
    metaReporting: 'Jelentések',
    metaReportingTag: 'Csak olvasás',
    metaReportingBody:
      'Olvassa a hirdetési fiókokat, kampányokat, hirdetéssorozatokat, hirdetéseket és eredményeket, valamint Facebook-oldalait. A Poneva soha nem módosít semmit a Metában.',
    metaManage: 'Jelentések és hirdetéskezelés',
    metaManageTag: 'Olvasás és írás',
    metaManageBody:
      'Emellett kampányokat, hirdetéseket és katalógusokat hoz létre és frissít a Meta saját eszközeivel. Ön dönti el, hogy a Poneva rákérdezzen-e minden módosítás előtt.',
    metaOwnApp:
      'A Meta azt kéri, hogy minden vállalkozás saját Meta-alkalmazáson keresztül csatlakozzon. A következő lépés egyszer végigvezeti a beállításán.',
    metaConnectedAlready: 'Csatlakoztatva',
    prepareTitle: 'Készítse elő Meta-alkalmazását',
    prepareIntro:
      'Ezt egyszer kell megtennie a Meta for Developers felületén. Adminisztrátori hozzáférésre van szüksége cége Meta-vállalkozásához.',
    prepareAppTitle: 'Hozza létre vagy nyissa meg Meta-alkalmazását',
    prepareAppReporting: 'Kapcsolja be ezeket: Marketing API és Facebook Login for Business.',
    prepareAppManage:
      'Adja hozzá ezt a használati esetet: „Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Meta for Developers megnyitása',
    prepareRedirectTitle: 'Adja hozzá a Poneva bejelentkezési címét',
    prepareRedirectBody:
      'A Facebook Login for Business részben illessze be a Valid OAuth Redirect URIs mezőbe. Hagyja meg a már ott lévő címeket, majd mentsen.',
    prepareSecretTitle: 'Tartsa kéznél: App ID és App Secret',
    prepareSecretBody:
      'Mindkettő megtalálható az alkalmazásában itt: App settings → Basic. Az utolsó lépésben egy biztonságos ablakba illeszti be őket, soha nem a Ponevába.',
    prepareDone: 'Kész',
    copy: 'Másolás',
    copied: 'Másolva',
    copyAddress: 'Bejelentkezési cím másolása',
    copyManual: 'Jelölje ki a címet, és másolja a billentyűzettel.',
    permissionsTitle: 'Amit a Meta jóváhagyásra kér',
    permissionsNote:
      'A Meta kérheti a vállalkozása ellenőrzését, mielőtt az alkalmazás hozzáférhet a sajátjain kívüli hirdetési fiókokhoz.',
    scopeAdsRead: 'Hirdetései és azok eredményeinek olvasása',
    scopeAdsManagement: 'Hirdetések létrehozása és módosítása',
    scopeAdsMcp: 'A Meta hirdetési eszközeinek használata',
    scopeBusiness: 'Vállalkozási portfóliói megtekintése',
    scopePages: 'Az Ön által kezelt oldalak listázása',
    scopePageEngagement: 'Oldaladatok és interakciók olvasása',
    scopeInstagram: 'Instagram-fiók alapadatainak olvasása',
    scopeCatalog: 'Termékkatalógusok kezelése',
    guide: 'Lépésenkénti útmutató',
    metaAccessTitle: 'Ki használhatja a Meta Ads szolgáltatást?',
    metaAccessIntro:
      'A hirdetési fiókok általában az egész céghez tartoznak. Ezt bármikor módosíthatja.',
    metaReadOnlyTitle: 'Csak olvasás',
    metaReadOnlyBody:
      'A jelentések soha nem módosítanak semmit a Metában. Ha szeretné, hogy a Poneva hirdetéseket kezeljen, az első lépésben válassza a hirdetéskezelést.',
    signInTitle: 'Bejelentkezés a Metával',
    signInIntro: 'Megnyílik egy biztonságos ablak. Fejezze be ott, és ez az oldal magától frissül.',
    signInWindow: 'A biztonságos ablakban',
    signInPaste: 'Illessze be: App ID és App Secret',
    signInChoose: 'Válassza ki vállalkozását és annak hirdetési fiókjait',
    signInApprove: 'Hagyja jóvá a hozzáférést a Metában',
    signInWaiting: 'Várakozás a Metára…',
    signInSecret:
      'Az App Secret közvetlenül a Nangóhoz, a Poneva által használt bejelentkezési szolgáltatáshoz kerül, és ott titkosítva tárolódik. A Poneva soha nem látja.',
    stopWaiting: 'Várakozás leállítása',
    doneTitle: 'A(z) {name} csatlakoztatva',
    doneAccountsYou: 'Mostantól kérdezhet a Ponevától a következőkről: {count}.',
    doneAccountsCompany:
      '{company} minden tagja kérdezhet mostantól a Ponevától a következőkről: {count}.',
    doneTools: 'A Meta ennyit biztosított ehhez a fiókhoz: {count}.',
    doneTry: 'Kérdezze például ezt',
    doneAsk: 'Kérdés a hirdetéseimről',
    doneDone: 'Kész',
    doneTryReport: 'Hogyan teljesítettek a múlt heti kampányok?',
    doneTryReturn: 'Mely hirdetések hozták a legjobb megtérülést ebben a hónapban?',
    continue: 'Tovább',
    continueToMeta: 'Tovább a Metához',
    back: 'Vissza',
    cancel: 'Mégse',
    askHelp: 'Segítség kérése a Ponevától',
    askHelpPrompt:
      'Segíts beállítani a saját Meta-alkalmazásunkat, hogy csatlakoztathassam a Meta Adst a Ponevához.',
    chatTitle: 'Csatlakoztasson a folytatáshoz',
    chatIntro: 'Ez a csevegés ott marad, ahol van.',
    chatConnectedYou: 'Az Ön számára csatlakoztatva',
    chatConnectedCompany: '{company} számára csatlakoztatva',
    chatUnavailable: 'Bejelentkezve · az eszközök nem érhetők el',
    chatFoot:
      'Jelentkezzen be a szolgáltatónál. Amikor megjelenik a Csatlakoztatva felirat, kérje meg a Ponevát, hogy folytassa.',
    chatPopupBlocked: 'A böngészője letiltotta a bejelentkezési ablakot.',
    chatOpenAuthorization: 'Bejelentkezés megnyitása',
    chatFallbackBody: 'Használja ezt a szolgáltatást a munkájában.',
  },
  it: {
    eyebrow: 'Area di lavoro di {company}',
    title: 'Connessioni',
    intro:
      'Collega i tuoi strumenti per trovare informazioni e portare a termine il lavoro. Scegli chi può usare ogni connessione e se Poneva può solo leggere o anche apportare modifiche.',
    activity: 'Attività',
    backToConnections: 'Torna a Connessioni',
    yoursTitle: 'Le tue connessioni',
    filterLabel: 'Mostra connessioni',
    filterAll: 'Tutte',
    filterMine: 'Solo io',
    filterShared: 'Condivise',
    filterAttention: 'Da controllare',
    columnConnection: 'Connessione',
    columnWho: 'Chi può usarla',
    columnWhat: 'Cosa può fare Poneva',
    columnStatus: 'Stato',
    yoursLoading: 'Caricamento delle tue connessioni…',
    yoursEmpty:
      'Non hai ancora collegato nulla. Cerca qui sotto per collegare il tuo primo strumento.',
    filterEmpty: 'Al momento non c’è niente qui.',
    yoursLoadError: 'Impossibile caricare le tue connessioni.',
    serversLoadError: 'Impossibile caricare i tuoi server.',
    databasesLoadError: 'Impossibile caricare i tuoi database.',
    retry: 'Riprova',
    stateConnected: 'Collegata',
    stateAttention: 'Accesso necessario',
    stateReview: 'Rivedi l’accesso',
    stateUnavailable: 'Non disponibile',
    stateOff: 'Disattivata',
    stateConnecting: 'Collegamento…',
    signInAgain: 'Accedi di nuovo',
    attentionBody:
      'L’accesso a {name} è scaduto o è stato revocato. Accedi di nuovo per continuare a usarla.',
    reviewBody:
      'L’account collegato a {name} è cambiato. Prima di usarla di nuovo, controlla chi può usarla e cosa può fare Poneva.',
    unavailableBody:
      'Hai effettuato l’accesso a {name}, ma i suoi strumenti al momento non sono disponibili.',
    offBody:
      '{name} è disattivata. Nessuno potrà usarla finché non verrà riattivata. Non viene eliminato nulla.',
    kindServer: 'Il tuo server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabella|many:di tabelle|other:tabelle',
    adAccountsNoun:
      'one:account pubblicitario|many:di account pubblicitari|other:account pubblicitari',
    toolsNoun: 'one:strumento|many:di strumenti|other:strumenti',
    close: 'Chiudi',
    moreActions: 'Altre azioni per {name}',
    tabOverview: 'Panoramica',
    tabAccess: 'Accesso',
    tabActivity: 'Attività',
    connectedOn: 'Dal {date}',
    lastUsed: 'Ultimo utilizzo {when}',
    whatTitle: 'Cosa può fare Poneva',
    readsLine: 'Legge quando glielo chiedi',
    changesAsk: 'Ti chiede prima di modificare qualsiasi cosa',
    changesAllowed: 'Apporta modifiche senza chiedere. Ognuna viene registrata in Attività.',
    changesNever: 'Non modifica mai nulla',
    readinessReady: '{count} a disposizione',
    change: 'Modifica',
    tryTitle: 'Provalo in una chat',
    tryPrompt: 'In cosa puoi aiutarmi con {name}?',
    addAccount: 'Aggiungi un altro account',
    disconnect: 'Scollega',
    disconnectConfirm:
      'Scollegare {name}? Smetterà di funzionare nelle chat, nelle App e negli agenti finché non verrà ricollegata.',
    disconnectKeep: 'Mantieni',
    nativeOnlyYou: 'Solo tu puoi usare questo account.',
    nativeEveryone: 'Tutti in {company} possono usare questo account.',
    providerPermissions: 'Restano valide le autorizzazioni dell’account presso il fornitore.',
    manageDatabase: 'Gestisci database',
    activityLoading: 'Caricamento dell’attività…',
    activityLoadError: 'Impossibile caricare l’attività.',
    activityRead: 'Lettura',
    activityChange: 'Modifica',
    activitySetting: 'Impostazione',
    activityDenied: 'Negato',
    activityFailed: 'Non riuscito',
    activityUnknown: 'Esito sconosciuto',
    activityAll: 'Apri tutta l’attività',
    activityToday: 'Oggi',
    activityYesterday: 'Ieri',
    eventCreated: 'Connessione creata',
    eventShared: 'Condivisione modificata',
    eventPersonAdded: 'Persona aggiunta',
    eventPersonRemoved: 'Persona rimossa',
    eventAccessChanged: 'Accesso modificato',
    eventRevoked: 'Accesso disattivato',
    eventRemoved: 'Connessione rimossa',
    eventSettings: 'Limite di tempo delle query modificato',
    addTitle: 'Aggiungi una connessione',
    integrations: 'one:integrazione|many:di integrazioni|other:integrazioni',
    searchLabel: 'Cerca integrazioni',
    searchPlaceholder: 'Cerca integrazioni',
    clearSearch: 'Cancella ricerca',
    inYours: 'Nelle tue connessioni',
    addNew: 'Aggiungi nuove',
    resultsFor: '{count} per “{query}”',
    resultsNoun: 'one:risultato|many:di risultati|other:risultati',
    popularFirst: 'Prima le più popolari',
    searching: 'Ricerca in corso…',
    searchUnavailable: 'La ricerca delle connessioni è temporaneamente non disponibile.',
    noResults:
      'Ancora nessuna corrispondenza. Prova un altro nome o chiedi a Poneva quale strumento è adatto.',
    showMore: 'Mostra altro',
    showMoreRetry: 'Riprova a caricarne altre',
    loadingMore: 'Caricamento…',
    moreFailed: 'Impossibile caricare altre integrazioni. I tuoi risultati sono ancora qui.',
    manageMembers: 'Scegli cosa possono collegare i membri',
    manageDone: 'Fatto',
    manageHint:
      'Attiva o disattiva le app per i membri. Gli account già collegati restano collegati.',
    allowFor: 'Consenti nuove connessioni a {name} per {company}',
    tileConnect: 'Collega',
    tileConnecting: 'Collegamento…',
    tileConnected: 'Collegata',
    tileSignIn: 'Accedi di nuovo',
    tileOff: 'Disattivata',
    tileUnavailable: 'Non disponibile',
    tileNotAllowed: 'Non consentita',
    tileNotAllowedNote: 'Il tuo amministratore ha disattivato le nuove connessioni',
    tileAccountOnly: 'Solo connessione dell’account · ancora nessuno strumento per la chat',
    tileApiKey: 'Chiave API richiesta',
    tileOwnApp: 'Usa la tua app {name}',
    tileAdminSetup: 'Configurazione da proprietario o amministratore',
    otherWays: 'Altri modi per collegarsi',
    serverTitle: 'Il tuo server',
    serverBody: 'Aggiungi strumenti da un server MCP gestito dal tuo team.',
    serverAction: 'Aggiungi server',
    databaseTitle: 'Un database',
    databaseBody: 'PostgreSQL, limitato agli schemi o alle tabelle che scegli.',
    databaseAction: 'Collega',
    assistantsTitle: 'Altri assistenti IA',
    assistantsBody:
      'Consenti a Claude, ChatGPT o a un agente di programmazione di leggere le conoscenze aziendali approvate.',
    assistantsAction: 'Configura',
    assistantsSheetIntro:
      'Assistenti esterni a Poneva che leggono le conoscenze aziendali approvate e gli agenti di programmazione che lavorano per te.',
    footnote:
      'Approvi le modifiche prima che vengano eseguite. Integrazioni fornite da Pipedream; gli strumenti offerti da ogni app variano.',
    askBody:
      'Non sai quale ti serve? Poneva può suggerirti connessioni in base al lavoro che descrivi.',
    askAction: 'Chiedi in chat',
    askPrompt:
      'Quali fonti aziendali sarebbe più utile collegare per il nostro lavoro, in base a quello che sai di noi?',
    compactTitle: 'Porta con te i tuoi strumenti.',
    forYou: 'Per te',
    browseAll: 'Sfoglia tutti gli strumenti',
    selectedForFocus: 'Selezionati in base alle tue priorità',
    popupBlocked: 'Il browser ha bloccato la finestra di accesso.',
    popupOpen: 'Apri la pagina di accesso',
    popupKeepChecking: 'Questa pagina continua a controllare.',
    noticeAdded: 'Connessione aggiunta.',
    noticeAddedReturn: 'Connessione aggiunta. Puoi chiudere questa scheda e tornare al tuo lavoro.',
    noticeSignInUnfinished:
      'L’accesso non è stato completato. Usa Accedi di nuovo sul server per riprovare.',
    noticeChecked: 'Connessione verificata correttamente.',
    noticeUnreachable: 'Non è ancora stato possibile raggiungere il server.',
    noticeSignInReady: 'Una pagina di accesso è pronta in una nuova scheda.',
    noticeAuthorized: 'L’autorizzazione è stata completata.',
    noticeDatabaseRemoved: 'PostgreSQL è stato scollegato.',
    noticeStoreConnected: 'Negozio Shopify collegato.',
    noticeStoreChooseAccess:
      'Negozio Shopify collegato. Aprilo in Le tue connessioni per scegliere chi può usarlo.',
    allowFailed: 'Impossibile aggiornare {name}. Riprova.',
    accessUnknown: 'I dettagli dell’accesso non sono disponibili.',
    metaStep: 'Passaggio {step} di {total}',
    stepsLabel: 'Passaggi di configurazione',
    stepChoose: 'Scegli',
    stepPrepare: 'Prepara',
    stepAccess: 'Accesso',
    stepSignIn: 'Accedi',
    metaChooseTitle: 'Collega Meta Ads',
    metaChooseIntro:
      'Porta i tuoi account pubblicitari Meta in Poneva. Prima scegli cosa può farne Poneva.',
    metaReporting: 'Report',
    metaReportingTag: 'Sola lettura',
    metaReportingBody:
      'Legge account pubblicitari, campagne, gruppi di inserzioni, inserzioni e risultati, oltre alle tue Pagine Facebook. Poneva non modifica mai nulla in Meta.',
    metaManage: 'Report e gestione delle inserzioni',
    metaManageTag: 'Lettura e scrittura',
    metaManageBody:
      'Crea e aggiorna anche campagne, inserzioni e cataloghi con gli strumenti di Meta. Scegli tu se Poneva deve chiedere prima di ogni modifica.',
    metaOwnApp:
      'Meta chiede a ogni azienda di collegarsi tramite la propria app Meta. Il passaggio successivo ti guida nella configurazione, una sola volta.',
    metaConnectedAlready: 'Collegata',
    prepareTitle: 'Prepara la tua app Meta',
    prepareIntro:
      'Fallo una sola volta in Meta for Developers. Ti serve l’accesso da amministratore al portfolio business della tua azienda su Meta.',
    prepareAppTitle: 'Crea o apri la tua app Meta',
    prepareAppReporting: 'Attiva Marketing API e Facebook Login for Business.',
    prepareAppManage: 'Aggiungi il caso d’uso “Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Apri Meta for Developers',
    prepareRedirectTitle: 'Aggiungi l’indirizzo di accesso di Poneva',
    prepareRedirectBody:
      'In Facebook Login for Business, incollalo in Valid OAuth Redirect URIs. Mantieni gli indirizzi già presenti, poi salva.',
    prepareSecretTitle: 'Tieni a portata di mano App ID e App Secret',
    prepareSecretBody:
      'Li trovi entrambi nella tua app in App settings → Basic. Li incollerai in una finestra sicura nell’ultimo passaggio, mai in Poneva.',
    prepareDone: 'Fatto',
    copy: 'Copia',
    copied: 'Copiato',
    copyAddress: 'Copia l’indirizzo di accesso',
    copyManual: 'Seleziona l’indirizzo e copialo con la tastiera.',
    permissionsTitle: 'Cosa ti chiederà di approvare Meta',
    permissionsNote:
      'Meta potrebbe chiedere di verificare la tua azienda prima che l’app possa accedere ad account pubblicitari diversi dai tuoi.',
    scopeAdsRead: 'Leggere le tue inserzioni e i loro risultati',
    scopeAdsManagement: 'Creare e modificare inserzioni',
    scopeAdsMcp: 'Usare gli strumenti pubblicitari di Meta',
    scopeBusiness: 'Vedere i tuoi portfolio aziendali',
    scopePages: 'Elencare le Pagine che gestisci',
    scopePageEngagement: 'Leggere i dettagli e l’interazione delle Pagine',
    scopeInstagram: 'Leggere i dati di base dell’account Instagram',
    scopeCatalog: 'Gestire i cataloghi prodotti',
    guide: 'Guida passo passo',
    metaAccessTitle: 'Chi può usare Meta Ads?',
    metaAccessIntro:
      'Gli account pubblicitari di solito appartengono a tutta l’azienda. Puoi cambiarlo in qualsiasi momento.',
    metaReadOnlyTitle: 'Sola lettura',
    metaReadOnlyBody:
      'I report non modificano mai nulla in Meta. Per far gestire le inserzioni a Poneva, scegli la gestione delle inserzioni nel primo passaggio.',
    signInTitle: 'Accedi con Meta',
    signInIntro: 'Si apre una finestra sicura. Completa lì e questa pagina si aggiornerà da sola.',
    signInWindow: 'Nella finestra sicura',
    signInPaste: 'Incolla App ID e App Secret',
    signInChoose: 'Scegli la tua azienda e i suoi account pubblicitari',
    signInApprove: 'Approva l’accesso in Meta',
    signInWaiting: 'In attesa di Meta…',
    signInSecret:
      'Il tuo App Secret va direttamente a Nango, il servizio di accesso usato da Poneva, e lì viene conservato crittografato. Poneva non lo vede mai.',
    stopWaiting: 'Smetti di attendere',
    doneTitle: '{name} è collegato',
    doneAccountsYou: 'Ora puoi fare domande a Poneva su {count}.',
    doneAccountsCompany: 'Ora tutti in {company} possono fare domande a Poneva su {count}.',
    doneTools: 'Meta ha fornito {count} per questo account.',
    doneTry: 'Prova a chiedere',
    doneAsk: 'Chiedi delle mie inserzioni',
    doneDone: 'Fatto',
    doneTryReport: 'Come sono andate le campagne della settimana scorsa?',
    doneTryReturn: 'Quali inserzioni hanno avuto il miglior ritorno questo mese?',
    continue: 'Continua',
    continueToMeta: 'Continua su Meta',
    back: 'Indietro',
    cancel: 'Annulla',
    askHelp: 'Chiedi aiuto a Poneva',
    askHelpPrompt:
      'Aiutami a configurare la nostra app Meta così posso collegare Meta Ads a Poneva.',
    chatTitle: 'Collega per continuare',
    chatIntro: 'Questa chat resta dov’è.',
    chatConnectedYou: 'Collegata per te',
    chatConnectedCompany: 'Collegata per {company}',
    chatUnavailable: 'Accesso effettuato · strumenti non disponibili',
    chatFoot: 'Accedi con il fornitore. Quando compare Collegata, chiedi a Poneva di continuare.',
    chatPopupBlocked: 'Il tuo browser ha bloccato la finestra di accesso.',
    chatOpenAuthorization: 'Apri l’accesso',
    chatFallbackBody: 'Usa questo servizio nel tuo lavoro.',
  },
  lt: {
    eyebrow: '{company} darbo erdvė',
    title: 'Jungtys',
    intro:
      'Prijunkite savo įrankius, kad rastumėte informacijos ir atliktumėte darbus. Pasirinkite, kas gali naudotis kiekviena jungtimi ir ar Poneva gali tik skaityti, ar ir atlikti pakeitimus.',
    activity: 'Veikla',
    backToConnections: 'Grįžti į jungtis',
    yoursTitle: 'Jūsų jungtys',
    filterLabel: 'Rodyti jungtis',
    filterAll: 'Visos',
    filterMine: 'Tik aš',
    filterShared: 'Bendrinamos',
    filterAttention: 'Reikia dėmesio',
    columnConnection: 'Jungtis',
    columnWho: 'Kas gali ja naudotis',
    columnWhat: 'Ką Poneva gali daryti',
    columnStatus: 'Būsena',
    yoursLoading: 'Įkeliamos jūsų jungtys…',
    yoursEmpty: 'Dar niekas neprijungta. Ieškokite žemiau ir prijunkite pirmąjį įrankį.',
    filterEmpty: 'Čia kol kas nieko nėra.',
    yoursLoadError: 'Nepavyko įkelti jūsų jungčių.',
    serversLoadError: 'Nepavyko įkelti jūsų serverių.',
    databasesLoadError: 'Nepavyko įkelti jūsų duomenų bazių.',
    retry: 'Bandyti dar kartą',
    stateConnected: 'Prijungta',
    stateAttention: 'Reikia prisijungti',
    stateReview: 'Peržiūrėkite prieigą',
    stateUnavailable: 'Nepasiekiama',
    stateOff: 'Išjungta',
    stateConnecting: 'Jungiama…',
    signInAgain: 'Prisijungti iš naujo',
    attentionBody:
      'Prisijungimas prie paslaugos „{name}“ baigė galioti arba buvo atšauktas. Prisijunkite iš naujo, kad galėtumėte toliau ja naudotis.',
    reviewBody:
      'Pasikeitė paskyra, susieta su paslauga „{name}“. Prieš ją vėl naudojant, peržiūrėkite, kas gali ja naudotis ir ką gali daryti Poneva.',
    unavailableBody:
      'Prie paslaugos „{name}“ prisijungta, bet jos įrankiai šiuo metu nepasiekiami.',
    offBody:
      'Paslauga „{name}“ išjungta. Niekas negalės ja naudotis, kol ji nebus vėl įjungta. Niekas neištrinama.',
    kindServer: 'Jūsų serveris',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:lentelė|few:lentelės|many:lentelės|other:lentelių',
    adAccountsNoun:
      'one:reklamos paskyra|few:reklamos paskyros|many:reklamos paskyros|other:reklamos paskyrų',
    toolsNoun: 'one:įrankis|few:įrankiai|many:įrankio|other:įrankių',
    close: 'Uždaryti',
    moreActions: 'Daugiau veiksmų: {name}',
    tabOverview: 'Apžvalga',
    tabAccess: 'Prieiga',
    tabActivity: 'Veikla',
    connectedOn: 'Nuo {date}',
    lastUsed: 'Paskutinį kartą naudota {when}',
    whatTitle: 'Ką Poneva gali daryti',
    readsLine: 'Skaito, kai paprašote',
    changesAsk: 'Prieš ką nors keisdama paklausia jūsų',
    changesAllowed:
      'Atlieka pakeitimus neklausdama. Kiekvienas pakeitimas įrašomas skiltyje „Veikla“.',
    changesNever: 'Niekada nieko nekeičia',
    readinessReady: 'Paruošta: {count}',
    change: 'Keisti',
    tryTitle: 'Išbandykite pokalbyje',
    tryPrompt: 'Kuo gali man padėti su „{name}“?',
    addAccount: 'Pridėti kitą paskyrą',
    disconnect: 'Atjungti',
    disconnectConfirm:
      'Atjungti paslaugą „{name}“? Ji nustos veikti pokalbiuose, programėlėse ir agentuose, kol vėl bus prijungta.',
    disconnectKeep: 'Palikti',
    nativeOnlyYou: 'Šią paskyrą galite naudoti tik jūs.',
    nativeEveryone: 'Šią paskyrą gali naudoti visi įmonėje „{company}“.',
    providerPermissions: 'Paslaugų teikėjo paskyros leidimai vis tiek galioja.',
    manageDatabase: 'Tvarkyti duomenų bazę',
    activityLoading: 'Įkeliama veikla…',
    activityLoadError: 'Nepavyko įkelti veiklos.',
    activityRead: 'Skaitymas',
    activityChange: 'Pakeitimas',
    activitySetting: 'Nustatymas',
    activityDenied: 'Atmesta',
    activityFailed: 'Nepavyko',
    activityUnknown: 'Nežinomas rezultatas',
    activityAll: 'Atidaryti visą veiklą',
    activityToday: 'Šiandien',
    activityYesterday: 'Vakar',
    eventCreated: 'Jungtis sukurta',
    eventShared: 'Bendrinimas pakeistas',
    eventPersonAdded: 'Žmogus pridėtas',
    eventPersonRemoved: 'Žmogus pašalintas',
    eventAccessChanged: 'Prieiga pakeista',
    eventRevoked: 'Prieiga išjungta',
    eventRemoved: 'Jungtis pašalinta',
    eventSettings: 'Užklausų laiko limitas pakeistas',
    addTitle: 'Pridėti jungtį',
    integrations: 'one:integracija|few:integracijos|many:integracijos|other:integracijų',
    searchLabel: 'Ieškoti integracijų',
    searchPlaceholder: 'Ieškoti integracijų',
    clearSearch: 'Išvalyti paiešką',
    inYours: 'Jūsų jungtyse',
    addNew: 'Pridėti naują',
    resultsFor: '{count} pagal „{query}“',
    resultsNoun: 'one:rezultatas|few:rezultatai|many:rezultato|other:rezultatų',
    popularFirst: 'Pirmiausia populiariausios',
    searching: 'Ieškoma…',
    searchUnavailable: 'Jungčių paieška laikinai nepasiekiama.',
    noResults:
      'Kol kas niekas neatitinka. Pabandykite kitą pavadinimą arba paklauskite Poneva, kuris įrankis tinka.',
    showMore: 'Rodyti daugiau',
    showMoreRetry: 'Bandyti įkelti daugiau dar kartą',
    loadingMore: 'Įkeliama…',
    moreFailed: 'Nepavyko įkelti daugiau integracijų. Jūsų rezultatai vis dar čia.',
    manageMembers: 'Pasirinkti, ką gali prijungti nariai',
    manageDone: 'Atlikta',
    manageHint:
      'Įjunkite arba išjunkite programėles nariams. Jau prijungtos paskyros lieka prijungtos.',
    allowFor: 'Leisti naujas „{name}“ jungtis įmonėje „{company}“',
    tileConnect: 'Prijungti',
    tileConnecting: 'Jungiama…',
    tileConnected: 'Prijungta',
    tileSignIn: 'Prisijungti iš naujo',
    tileOff: 'Išjungta',
    tileUnavailable: 'Nepasiekiama',
    tileNotAllowed: 'Neleidžiama',
    tileNotAllowedNote: 'Jūsų administratorius išjungė naujas jungtis',
    tileAccountOnly: 'Tik paskyros jungtis · pokalbių įrankių dar nėra',
    tileApiKey: 'Reikia API rakto',
    tileOwnApp: 'Naudoja jūsų „{name}“ programėlę',
    tileAdminSetup: 'Nustato savininkas arba administratorius',
    otherWays: 'Kiti prijungimo būdai',
    serverTitle: 'Jūsų serveris',
    serverBody: 'Pridėkite įrankių iš MCP serverio, kurį valdo jūsų komanda.',
    serverAction: 'Pridėti serverį',
    databaseTitle: 'Duomenų bazė',
    databaseBody: 'PostgreSQL, apribota jūsų pasirinktomis schemomis ar lentelėmis.',
    databaseAction: 'Prijungti',
    assistantsTitle: 'Kiti DI asistentai',
    assistantsBody:
      'Leiskite Claude, ChatGPT ar programavimo agentui skaityti patvirtintas įmonės žinias.',
    assistantsAction: 'Nustatyti',
    assistantsSheetIntro:
      'Asistentai už Poneva ribų, skaitantys patvirtintas įmonės žinias, ir programavimo agentai, dirbantys jums.',
    footnote:
      'Pakeitimus patvirtinate prieš juos atliekant. Integracijas teikia Pipedream; kiekvienos programėlės įrankiai skiriasi.',
    askBody: 'Nežinote, kurios reikia? Poneva gali pasiūlyti jungčių pagal darbą, kurį aprašysite.',
    askAction: 'Klausti pokalbyje',
    askPrompt:
      'Kuriuos įmonės šaltinius būtų naudingiausia prijungti mūsų darbui, atsižvelgiant į tai, ką apie mus žinai?',
    compactTitle: 'Pasiimkite savo įrankius.',
    forYou: 'Jums',
    browseAll: 'Naršyti visus įrankius',
    selectedForFocus: 'Atrinkta pagal jūsų dėmesio sritis',
    popupBlocked: 'Naršyklė užblokavo prisijungimo langą.',
    popupOpen: 'Atidaryti prisijungimo puslapį',
    popupKeepChecking: 'Šis puslapis toliau tikrina.',
    noticeAdded: 'Jungtis pridėta.',
    noticeAddedReturn: 'Jungtis pridėta. Galite uždaryti šį skirtuką ir grįžti prie savo darbo.',
    noticeSignInUnfinished:
      'Prisijungimas nebaigtas. Norėdami bandyti dar kartą, prie serverio pasirinkite „Prisijungti iš naujo“.',
    noticeChecked: 'Jungtis sėkmingai patikrinta.',
    noticeUnreachable: 'Serverio kol kas nepavyko pasiekti.',
    noticeSignInReady: 'Prisijungimo puslapis paruoštas naujame skirtuke.',
    noticeAuthorized: 'Autorizavimas baigtas.',
    noticeDatabaseRemoved: 'Duomenų bazė PostgreSQL atjungta.',
    noticeStoreConnected: 'Shopify parduotuvė prijungta.',
    noticeStoreChooseAccess:
      'Shopify parduotuvė prijungta. Atidarykite ją skiltyje „Jūsų jungtys“ ir pasirinkite, kas gali ja naudotis.',
    allowFailed: 'Nepavyko atnaujinti „{name}“. Bandykite dar kartą.',
    accessUnknown: 'Prieigos informacija nepasiekiama.',
    metaStep: '{step} žingsnis iš {total}',
    stepsLabel: 'Nustatymo žingsniai',
    stepChoose: 'Pasirinkimas',
    stepPrepare: 'Paruošimas',
    stepAccess: 'Prieiga',
    stepSignIn: 'Prisijungimas',
    metaChooseTitle: 'Prijungti Meta Ads',
    metaChooseIntro:
      'Perkelkite savo Meta reklamos paskyras į Poneva. Pirmiausia pasirinkite, ką Poneva gali su jomis daryti.',
    metaReporting: 'Ataskaitos',
    metaReportingTag: 'Tik skaitymas',
    metaReportingBody:
      'Skaito reklamos paskyras, kampanijas, reklamų rinkinius, reklamas ir rezultatus, taip pat jūsų Facebook puslapius. Poneva niekada nieko nekeičia Meta.',
    metaManage: 'Ataskaitos ir reklamų valdymas',
    metaManageTag: 'Skaitymas ir rašymas',
    metaManageBody:
      'Taip pat kuria ir atnaujina kampanijas, reklamas ir katalogus pačios Meta įrankiais. Jūs pasirenkate, ar Poneva klaus prieš kiekvieną pakeitimą.',
    metaOwnApp:
      'Meta reikalauja, kad kiekviena įmonė jungtųsi per savo Meta programėlę. Kitame žingsnyje vieną kartą parodysime, kaip ją nustatyti.',
    metaConnectedAlready: 'Prijungta',
    prepareTitle: 'Paruoškite savo Meta programėlę',
    prepareIntro:
      'Tai atlikite vieną kartą Meta for Developers. Jums reikia administratoriaus prieigos prie savo įmonės verslo paskyros Meta.',
    prepareAppTitle: 'Sukurkite arba atidarykite savo Meta programėlę',
    prepareAppReporting: 'Įjunkite Marketing API ir Facebook Login for Business.',
    prepareAppManage: 'Pridėkite naudojimo atvejį „Create and manage ads with the ads MCP“.',
    prepareAppOpen: 'Atidaryti Meta for Developers',
    prepareRedirectTitle: 'Pridėkite Poneva prisijungimo adresą',
    prepareRedirectBody:
      'Facebook Login for Business skiltyje įklijuokite jį į Valid OAuth Redirect URIs. Palikite jau esamus adresus ir išsaugokite.',
    prepareSecretTitle: 'Pasiruoškite App ID ir App Secret',
    prepareSecretBody:
      'Abu rasite savo programėlėje skiltyje App settings → Basic. Paskutiniame žingsnyje juos įklijuosite į saugų langą, niekada ne į Poneva.',
    prepareDone: 'Atlikta',
    copy: 'Kopijuoti',
    copied: 'Nukopijuota',
    copyAddress: 'Kopijuoti prisijungimo adresą',
    copyManual: 'Pažymėkite adresą ir nukopijuokite jį klaviatūra.',
    permissionsTitle: 'Ką Meta paprašys patvirtinti',
    permissionsNote:
      'Meta gali paprašyti patvirtinti jūsų įmonę, kol programėlė galės pasiekti ne tik jūsų reklamos paskyras.',
    scopeAdsRead: 'Skaityti jūsų reklamas ir jų rezultatus',
    scopeAdsManagement: 'Kurti ir keisti reklamas',
    scopeAdsMcp: 'Naudoti Meta reklamų įrankius',
    scopeBusiness: 'Matyti jūsų verslo portfelius',
    scopePages: 'Rodyti jūsų valdomų puslapių sąrašą',
    scopePageEngagement: 'Skaityti puslapių informaciją ir įsitraukimą',
    scopeInstagram: 'Skaityti pagrindinę Instagram paskyros informaciją',
    scopeCatalog: 'Valdyti produktų katalogus',
    guide: 'Nuoseklus vadovas',
    metaAccessTitle: 'Kas gali naudotis Meta Ads?',
    metaAccessIntro:
      'Reklamos paskyros paprastai priklauso visai įmonei. Tai galite pakeisti bet kada.',
    metaReadOnlyTitle: 'Tik skaitymas',
    metaReadOnlyBody:
      'Ataskaitos niekada nieko nekeičia Meta. Jei norite, kad Poneva valdytų reklamas, pirmame žingsnyje pasirinkite reklamų valdymą.',
    signInTitle: 'Prisijungti per Meta',
    signInIntro: 'Atsidaro saugus langas. Užbaikite ten, o šis puslapis atsinaujins pats.',
    signInWindow: 'Saugiame lange',
    signInPaste: 'Įklijuokite App ID ir App Secret',
    signInChoose: 'Pasirinkite savo įmonę ir jos reklamos paskyras',
    signInApprove: 'Patvirtinkite prieigą Meta',
    signInWaiting: 'Laukiama Meta…',
    signInSecret:
      'Jūsų App Secret siunčiamas tiesiai į Nango, Poneva naudojamą prisijungimo paslaugą, ir ten saugomas užšifruotas. Poneva jo niekada nemato.',
    stopWaiting: 'Nebelaukti',
    doneTitle: 'Paslauga „{name}“ prijungta',
    doneAccountsYou: 'Dabar galite klausti Poneva apie reklamos paskyras. Prijungta: {count}.',
    doneAccountsCompany:
      'Dabar visi įmonėje „{company}“ gali klausti Poneva apie reklamos paskyras. Prijungta: {count}.',
    doneTools: 'Meta šiai paskyrai suteikė: {count}.',
    doneTry: 'Pabandykite paklausti',
    doneAsk: 'Klausti apie mano reklamas',
    doneDone: 'Atlikta',
    doneTryReport: 'Kaip sekėsi praėjusios savaitės kampanijoms?',
    doneTryReturn: 'Kurios reklamos šį mėnesį davė didžiausią grąžą?',
    continue: 'Tęsti',
    continueToMeta: 'Tęsti į Meta',
    back: 'Atgal',
    cancel: 'Atšaukti',
    askHelp: 'Paprašyti Poneva pagalbos',
    askHelpPrompt:
      'Padėk man nustatyti mūsų Meta programėlę, kad galėčiau prijungti Meta Ads prie Poneva.',
    chatTitle: 'Prijunkite, kad galėtumėte tęsti',
    chatIntro: 'Šis pokalbis lieka ten, kur yra.',
    chatConnectedYou: 'Prijungta jums',
    chatConnectedCompany: 'Prijungta įmonei „{company}“',
    chatUnavailable: 'Prisijungta · įrankiai nepasiekiami',
    chatFoot:
      'Prisijunkite pas paslaugų teikėją. Kai bus rodoma „Prijungta“, paprašykite Poneva tęsti.',
    chatPopupBlocked: 'Jūsų naršyklė užblokavo prisijungimo langą.',
    chatOpenAuthorization: 'Atidaryti prisijungimą',
    chatFallbackBody: 'Naudokite šią paslaugą savo darbe.',
  },
  lv: {
    eyebrow: '{company} darbvieta',
    title: 'Savienojumi',
    intro:
      'Pievienojiet savus rīkus, lai atrastu informāciju un paveiktu darbu. Izvēlieties, kas var izmantot katru savienojumu un vai Poneva var tikai lasīt vai arī veikt izmaiņas.',
    activity: 'Darbība',
    backToConnections: 'Atpakaļ uz savienojumiem',
    yoursTitle: 'Jūsu savienojumi',
    filterLabel: 'Rādīt savienojumus',
    filterAll: 'Visi',
    filterMine: 'Tikai es',
    filterShared: 'Kopīgotie',
    filterAttention: 'Nepieciešama uzmanība',
    columnConnection: 'Savienojums',
    columnWho: 'Kas to var izmantot',
    columnWhat: 'Ko Poneva var darīt',
    columnStatus: 'Statuss',
    yoursLoading: 'Ielādē jūsu savienojumus…',
    yoursEmpty: 'Vēl nekas nav pievienots. Meklējiet zemāk, lai pievienotu savu pirmo rīku.',
    filterEmpty: 'Šeit pašlaik nekā nav.',
    yoursLoadError: 'Neizdevās ielādēt jūsu savienojumus.',
    serversLoadError: 'Neizdevās ielādēt jūsu serverus.',
    databasesLoadError: 'Neizdevās ielādēt jūsu datubāzes.',
    retry: 'Mēģināt vēlreiz',
    stateConnected: 'Pievienots',
    stateAttention: 'Jāpierakstās',
    stateReview: 'Pārskatiet piekļuvi',
    stateUnavailable: 'Nav pieejams',
    stateOff: 'Izslēgts',
    stateConnecting: 'Savieno…',
    signInAgain: 'Pierakstīties vēlreiz',
    attentionBody:
      'Pierakstīšanās pakalpojumā “{name}” ir beigusies vai atsaukta. Pierakstieties vēlreiz, lai turpinātu to izmantot.',
    reviewBody:
      'Konts, kas saistīts ar pakalpojumu “{name}”, ir mainījies. Pirms to atkal izmanto, pārskatiet, kas to var izmantot un ko var darīt Poneva.',
    unavailableBody:
      'Pakalpojumā “{name}” pierakstīšanās ir aktīva, bet tā rīki pašlaik nav pieejami.',
    offBody:
      'Pakalpojums “{name}” ir izslēgts. Neviens to nevarēs izmantot, līdz tas atkal tiks ieslēgts. Nekas netiek dzēsts.',
    kindServer: 'Jūsu serveris',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'zero:tabulu|one:tabula|other:tabulas',
    adAccountsNoun: 'zero:reklāmas kontu|one:reklāmas konts|other:reklāmas konti',
    toolsNoun: 'zero:rīku|one:rīks|other:rīki',
    close: 'Aizvērt',
    moreActions: 'Citas darbības: {name}',
    tabOverview: 'Pārskats',
    tabAccess: 'Piekļuve',
    tabActivity: 'Darbība',
    connectedOn: 'Kopš {date}',
    lastUsed: 'Pēdējoreiz izmantots {when}',
    whatTitle: 'Ko Poneva var darīt',
    readsLine: 'Lasa, kad jūs lūdzat',
    changesAsk: 'Pirms kaut ko maina, pajautā jums',
    changesAllowed: 'Veic izmaiņas bez jautāšanas. Katra izmaiņa tiek reģistrēta sadaļā “Darbība”.',
    changesNever: 'Nekad neko nemaina',
    readinessReady: 'Var izmantot: {count}',
    change: 'Mainīt',
    tryTitle: 'Izmēģiniet tērzēšanā',
    tryPrompt: 'Kā tu vari man palīdzēt ar “{name}”?',
    addAccount: 'Pievienot vēl vienu kontu',
    disconnect: 'Atvienot',
    disconnectConfirm:
      'Vai atvienot pakalpojumu “{name}”? Tas pārstās darboties tērzēšanās, lietotnēs un aģentos, līdz tiks atkal pievienots.',
    disconnectKeep: 'Paturēt',
    nativeOnlyYou: 'Šo kontu varat izmantot tikai jūs.',
    nativeEveryone: 'Šo kontu var izmantot visi uzņēmumā “{company}”.',
    providerPermissions: 'Pakalpojuma sniedzēja konta atļaujas joprojām ir spēkā.',
    manageDatabase: 'Pārvaldīt datubāzi',
    activityLoading: 'Ielādē darbības…',
    activityLoadError: 'Neizdevās ielādēt darbības.',
    activityRead: 'Lasīšana',
    activityChange: 'Izmaiņa',
    activitySetting: 'Iestatījums',
    activityDenied: 'Noraidīts',
    activityFailed: 'Neizdevās',
    activityUnknown: 'Nezināms rezultāts',
    activityAll: 'Atvērt visas darbības',
    activityToday: 'Šodien',
    activityYesterday: 'Vakar',
    eventCreated: 'Savienojums izveidots',
    eventShared: 'Kopīgošana mainīta',
    eventPersonAdded: 'Cilvēks pievienots',
    eventPersonRemoved: 'Cilvēks noņemts',
    eventAccessChanged: 'Piekļuve mainīta',
    eventRevoked: 'Piekļuve izslēgta',
    eventRemoved: 'Savienojums noņemts',
    eventSettings: 'Vaicājuma laika ierobežojums mainīts',
    addTitle: 'Pievienot savienojumu',
    integrations: 'zero:integrāciju|one:integrācija|other:integrācijas',
    searchLabel: 'Meklēt integrācijas',
    searchPlaceholder: 'Meklēt integrācijas',
    clearSearch: 'Notīrīt meklēšanu',
    inYours: 'Jūsu savienojumos',
    addNew: 'Pievienot jaunu',
    resultsFor: '{count} vaicājumam “{query}”',
    resultsNoun: 'zero:rezultātu|one:rezultāts|other:rezultāti',
    popularFirst: 'Vispirms populārākie',
    searching: 'Meklē…',
    searchUnavailable: 'Savienojumu meklēšana īslaicīgi nav pieejama.',
    noResults:
      'Pagaidām nekas neatbilst. Mēģiniet citu nosaukumu vai pajautājiet Poneva, kurš rīks der.',
    showMore: 'Rādīt vairāk',
    showMoreRetry: 'Vēlreiz mēģināt ielādēt vairāk',
    loadingMore: 'Ielādē…',
    moreFailed: 'Neizdevās ielādēt vairāk integrāciju. Jūsu rezultāti joprojām ir šeit.',
    manageMembers: 'Izvēlēties, ko dalībnieki var pievienot',
    manageDone: 'Gatavs',
    manageHint:
      'Ieslēdziet vai izslēdziet lietotnes dalībniekiem. Jau pievienotie konti paliek pievienoti.',
    allowFor: 'Atļaut jaunus “{name}” savienojumus uzņēmumā “{company}”',
    tileConnect: 'Pievienot',
    tileConnecting: 'Savieno…',
    tileConnected: 'Pievienots',
    tileSignIn: 'Pierakstīties vēlreiz',
    tileOff: 'Izslēgts',
    tileUnavailable: 'Nav pieejams',
    tileNotAllowed: 'Nav atļauts',
    tileNotAllowedNote: 'Jūsu administrators izslēdza jaunus savienojumus',
    tileAccountOnly: 'Tikai konta savienojums · vēl nav tērzēšanas rīku',
    tileApiKey: 'Nepieciešama API atslēga',
    tileOwnApp: 'Izmanto jūsu “{name}” lietotni',
    tileAdminSetup: 'Iestata īpašnieks vai administrators',
    otherWays: 'Citi pievienošanas veidi',
    serverTitle: 'Jūsu serveris',
    serverBody: 'Pievienojiet rīkus no MCP servera, ko uztur jūsu komanda.',
    serverAction: 'Pievienot serveri',
    databaseTitle: 'Datubāze',
    databaseBody: 'PostgreSQL, ierobežota ar jūsu izvēlētajām shēmām vai tabulām.',
    databaseAction: 'Pievienot',
    assistantsTitle: 'Citi MI asistenti',
    assistantsBody:
      'Ļaujiet Claude, ChatGPT vai programmēšanas aģentam lasīt apstiprinātās uzņēmuma zināšanas.',
    assistantsAction: 'Iestatīt',
    assistantsSheetIntro:
      'Asistenti ārpus Poneva, kas lasa apstiprinātās uzņēmuma zināšanas, un programmēšanas aģenti, kas strādā jūsu labā.',
    footnote:
      'Izmaiņas jūs apstiprināt, pirms tās tiek veiktas. Integrācijas nodrošina Pipedream; katras lietotnes piedāvātie rīki atšķiras.',
    askBody:
      'Nezināt, kurš jums vajadzīgs? Poneva var ieteikt savienojumus, pamatojoties uz jūsu aprakstīto darbu.',
    askAction: 'Jautāt tērzēšanā',
    askPrompt:
      'Kurus uzņēmuma avotus mūsu darbam būtu visnoderīgāk pievienot, ņemot vērā to, ko tu par mums zini?',
    compactTitle: 'Paņemiet līdzi savus rīkus.',
    forYou: 'Jums',
    browseAll: 'Pārlūkot visus rīkus',
    selectedForFocus: 'Atlasīts atbilstoši jūsu fokusam',
    popupBlocked: 'Pārlūks bloķēja pierakstīšanās logu.',
    popupOpen: 'Atvērt pierakstīšanās lapu',
    popupKeepChecking: 'Šī lapa turpina pārbaudīt.',
    noticeAdded: 'Savienojums pievienots.',
    noticeAddedReturn:
      'Savienojums pievienots. Varat aizvērt šo cilni un atgriezties pie sava darba.',
    noticeSignInUnfinished:
      'Pierakstīšanās netika pabeigta. Lai mēģinātu vēlreiz, pie servera izvēlieties “Pierakstīties vēlreiz”.',
    noticeChecked: 'Savienojums veiksmīgi pārbaudīts.',
    noticeUnreachable: 'Serveri vēl neizdevās sasniegt.',
    noticeSignInReady: 'Pierakstīšanās lapa ir gatava jaunā cilnē.',
    noticeAuthorized: 'Autorizācija ir pabeigta.',
    noticeDatabaseRemoved: 'PostgreSQL datubāze ir atvienota.',
    noticeStoreConnected: 'Shopify veikals pievienots.',
    noticeStoreChooseAccess:
      'Shopify veikals pievienots. Atveriet to sadaļā “Jūsu savienojumi”, lai izvēlētos, kas to var izmantot.',
    allowFailed: 'Neizdevās atjaunināt “{name}”. Mēģiniet vēlreiz.',
    accessUnknown: 'Informācija par piekļuvi nav pieejama.',
    metaStep: '{step}. solis no {total}',
    stepsLabel: 'Iestatīšanas soļi',
    stepChoose: 'Izvēle',
    stepPrepare: 'Sagatavošana',
    stepAccess: 'Piekļuve',
    stepSignIn: 'Pierakstīšanās',
    metaChooseTitle: 'Pievienot Meta Ads',
    metaChooseIntro:
      'Pievienojiet savus Meta reklāmas kontus Poneva. Vispirms izvēlieties, ko Poneva drīkst ar tiem darīt.',
    metaReporting: 'Pārskati',
    metaReportingTag: 'Tikai lasīšana',
    metaReportingBody:
      'Lasa reklāmas kontus, kampaņas, reklāmu kopas, reklāmas un rezultātus, kā arī jūsu Facebook lapas. Poneva nekad neko nemaina Meta.',
    metaManage: 'Pārskati un reklāmu pārvaldība',
    metaManageTag: 'Lasīšana un rakstīšana',
    metaManageBody:
      'Arī izveido un atjaunina kampaņas, reklāmas un katalogus ar pašas Meta rīkiem. Jūs izvēlaties, vai Poneva jautā pirms katras izmaiņas.',
    metaOwnApp:
      'Meta prasa, lai katrs uzņēmums pievienotos, izmantojot savu Meta lietotni. Nākamais solis vienreiz palīdzēs to iestatīt.',
    metaConnectedAlready: 'Pievienots',
    prepareTitle: 'Sagatavojiet savu Meta lietotni',
    prepareIntro:
      'Izdariet to vienreiz vietnē Meta for Developers. Jums vajag administratora piekļuvi sava uzņēmuma Meta biznesa kontam.',
    prepareAppTitle: 'Izveidojiet vai atveriet savu Meta lietotni',
    prepareAppReporting: 'Ieslēdziet Marketing API un Facebook Login for Business.',
    prepareAppManage: 'Pievienojiet lietojuma gadījumu “Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Atvērt Meta for Developers',
    prepareRedirectTitle: 'Pievienojiet Poneva pierakstīšanās adresi',
    prepareRedirectBody:
      'Sadaļā Facebook Login for Business ielīmējiet to laukā Valid OAuth Redirect URIs. Paturiet tur jau esošās adreses un saglabājiet.',
    prepareSecretTitle: 'Sagatavojiet App ID un App Secret',
    prepareSecretBody:
      'Abi ir jūsu lietotnē sadaļā App settings → Basic. Pēdējā solī jūs tos ielīmēsiet drošā logā, nekad ne Poneva.',
    prepareDone: 'Gatavs',
    copy: 'Kopēt',
    copied: 'Nokopēts',
    copyAddress: 'Kopēt pierakstīšanās adresi',
    copyManual: 'Atlasiet adresi un nokopējiet to ar tastatūru.',
    permissionsTitle: 'Ko Meta lūgs jums apstiprināt',
    permissionsNote:
      'Meta var lūgt verificēt jūsu uzņēmumu, pirms lietotne var piekļūt citiem reklāmas kontiem, ne tikai jūsējiem.',
    scopeAdsRead: 'Lasīt jūsu reklāmas un to rezultātus',
    scopeAdsManagement: 'Izveidot un mainīt reklāmas',
    scopeAdsMcp: 'Izmantot Meta reklāmu rīkus',
    scopeBusiness: 'Skatīt jūsu biznesa portfeļus',
    scopePages: 'Rādīt jūsu pārvaldīto lapu sarakstu',
    scopePageEngagement: 'Lasīt lapu informāciju un iesaisti',
    scopeInstagram: 'Lasīt Instagram konta pamatinformāciju',
    scopeCatalog: 'Pārvaldīt produktu katalogus',
    guide: 'Detalizēta pamācība',
    metaAccessTitle: 'Kas var izmantot Meta Ads?',
    metaAccessIntro:
      'Reklāmas konti parasti pieder visam uzņēmumam. To varat mainīt jebkurā laikā.',
    metaReadOnlyTitle: 'Tikai lasīšana',
    metaReadOnlyBody:
      'Pārskati nekad neko nemaina Meta. Ja vēlaties, lai Poneva pārvaldītu reklāmas, pirmajā solī izvēlieties reklāmu pārvaldību.',
    signInTitle: 'Pierakstīties ar Meta',
    signInIntro: 'Atveras drošs logs. Pabeidziet tur, un šī lapa atjaunināsies pati.',
    signInWindow: 'Drošajā logā',
    signInPaste: 'Ielīmējiet App ID un App Secret',
    signInChoose: 'Izvēlieties savu uzņēmumu un tā reklāmas kontus',
    signInApprove: 'Apstipriniet piekļuvi Meta',
    signInWaiting: 'Gaida Meta…',
    signInSecret:
      'Jūsu App Secret nonāk tieši pie Nango, pierakstīšanās pakalpojuma, ko izmanto Poneva, un tur tiek glabāts šifrēts. Poneva to nekad neredz.',
    stopWaiting: 'Beigt gaidīt',
    doneTitle: 'Pakalpojums “{name}” ir pievienots',
    doneAccountsYou: 'Tagad varat jautāt Poneva par saviem reklāmas kontiem. Kopā: {count}.',
    doneAccountsCompany:
      'Tagad visi uzņēmumā “{company}” var jautāt Poneva par reklāmas kontiem. Kopā: {count}.',
    doneTools: 'Meta šim kontam nodrošināja: {count}.',
    doneTry: 'Pamēģiniet pajautāt',
    doneAsk: 'Jautāt par manām reklāmām',
    doneDone: 'Gatavs',
    doneTryReport: 'Kā veicās pagājušās nedēļas kampaņām?',
    doneTryReturn: 'Kuras reklāmas šomēnes deva vislabāko atdevi?',
    continue: 'Turpināt',
    continueToMeta: 'Turpināt uz Meta',
    back: 'Atpakaļ',
    cancel: 'Atcelt',
    askHelp: 'Lūgt Poneva palīdzību',
    askHelpPrompt:
      'Palīdzi man iestatīt mūsu pašu Meta lietotni, lai es varētu pievienot Meta Ads Poneva.',
    chatTitle: 'Pievienojiet, lai turpinātu',
    chatIntro: 'Šī tērzēšana paliek, kur ir.',
    chatConnectedYou: 'Pievienots jums',
    chatConnectedCompany: 'Pievienots uzņēmumam “{company}”',
    chatUnavailable: 'Pierakstīšanās aktīva · rīki nav pieejami',
    chatFoot:
      'Pierakstieties pie pakalpojuma sniedzēja. Kad redzams “Pievienots”, palūdziet Poneva turpināt.',
    chatPopupBlocked: 'Jūsu pārlūks bloķēja pierakstīšanās logu.',
    chatOpenAuthorization: 'Atvērt pierakstīšanos',
    chatFallbackBody: 'Izmantojiet šo pakalpojumu savā darbā.',
  },
  mt: {
    eyebrow: 'Spazju tax-xogħol ta’ {company}',
    title: 'Konnessjonijiet',
    intro:
      'Qabbad l-għodod tiegħek biex issib informazzjoni u tlesti x-xogħol. Agħżel min jista’ juża kull konnessjoni u jekk Poneva jistax jaqra jew jagħmel ukoll bidliet.',
    activity: 'Attività',
    backToConnections: 'Lura għall-Konnessjonijiet',
    yoursTitle: 'Il-konnessjonijiet tiegħek',
    filterLabel: 'Uri l-konnessjonijiet',
    filterAll: 'Kollha',
    filterMine: 'Jien biss',
    filterShared: 'Maqsuma',
    filterAttention: 'Jeħtieġu attenzjoni',
    columnConnection: 'Konnessjoni',
    columnWho: 'Min jista’ jużaha',
    columnWhat: 'X’jista’ jagħmel Poneva',
    columnStatus: 'Status',
    yoursLoading: 'Qed jitgħabbew il-konnessjonijiet tiegħek…',
    yoursEmpty: 'Għadu m’hu mqabbad xejn. Fittex hawn taħt biex tqabbad l-ewwel għodda tiegħek.',
    filterEmpty: 'M’hawn xejn bħalissa.',
    yoursLoadError: 'Il-konnessjonijiet tiegħek ma setgħux jitgħabbew.',
    serversLoadError: 'Is-servers tiegħek ma setgħux jitgħabbew.',
    databasesLoadError: 'Id-databases tiegħek ma setgħux jitgħabbew.',
    retry: 'Erġa’ pprova',
    stateConnected: 'Konness',
    stateAttention: 'Jeħtieġ dħul',
    stateReview: 'Irrevedi l-aċċess',
    stateUnavailable: 'Mhux disponibbli',
    stateOff: 'Mitfi',
    stateConnecting: 'Qed jikkonnettja…',
    signInAgain: 'Erġa’ idħol',
    attentionBody: 'Id-dħul għal {name} skada jew ġie revokat. Erġa’ idħol biex tibqa’ tużah.',
    reviewBody:
      'Il-kont wara {name} inbidel. Irrevedi min jista’ jużah u x’jista’ jagħmel Poneva qabel ma jerġa’ jintuża.',
    unavailableBody: 'Int dħalt f’{name}, iżda l-għodod tiegħu mhumiex disponibbli bħalissa.',
    offBody: '{name} huwa mitfi. Ħadd ma jista’ jużah sakemm jerġa’ jinxtegħel. Xejn ma jitħassar.',
    kindServer: 'Is-server tiegħek',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabella|two:tabelli|few:tabelli|many:tabella|other:tabella',
    adAccountsNoun:
      'one:kont tar-reklami|two:kontijiet tar-reklami|few:kontijiet tar-reklami|many:kont tar-reklami|other:kont tar-reklami',
    toolsNoun: 'one:għodda|two:għodod|few:għodod|many:għodda|other:għodda',
    close: 'Agħlaq',
    moreActions: 'Aktar azzjonijiet għal {name}',
    tabOverview: 'Ħarsa ġenerali',
    tabAccess: 'Aċċess',
    tabActivity: 'Attività',
    connectedOn: 'Minn {date}',
    lastUsed: 'L-aħħar użu {when}',
    whatTitle: 'X’jista’ jagħmel Poneva',
    readsLine: 'Jaqra meta titlob',
    changesAsk: 'Jistaqsik qabel ma jbiddel xi ħaġa',
    changesAllowed: 'Jagħmel bidliet mingħajr ma jistaqsi. Kull bidla tiġi rreġistrata f’Attività.',
    changesNever: 'Qatt ma jibdel xejn',
    readinessReady: '{count} disponibbli',
    change: 'Ibdel',
    tryTitle: 'Ippruvah f’chat',
    tryPrompt: 'F’xiex tista’ tgħinni b’{name}?',
    addAccount: 'Żid kont ieħor',
    disconnect: 'Skonnettja',
    disconnectConfirm:
      'Tiskonnettja {name}? Jieqaf jaħdem fiċ-chats, fl-apps u fl-aġenti sakemm jerġa’ jiġi kkonnettjat.',
    disconnectKeep: 'Żomm',
    nativeOnlyYou: 'Int biss tista’ tuża dan il-kont.',
    nativeEveryone: 'Kulħadd f’{company} jista’ juża dan il-kont.',
    providerPermissions: 'Il-permessi tal-kont tal-fornitur stess xorta japplikaw.',
    manageDatabase: 'Immaniġġja d-database',
    activityLoading: 'Qed titgħabba l-attività…',
    activityLoadError: 'L-attività ma setgħetx titgħabba.',
    activityRead: 'Qari',
    activityChange: 'Bidla',
    activitySetting: 'Issettjar',
    activityDenied: 'Miċħud',
    activityFailed: 'Falla',
    activityUnknown: 'Riżultat mhux magħruf',
    activityAll: 'Iftaħ l-attività kollha',
    activityToday: 'Illum',
    activityYesterday: 'Ilbieraħ',
    eventCreated: 'Konnessjoni maħluqa',
    eventShared: 'Il-qsim inbidel',
    eventPersonAdded: 'Persuna miżjuda',
    eventPersonRemoved: 'Persuna mneħħija',
    eventAccessChanged: 'L-aċċess inbidel',
    eventRevoked: 'L-aċċess intefa',
    eventRemoved: 'Konnessjoni mneħħija',
    eventSettings: 'Il-limitu tal-ħin tal-queries inbidel',
    addTitle: 'Żid konnessjoni',
    integrations:
      'one:integrazzjoni|two:integrazzjonijiet|few:integrazzjonijiet|many:integrazzjoni|other:integrazzjoni',
    searchLabel: 'Fittex integrazzjonijiet',
    searchPlaceholder: 'Fittex integrazzjonijiet',
    clearSearch: 'Neħħi t-tfittxija',
    inYours: 'Fil-konnessjonijiet tiegħek',
    addNew: 'Żid ġdida',
    resultsFor: '{count} għal “{query}”',
    resultsNoun: 'one:riżultat|two:riżultati|few:riżultati|many:riżultat|other:riżultat',
    popularFirst: 'L-aktar popolari l-ewwel',
    searching: 'Qed ifittex…',
    searchUnavailable: 'It-tfittxija tal-konnessjonijiet mhix disponibbli għalissa.',
    noResults:
      'Għadu ma jaqbel xejn. Ipprova isem ieħor, jew staqsi lil Poneva liema għodda taqbel.',
    showMore: 'Uri aktar',
    showMoreRetry: 'Erġa’ pprova ttella’ aktar',
    loadingMore: 'Qed jitgħabba…',
    moreFailed: 'Ma setgħux jitgħabbew aktar integrazzjonijiet. Ir-riżultati tiegħek għadhom hawn.',
    manageMembers: 'Agħżel x’jistgħu jqabbdu l-membri',
    manageDone: 'Lest',
    manageHint:
      'Ixgħel jew itfi l-apps għall-membri. Il-kontijiet li diġà huma mqabbda jibqgħu mqabbda.',
    allowFor: 'Ħalli konnessjonijiet ġodda ma’ {name} għal {company}',
    tileConnect: 'Qabbad',
    tileConnecting: 'Qed jikkonnettja…',
    tileConnected: 'Konness',
    tileSignIn: 'Erġa’ idħol',
    tileOff: 'Mitfi',
    tileUnavailable: 'Mhux disponibbli',
    tileNotAllowed: 'Mhux permess',
    tileNotAllowedNote: 'L-amministratur tiegħek tefa l-konnessjonijiet ġodda',
    tileAccountOnly: 'Konnessjoni tal-kont biss · għad m’hemmx għodod taċ-chat',
    tileApiKey: 'Meħtieġa ċavetta API',
    tileOwnApp: 'Juża l-app {name} tiegħek stess',
    tileAdminSetup: 'Konfigurazzjoni mis-sid jew mill-amministratur',
    otherWays: 'Modi oħra kif tqabbad',
    serverTitle: 'Is-server tiegħek',
    serverBody: 'Żid għodod minn server MCP li jħaddem it-tim tiegħek.',
    serverAction: 'Żid server',
    databaseTitle: 'Database',
    databaseBody: 'PostgreSQL, limitat għall-iskemi jew it-tabelli li tagħżel.',
    databaseAction: 'Qabbad',
    assistantsTitle: 'Assistenti oħra tal-IA',
    assistantsBody:
      'Ħalli lil Claude, ChatGPT jew aġent tal-ikkowdjar jaqraw l-għarfien approvat tal-kumpanija.',
    assistantsAction: 'Issettja',
    assistantsSheetIntro:
      'Assistenti barra minn Poneva li jaqraw l-għarfien approvat tal-kumpanija, u l-aġenti tal-ikkowdjar li jaħdmu għalik.',
    footnote:
      'Int tapprova l-bidliet qabel ma jitwettqu. L-integrazzjonijiet huma pprovduti minn Pipedream; l-għodod li toffri kull app ivarjaw.',
    askBody:
      'Mintix ċert liema għandek bżonn? Poneva jista’ jissuġġerixxi konnessjonijiet skont ix-xogħol li tiddeskrivi.',
    askAction: 'Staqsi fiċ-chat',
    askPrompt:
      'Liema sorsi tal-kumpanija jkunu l-aktar utli biex nqabbdu għax-xogħol tagħna, skont dak li taf dwarna?',
    compactTitle: 'Ġib l-għodod tiegħek miegħek.',
    forYou: 'Għalik',
    browseAll: 'Ara l-għodod kollha',
    selectedForFocus: 'Magħżula skont il-fokus tiegħek',
    popupBlocked: 'Il-browser imblokka t-tieqa tad-dħul.',
    popupOpen: 'Iftaħ il-paġna tad-dħul',
    popupKeepChecking: 'Din il-paġna tibqa’ tiċċekkja.',
    noticeAdded: 'Il-konnessjoni ġiet miżjuda.',
    noticeAddedReturn:
      'Il-konnessjoni ġiet miżjuda. Tista’ tagħlaq din it-tab u terġa’ lura għax-xogħol tiegħek.',
    noticeSignInUnfinished:
      'Id-dħul ma tlestiex. Uża Erġa’ idħol fuq is-server biex terġa’ tipprova.',
    noticeChecked: 'Il-konnessjoni ġiet iċċekkjata b’suċċess.',
    noticeUnreachable: 'Is-server għadu ma setax jintlaħaq.',
    noticeSignInReady: 'Paġna tad-dħul hija lesta f’tab ġdida.',
    noticeAuthorized: 'L-awtorizzazzjoni tlestiet.',
    noticeDatabaseRemoved: 'PostgreSQL ġie skonnettjat.',
    noticeStoreConnected: 'Il-ħanut Shopify ġie konness.',
    noticeStoreChooseAccess:
      'Il-ħanut Shopify ġie konness. Iftħu fil-konnessjonijiet tiegħek biex tagħżel min jista’ jużah.',
    allowFailed: 'Ma setax jiġi aġġornat {name}. Erġa’ pprova.',
    accessUnknown: 'Id-dettalji tal-aċċess mhumiex disponibbli.',
    metaStep: 'Pass {step} minn {total}',
    stepsLabel: 'Passi tal-konfigurazzjoni',
    stepChoose: 'Agħżel',
    stepPrepare: 'Ipprepara',
    stepAccess: 'Aċċess',
    stepSignIn: 'Idħol',
    metaChooseTitle: 'Qabbad Meta Ads',
    metaChooseIntro:
      'Daħħal il-kontijiet tar-reklami ta’ Meta tiegħek f’Poneva. L-ewwel, agħżel x’jista’ jagħmel Poneva bihom.',
    metaReporting: 'Rapporti',
    metaReportingTag: 'Qari biss',
    metaReportingBody:
      'Jaqra l-kontijiet tar-reklami, il-kampanji, is-settijiet tar-reklami, ir-reklami u r-riżultati, kif ukoll il-Paġni ta’ Facebook tiegħek. Poneva qatt ma jibdel xejn f’Meta.',
    metaManage: 'Rapporti u ġestjoni tar-reklami',
    metaManageTag: 'Qari u kitba',
    metaManageBody:
      'Joħloq u jaġġorna wkoll kampanji, reklami u katalgi bl-għodod ta’ Meta stess. Int tagħżel jekk Poneva jistaqsix qabel kull bidla.',
    metaOwnApp:
      'Meta titlob li kull negozju jikkonnettja permezz tal-app Meta tiegħu stess. Il-pass li jmiss jgħinek tissettjaha, darba biss.',
    metaConnectedAlready: 'Konness',
    prepareTitle: 'Ipprepara l-app Meta tiegħek',
    prepareIntro:
      'Agħmel dan darba f’Meta for Developers. Għandek bżonn aċċess ta’ amministratur għall-portafoll tan-negozju tal-kumpanija tiegħek f’Meta.',
    prepareAppTitle: 'Oħloq jew iftaħ l-app Meta tiegħek',
    prepareAppReporting: 'Ixgħel Marketing API u Facebook Login for Business.',
    prepareAppManage: 'Żid il-każ tal-użu “Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Iftaħ Meta for Developers',
    prepareRedirectTitle: 'Żid l-indirizz tad-dħul ta’ Poneva',
    prepareRedirectBody:
      'F’Facebook Login for Business, waħħlu taħt Valid OAuth Redirect URIs. Żomm l-indirizzi li diġà hemm, imbagħad issejvja.',
    prepareSecretTitle: 'Żomm lesti l-App ID u l-App Secret tiegħek',
    prepareSecretBody:
      'It-tnejn jinsabu fl-app tiegħek taħt App settings → Basic. Se twaħħalhom f’tieqa sigura fl-aħħar pass, qatt f’Poneva.',
    prepareDone: 'Lest',
    copy: 'Ikkopja',
    copied: 'Ikkupjat',
    copyAddress: 'Ikkopja l-indirizz tad-dħul',
    copyManual: 'Agħżel l-indirizz u kkopjah bit-tastiera.',
    permissionsTitle: 'X’se titolbok tapprova Meta',
    permissionsNote:
      'Meta tista’ titlob li tivverifika n-negozju tiegħek qabel ma l-app tkun tista’ tilħaq kontijiet tar-reklami lil hinn minn tiegħek.',
    scopeAdsRead: 'Aqra r-reklami tiegħek u r-riżultati tagħhom',
    scopeAdsManagement: 'Oħloq u ibdel ir-reklami',
    scopeAdsMcp: 'Uża l-għodod tar-reklami ta’ Meta',
    scopeBusiness: 'Ara l-portafolli tan-negozju tiegħek',
    scopePages: 'Elenka l-Paġni li tamministra',
    scopePageEngagement: 'Aqra d-dettalji u l-interazzjoni tal-Paġni',
    scopeInstagram: 'Aqra d-dettalji bażiċi tal-kont ta’ Instagram',
    scopeCatalog: 'Immaniġġja l-katalgi tal-prodotti',
    guide: 'Gwida pass pass',
    metaAccessTitle: 'Min jista’ juża Meta Ads?',
    metaAccessIntro:
      'Il-kontijiet tar-reklami ġeneralment jappartjenu lill-kumpanija kollha. Tista’ tibdel dan meta trid.',
    metaReadOnlyTitle: 'Qari biss',
    metaReadOnlyBody:
      'Ir-rapporti qatt ma jibdlu xejn f’Meta. Biex tħalli lil Poneva jimmaniġġja r-reklami, agħżel il-ġestjoni tar-reklami fl-ewwel pass.',
    signInTitle: 'Idħol b’Meta',
    signInIntro: 'Tinfetaħ tieqa sigura. Lesti hemmhekk, u din il-paġna taġġorna waħedha.',
    signInWindow: 'Fit-tieqa sigura',
    signInPaste: 'Waħħal l-App ID u l-App Secret tiegħek',
    signInChoose: 'Agħżel in-negozju tiegħek u l-kontijiet tar-reklami tiegħu',
    signInApprove: 'Approva l-aċċess f’Meta',
    signInWaiting: 'Qed nistennew lil Meta…',
    signInSecret:
      'L-App Secret tiegħek imur dritt għand Nango, is-servizz tad-dħul li juża Poneva, u jinħażen hemmhekk kriptat. Poneva qatt ma jarah.',
    stopWaiting: 'Tibqax tistenna',
    doneTitle: '{name} huwa konness',
    doneAccountsYou: 'Issa tista’ tistaqsi lil Poneva dwar {count}.',
    doneAccountsCompany: 'Issa kulħadd f’{company} jista’ jistaqsi lil Poneva dwar {count}.',
    doneTools: 'Meta pprovdiet {count} għal dan il-kont.',
    doneTry: 'Ipprova staqsi',
    doneAsk: 'Staqsi dwar ir-reklami tiegħi',
    doneDone: 'Lest',
    doneTryReport: 'Kif marru l-kampanji tal-ġimgħa li għaddiet?',
    doneTryReturn: 'Liema reklami taw l-aħjar redditu dan ix-xahar?',
    continue: 'Kompli',
    continueToMeta: 'Kompli lejn Meta',
    back: 'Lura',
    cancel: 'Ikkanċella',
    askHelp: 'Itlob l-għajnuna lil Poneva',
    askHelpPrompt:
      'Għinni nissettja l-app Meta tagħna stess biex inkun nista’ nqabbad Meta Ads ma’ Poneva.',
    chatTitle: 'Qabbad biex tkompli',
    chatIntro: 'Dan iċ-chat jibqa’ fejn hu.',
    chatConnectedYou: 'Konness għalik',
    chatConnectedCompany: 'Konness għal {company}',
    chatUnavailable: 'Dħalt · għodod mhux disponibbli',
    chatFoot: 'Idħol mal-fornitur. Meta jidher Konness, staqsi lil Poneva biex ikompli.',
    chatPopupBlocked: 'Il-browser tiegħek imblokka t-tieqa tad-dħul.',
    chatOpenAuthorization: 'Iftaħ id-dħul',
    chatFallbackBody: 'Uża dan is-servizz fix-xogħol tiegħek.',
  },
  nl: {
    eyebrow: 'Werkruimte van {company}',
    title: 'Verbindingen',
    intro:
      'Verbind je tools om informatie te vinden en dingen gedaan te krijgen. Kies wie elke verbinding kan gebruiken en of Poneva kan lezen of ook wijzigingen kan aanbrengen.',
    activity: 'Activiteit',
    backToConnections: 'Terug naar Verbindingen',
    yoursTitle: 'Je verbindingen',
    filterLabel: 'Verbindingen tonen',
    filterAll: 'Alle',
    filterMine: 'Alleen ik',
    filterShared: 'Gedeeld',
    filterAttention: 'Aandacht nodig',
    columnConnection: 'Verbinding',
    columnWho: 'Wie het kan gebruiken',
    columnWhat: 'Poneva kan',
    columnStatus: 'Status',
    yoursLoading: 'Je verbindingen laden…',
    yoursEmpty: 'Er is nog niets verbonden. Zoek hieronder om je eerste tool te verbinden.',
    filterEmpty: 'Hier is nu niets.',
    yoursLoadError: 'Je verbindingen konden niet worden geladen.',
    serversLoadError: 'Je eigen servers konden niet worden geladen.',
    databasesLoadError: 'Je databases konden niet worden geladen.',
    retry: 'Opnieuw proberen',
    stateConnected: 'Verbonden',
    stateAttention: 'Inloggen nodig',
    stateReview: 'Toegang controleren',
    stateUnavailable: 'Niet beschikbaar',
    stateOff: 'Uitgeschakeld',
    stateConnecting: 'Verbinden…',
    signInAgain: 'Opnieuw inloggen',
    attentionBody:
      'De aanmelding voor {name} is verlopen of ingetrokken. Log opnieuw in om hem te blijven gebruiken.',
    reviewBody:
      'Het account achter {name} is gewijzigd. Controleer wie hem kan gebruiken en wat Poneva kan doen voordat hij weer wordt gebruikt.',
    unavailableBody: 'Je bent ingelogd bij {name}, maar de tools zijn nu niet beschikbaar.',
    offBody:
      '{name} is uitgeschakeld. Niemand kan hem gebruiken tot hij weer wordt ingeschakeld. Er wordt niets verwijderd.',
    kindServer: 'Eigen server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabel|other:tabellen',
    adAccountsNoun: 'one:advertentieaccount|other:advertentieaccounts',
    toolsNoun: 'one:tool|other:tools',
    close: 'Sluiten',
    moreActions: 'Meer acties voor {name}',
    tabOverview: 'Overzicht',
    tabAccess: 'Toegang',
    tabActivity: 'Activiteit',
    connectedOn: 'Sinds {date}',
    lastUsed: 'Laatst gebruikt {when}',
    whatTitle: 'Wat Poneva kan doen',
    readsLine: 'Leest wanneer je erom vraagt',
    changesAsk: 'Vraagt het je voordat er iets verandert',
    changesAllowed:
      'Brengt wijzigingen aan zonder te vragen. Elke wijziging wordt vastgelegd in Activiteit.',
    changesNever: 'Wijzigt nooit iets',
    readinessReady: '{count} klaar',
    change: 'Wijzigen',
    tryTitle: 'Probeer het in een chat',
    tryPrompt: 'Waarmee kun je me helpen via {name}?',
    addAccount: 'Nog een account toevoegen',
    disconnect: 'Verbinding verbreken',
    disconnectConfirm:
      'Verbinding met {name} verbreken? Hij werkt dan niet meer in chats, apps en agenten tot hij opnieuw wordt verbonden.',
    disconnectKeep: 'Behouden',
    nativeOnlyYou: 'Alleen jij kunt dit account gebruiken.',
    nativeEveryone: 'Iedereen bij {company} kan dit account gebruiken.',
    providerPermissions: 'De eigen accountrechten van de aanbieder blijven gelden.',
    manageDatabase: 'Database beheren',
    activityLoading: 'Activiteit laden…',
    activityLoadError: 'De activiteit kon niet worden geladen.',
    activityRead: 'Lezen',
    activityChange: 'Wijziging',
    activitySetting: 'Instelling',
    activityDenied: 'Geweigerd',
    activityFailed: 'Mislukt',
    activityUnknown: 'Onbekend resultaat',
    activityAll: 'Alle activiteit openen',
    activityToday: 'Vandaag',
    activityYesterday: 'Gisteren',
    eventCreated: 'Verbinding gemaakt',
    eventShared: 'Delen gewijzigd',
    eventPersonAdded: 'Persoon toegevoegd',
    eventPersonRemoved: 'Persoon verwijderd',
    eventAccessChanged: 'Toegang gewijzigd',
    eventRevoked: 'Toegang uitgeschakeld',
    eventRemoved: 'Verbinding verwijderd',
    eventSettings: 'Tijdslimiet voor query’s gewijzigd',
    addTitle: 'Een verbinding toevoegen',
    integrations: 'one:integratie|other:integraties',
    searchLabel: 'Integraties zoeken',
    searchPlaceholder: 'Integraties zoeken',
    clearSearch: 'Zoekopdracht wissen',
    inYours: 'In je verbindingen',
    addNew: 'Nieuw toevoegen',
    resultsFor: '{count} voor “{query}”',
    resultsNoun: 'one:resultaat|other:resultaten',
    popularFirst: 'Populairste eerst',
    searching: 'Zoeken…',
    searchUnavailable: 'Zoeken naar verbindingen is tijdelijk niet beschikbaar.',
    noResults: 'Nog geen resultaten. Probeer een andere naam, of vraag Poneva welke tool past.',
    showMore: 'Meer tonen',
    showMoreRetry: 'Opnieuw proberen meer te laden',
    loadingMore: 'Laden…',
    moreFailed: 'Meer integraties konden niet worden geladen. Je resultaten blijven staan.',
    manageMembers: 'Kiezen wat leden kunnen verbinden',
    manageDone: 'Gereed',
    manageHint:
      'Zet apps aan of uit voor leden. Accounts die al zijn verbonden, blijven verbonden.',
    allowFor: 'Nieuwe verbindingen met {name} toestaan voor {company}',
    tileConnect: 'Verbinden',
    tileConnecting: 'Verbinden…',
    tileConnected: 'Verbonden',
    tileSignIn: 'Opnieuw inloggen',
    tileOff: 'Uitgeschakeld',
    tileUnavailable: 'Niet beschikbaar',
    tileNotAllowed: 'Niet toegestaan',
    tileNotAllowedNote: 'Je beheerder heeft nieuwe verbindingen uitgeschakeld',
    tileAccountOnly: 'Alleen accountverbinding · nog geen chattools',
    tileApiKey: 'API-sleutel vereist',
    tileOwnApp: 'Gebruikt je eigen {name}-app',
    tileAdminSetup: 'Instellen door eigenaar of beheerder',
    otherWays: 'Andere manieren om te verbinden',
    serverTitle: 'Eigen server',
    serverBody: 'Voeg tools toe van een MCP-server die je team beheert.',
    serverAction: 'Server toevoegen',
    databaseTitle: 'Een database',
    databaseBody: 'PostgreSQL, beperkt tot de schema’s of tabellen die je kiest.',
    databaseAction: 'Verbinden',
    assistantsTitle: 'Andere AI-assistenten',
    assistantsBody: 'Laat Claude, ChatGPT of een codeeragent goedgekeurde bedrijfskennis lezen.',
    assistantsAction: 'Instellen',
    assistantsSheetIntro:
      'Assistenten buiten Poneva die goedgekeurde bedrijfskennis lezen, en de codeeragenten die voor je werken.',
    footnote:
      'Je keurt wijzigingen goed voordat ze worden uitgevoerd. Integraties via Pipedream; welke tools elke app biedt, verschilt.',
    askBody:
      'Weet je niet welke je nodig hebt? Poneva kan verbindingen voorstellen op basis van het werk dat je beschrijft.',
    askAction: 'Vragen in de chat',
    askPrompt:
      'Welke bedrijfsbronnen zouden het nuttigst zijn om te verbinden voor ons werk, op basis van wat je over ons weet?',
    compactTitle: 'Neem je tools mee.',
    forYou: 'Voor jou',
    browseAll: 'Alle tools bekijken',
    selectedForFocus: 'Gekozen op basis van je focus',
    popupBlocked: 'De browser heeft het aanmeldvenster geblokkeerd.',
    popupOpen: 'Aanmeldpagina openen',
    popupKeepChecking: 'Deze pagina blijft controleren.',
    noticeAdded: 'Verbinding toegevoegd.',
    noticeAddedReturn:
      'Verbinding toegevoegd. Je kunt dit tabblad sluiten en teruggaan naar je werk.',
    noticeSignInUnfinished:
      'Het inloggen is niet voltooid. Gebruik Opnieuw inloggen bij de server om het nog eens te proberen.',
    noticeChecked: 'De verbinding is met succes gecontroleerd.',
    noticeUnreachable: 'De server is nog niet bereikbaar.',
    noticeSignInReady: 'Er staat een aanmeldpagina klaar in een nieuw tabblad.',
    noticeAuthorized: 'Autorisatie voltooid.',
    noticeDatabaseRemoved: 'De verbinding met PostgreSQL is verbroken.',
    noticeStoreConnected: 'Shopify-winkel verbonden.',
    noticeStoreChooseAccess:
      'Shopify-winkel verbonden. Open hem in Je verbindingen om te kiezen wie hem kan gebruiken.',
    allowFailed: '{name} kon niet worden bijgewerkt. Probeer het opnieuw.',
    accessUnknown: 'Toegangsgegevens zijn niet beschikbaar.',
    metaStep: 'Stap {step} van {total}',
    stepsLabel: 'Instellingsstappen',
    stepChoose: 'Kiezen',
    stepPrepare: 'Voorbereiden',
    stepAccess: 'Toegang',
    stepSignIn: 'Inloggen',
    metaChooseTitle: 'Meta Ads verbinden',
    metaChooseIntro:
      'Breng je advertentieaccounts van Meta naar Poneva. Kies eerst wat Poneva ermee mag doen.',
    metaReporting: 'Rapportage',
    metaReportingTag: 'Alleen lezen',
    metaReportingBody:
      'Leest advertentieaccounts, campagnes, advertentiesets, advertenties en resultaten, plus je Facebook-pagina’s. Poneva wijzigt nooit iets in Meta.',
    metaManage: 'Rapportage en advertentiebeheer',
    metaManageTag: 'Lezen en schrijven',
    metaManageBody:
      'Maakt en werkt ook campagnes, advertenties en catalogi bij met de eigen tools van Meta. Jij kiest of Poneva vóór elke wijziging vraagt.',
    metaOwnApp:
      'Meta vraagt elk bedrijf om via een eigen Meta-app te verbinden. De volgende stap helpt je die eenmalig in te stellen.',
    metaConnectedAlready: 'Verbonden',
    prepareTitle: 'Je Meta-app voorbereiden',
    prepareIntro:
      'Doe dit eenmalig in Meta for Developers. Je hebt beheerderstoegang nodig tot het Meta-bedrijfsaccount van je bedrijf.',
    prepareAppTitle: 'Maak of open je Meta-app',
    prepareAppReporting: 'Schakel de Marketing API en Facebook Login for Business in.',
    prepareAppManage: 'Voeg het gebruiksscenario “Create and manage ads with the ads MCP” toe.',
    prepareAppOpen: 'Meta for Developers openen',
    prepareRedirectTitle: 'Voeg het aanmeldadres van Poneva toe',
    prepareRedirectBody:
      'Plak het in Facebook Login for Business onder Valid OAuth Redirect URIs. Behoud de adressen die er al staan en sla daarna op.',
    prepareSecretTitle: 'Houd je App ID en App Secret bij de hand',
    prepareSecretBody:
      'Beide staan in je app onder App settings → Basic. Je plakt ze in de laatste stap in een beveiligd venster, nooit in Poneva.',
    prepareDone: 'Klaar',
    copy: 'Kopiëren',
    copied: 'Gekopieerd',
    copyAddress: 'Aanmeldadres kopiëren',
    copyManual: 'Selecteer het adres en kopieer het met je toetsenbord.',
    permissionsTitle: 'Waar Meta je om toestemming vraagt',
    permissionsNote:
      'Meta kan vragen je bedrijf te verifiëren voordat de app advertentieaccounts buiten je eigen accounts kan bereiken.',
    scopeAdsRead: 'Je advertenties en hun resultaten lezen',
    scopeAdsManagement: 'Advertenties maken en wijzigen',
    scopeAdsMcp: 'De advertentietools van Meta gebruiken',
    scopeBusiness: 'Je bedrijfsportfolio’s bekijken',
    scopePages: 'De pagina’s die je beheert weergeven',
    scopePageEngagement: 'Paginagegevens en betrokkenheid lezen',
    scopeInstagram: 'Basisgegevens van het Instagram-account lezen',
    scopeCatalog: 'Productcatalogi beheren',
    guide: 'Stapsgewijze handleiding',
    metaAccessTitle: 'Wie kan Meta Ads gebruiken?',
    metaAccessIntro:
      'Advertentieaccounts zijn meestal van het hele bedrijf. Je kunt dit altijd wijzigen.',
    metaReadOnlyTitle: 'Alleen lezen',
    metaReadOnlyBody:
      'Rapportage wijzigt nooit iets in Meta. Wil je dat Poneva advertenties beheert, kies dan advertentiebeheer in de eerste stap.',
    signInTitle: 'Inloggen met Meta',
    signInIntro:
      'Er opent een beveiligd venster. Rond het daar af, dan wordt deze pagina vanzelf bijgewerkt.',
    signInWindow: 'In het beveiligde venster',
    signInPaste: 'Plak je App ID en App Secret',
    signInChoose: 'Kies je bedrijf en de advertentieaccounts ervan',
    signInApprove: 'Keur de toegang goed in Meta',
    signInWaiting: 'Wachten op Meta…',
    signInSecret:
      'Je App Secret gaat rechtstreeks naar Nango, de aanmelddienst die Poneva gebruikt, en wordt daar versleuteld opgeslagen. Poneva ziet hem nooit.',
    stopWaiting: 'Stoppen met wachten',
    doneTitle: '{name} is verbonden',
    doneAccountsYou: 'Je kunt Poneva nu vragen stellen over {count}.',
    doneAccountsCompany: 'Iedereen bij {company} kan Poneva nu vragen stellen over {count}.',
    doneTools: 'Meta heeft {count} beschikbaar gesteld voor dit account.',
    doneTry: 'Probeer eens te vragen',
    doneAsk: 'Vragen over mijn advertenties',
    doneDone: 'Klaar',
    doneTryReport: 'Hoe presteerden de campagnes van vorige week?',
    doneTryReturn: 'Welke advertenties leverden deze maand het beste rendement op?',
    continue: 'Doorgaan',
    continueToMeta: 'Doorgaan naar Meta',
    back: 'Terug',
    cancel: 'Annuleren',
    askHelp: 'Poneva om hulp vragen',
    askHelpPrompt:
      'Help me onze eigen Meta-app in te stellen, zodat ik Meta Ads met Poneva kan verbinden.',
    chatTitle: 'Verbind om door te gaan',
    chatIntro: 'Deze chat blijft gewoon staan.',
    chatConnectedYou: 'Verbonden voor jou',
    chatConnectedCompany: 'Verbonden voor {company}',
    chatUnavailable: 'Ingelogd · tools niet beschikbaar',
    chatFoot: 'Log in bij de aanbieder. Staat er Verbonden, vraag Poneva dan om verder te gaan.',
    chatPopupBlocked: 'Je browser heeft het aanmeldvenster geblokkeerd.',
    chatOpenAuthorization: 'Aanmelding openen',
    chatFallbackBody: 'Gebruik deze dienst in je werk.',
  },
  pl: {
    eyebrow: 'Obszar roboczy {company}',
    title: 'Połączenia',
    intro:
      'Połącz swoje narzędzia, aby znajdować informacje i załatwiać sprawy. Wybierz, kto może korzystać z każdego połączenia i czy Poneva może tylko odczytywać dane, czy też wprowadzać zmiany.',
    activity: 'Aktywność',
    backToConnections: 'Wróć do Połączeń',
    yoursTitle: 'Twoje połączenia',
    filterLabel: 'Pokaż połączenia',
    filterAll: 'Wszystkie',
    filterMine: 'Tylko ja',
    filterShared: 'Udostępnione',
    filterAttention: 'Wymagają uwagi',
    columnConnection: 'Połączenie',
    columnWho: 'Kto może go używać',
    columnWhat: 'Co może robić Poneva',
    columnStatus: 'Stan',
    yoursLoading: 'Wczytywanie twoich połączeń…',
    yoursEmpty:
      'Nic jeszcze nie jest połączone. Wyszukaj poniżej, aby połączyć pierwsze narzędzie.',
    filterEmpty: 'Na razie nic tu nie ma.',
    yoursLoadError: 'Nie udało się wczytać twoich połączeń.',
    serversLoadError: 'Nie udało się wczytać twoich własnych serwerów.',
    databasesLoadError: 'Nie udało się wczytać twoich baz danych.',
    retry: 'Spróbuj ponownie',
    stateConnected: 'Połączono',
    stateAttention: 'Wymaga zalogowania',
    stateReview: 'Sprawdź dostęp',
    stateUnavailable: 'Niedostępne',
    stateOff: 'Wyłączone',
    stateConnecting: 'Łączenie…',
    signInAgain: 'Zaloguj się ponownie',
    attentionBody:
      'Logowanie do usługi {name} wygasło lub zostało cofnięte. Zaloguj się ponownie, aby dalej z niej korzystać.',
    reviewBody:
      'Konto powiązane z usługą {name} się zmieniło. Zanim zostanie ponownie użyta, sprawdź, kto może z niej korzystać i co może robić Poneva.',
    unavailableBody:
      'Logowanie do usługi {name} jest aktywne, ale jej narzędzia są teraz niedostępne.',
    offBody:
      'Usługa {name} jest wyłączona. Nikt nie może z niej korzystać, dopóki nie zostanie ponownie włączona. Nic nie zostanie usunięte.',
    kindServer: 'Własny serwer',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabela|few:tabele|many:tabel|other:tabeli',
    adAccountsNoun:
      'one:konto reklamowe|few:konta reklamowe|many:kont reklamowych|other:konta reklamowego',
    toolsNoun: 'one:narzędzie|few:narzędzia|many:narzędzi|other:narzędzia',
    close: 'Zamknij',
    moreActions: 'Więcej działań: {name}',
    tabOverview: 'Przegląd',
    tabAccess: 'Dostęp',
    tabActivity: 'Aktywność',
    connectedOn: 'Od {date}',
    lastUsed: 'Ostatnio użyto {when}',
    whatTitle: 'Co może robić Poneva',
    readsLine: 'Odczytuje, gdy o to poprosisz',
    changesAsk: 'Pyta cię, zanim cokolwiek zmieni',
    changesAllowed:
      'Wprowadza zmiany bez pytania. Każda zmiana jest zapisywana w sekcji Aktywność.',
    changesNever: 'Nigdy niczego nie zmienia',
    readinessReady: 'Gotowe: {count}',
    change: 'Zmień',
    tryTitle: 'Wypróbuj na czacie',
    tryPrompt: 'W czym możesz mi pomóc za pomocą usługi {name}?',
    addAccount: 'Dodaj kolejne konto',
    disconnect: 'Rozłącz',
    disconnectConfirm:
      'Rozłączyć usługę {name}? Przestanie działać w czatach, aplikacjach i agentach, dopóki nie zostanie ponownie połączona.',
    disconnectKeep: 'Zachowaj',
    nativeOnlyYou: 'Tylko ty możesz używać tego konta.',
    nativeEveryone: 'Wszyscy w firmie {company} mogą używać tego konta.',
    providerPermissions: 'Nadal obowiązują uprawnienia konta u dostawcy.',
    manageDatabase: 'Zarządzaj bazą danych',
    activityLoading: 'Wczytywanie aktywności…',
    activityLoadError: 'Nie udało się wczytać aktywności.',
    activityRead: 'Odczyt',
    activityChange: 'Zmiana',
    activitySetting: 'Ustawienie',
    activityDenied: 'Odmowa',
    activityFailed: 'Niepowodzenie',
    activityUnknown: 'Nieznany wynik',
    activityAll: 'Otwórz całą aktywność',
    activityToday: 'Dzisiaj',
    activityYesterday: 'Wczoraj',
    eventCreated: 'Utworzono połączenie',
    eventShared: 'Zmieniono udostępnianie',
    eventPersonAdded: 'Dodano osobę',
    eventPersonRemoved: 'Usunięto osobę',
    eventAccessChanged: 'Zmieniono dostęp',
    eventRevoked: 'Wyłączono dostęp',
    eventRemoved: 'Usunięto połączenie',
    eventSettings: 'Zmieniono limit czasu zapytań',
    addTitle: 'Dodaj połączenie',
    integrations: 'one:integracja|few:integracje|many:integracji|other:integracji',
    searchLabel: 'Szukaj integracji',
    searchPlaceholder: 'Szukaj integracji',
    clearSearch: 'Wyczyść wyszukiwanie',
    inYours: 'W twoich połączeniach',
    addNew: 'Dodaj nowe',
    resultsFor: '{count} dla „{query}”',
    resultsNoun: 'one:wynik|few:wyniki|many:wyników|other:wyniku',
    popularFirst: 'Najpierw popularne',
    searching: 'Wyszukiwanie…',
    searchUnavailable: 'Wyszukiwanie połączeń jest tymczasowo niedostępne.',
    noResults:
      'Nic jeszcze nie pasuje. Spróbuj innej nazwy albo zapytaj Ponevę, które narzędzie pasuje.',
    showMore: 'Pokaż więcej',
    showMoreRetry: 'Spróbuj ponownie wczytać więcej',
    loadingMore: 'Wczytywanie…',
    moreFailed: 'Nie udało się wczytać kolejnych integracji. Twoje wyniki nadal tu są.',
    manageMembers: 'Wybierz, co członkowie mogą łączyć',
    manageDone: 'Gotowe',
    manageHint:
      'Włączaj lub wyłączaj aplikacje dla członków. Konta, które są już połączone, pozostają połączone.',
    allowFor: 'Zezwól na nowe połączenia z usługą {name} w firmie {company}',
    tileConnect: 'Połącz',
    tileConnecting: 'Łączenie…',
    tileConnected: 'Połączono',
    tileSignIn: 'Zaloguj się ponownie',
    tileOff: 'Wyłączone',
    tileUnavailable: 'Niedostępne',
    tileNotAllowed: 'Niedozwolone',
    tileNotAllowedNote: 'Administrator wyłączył nowe połączenia',
    tileAccountOnly: 'Tylko połączenie konta · na razie bez narzędzi czatu',
    tileApiKey: 'Wymagany klucz API',
    tileOwnApp: 'Używa twojej własnej aplikacji {name}',
    tileAdminSetup: 'Konfiguruje właściciel lub administrator',
    otherWays: 'Inne sposoby łączenia',
    serverTitle: 'Własny serwer',
    serverBody: 'Dodaj narzędzia z serwera MCP, który utrzymuje twój zespół.',
    serverAction: 'Dodaj serwer',
    databaseTitle: 'Baza danych',
    databaseBody: 'PostgreSQL, ograniczona do wybranych przez ciebie schematów lub tabel.',
    databaseAction: 'Połącz',
    assistantsTitle: 'Inni asystenci AI',
    assistantsBody:
      'Pozwól, aby Claude, ChatGPT lub agent do programowania czytali zatwierdzoną wiedzę firmy.',
    assistantsAction: 'Skonfiguruj',
    assistantsSheetIntro:
      'Asystenci spoza Ponevy, którzy czytają zatwierdzoną wiedzę firmy, oraz agenci do programowania, którzy pracują dla ciebie.',
    footnote:
      'Zatwierdzasz zmiany, zanim zostaną wykonane. Integracje zapewnia Pipedream; narzędzia oferowane przez poszczególne aplikacje się różnią.',
    askBody:
      'Nie wiesz, którego potrzebujesz? Poneva może zaproponować połączenia na podstawie opisanej przez ciebie pracy.',
    askAction: 'Zapytaj na czacie',
    askPrompt:
      'Które źródła firmowe najlepiej byłoby połączyć na potrzeby naszej pracy, biorąc pod uwagę to, co o nas wiesz?',
    compactTitle: 'Zabierz swoje narzędzia.',
    forYou: 'Dla ciebie',
    browseAll: 'Przeglądaj wszystkie narzędzia',
    selectedForFocus: 'Wybrane pod kątem twoich priorytetów',
    popupBlocked: 'Przeglądarka zablokowała okno logowania.',
    popupOpen: 'Otwórz stronę logowania',
    popupKeepChecking: 'Ta strona sprawdza dalej.',
    noticeAdded: 'Dodano połączenie.',
    noticeAddedReturn: 'Dodano połączenie. Możesz zamknąć tę kartę i wrócić do pracy.',
    noticeSignInUnfinished:
      'Logowanie nie zostało dokończone. Aby spróbować ponownie, użyj przy serwerze opcji Zaloguj się ponownie.',
    noticeChecked: 'Połączenie zostało pomyślnie sprawdzone.',
    noticeUnreachable: 'Nie udało się jeszcze połączyć z serwerem.',
    noticeSignInReady: 'Strona logowania jest gotowa w nowej karcie.',
    noticeAuthorized: 'Autoryzacja zakończona.',
    noticeDatabaseRemoved: 'Rozłączono PostgreSQL.',
    noticeStoreConnected: 'Połączono sklep Shopify.',
    noticeStoreChooseAccess:
      'Połączono sklep Shopify. Otwórz go w sekcji Twoje połączenia, aby wybrać, kto może z niego korzystać.',
    allowFailed: 'Nie udało się zaktualizować aplikacji {name}. Spróbuj ponownie.',
    accessUnknown: 'Szczegóły dostępu są niedostępne.',
    metaStep: 'Krok {step} z {total}',
    stepsLabel: 'Kroki konfiguracji',
    stepChoose: 'Wybór',
    stepPrepare: 'Przygotowanie',
    stepAccess: 'Dostęp',
    stepSignIn: 'Logowanie',
    metaChooseTitle: 'Połącz Meta Ads',
    metaChooseIntro:
      'Przenieś swoje konta reklamowe Meta do Ponevy. Najpierw wybierz, co Poneva może z nimi robić.',
    metaReporting: 'Raporty',
    metaReportingTag: 'Tylko odczyt',
    metaReportingBody:
      'Odczytuje konta reklamowe, kampanie, zestawy reklam, reklamy i wyniki, a także twoje strony na Facebooku. Poneva nigdy niczego nie zmienia w Meta.',
    metaManage: 'Raporty i zarządzanie reklamami',
    metaManageTag: 'Odczyt i zapis',
    metaManageBody:
      'Tworzy też i aktualizuje kampanie, reklamy i katalogi za pomocą własnych narzędzi Meta. Ty decydujesz, czy Poneva pyta przed każdą zmianą.',
    metaOwnApp:
      'Meta wymaga, aby każda firma łączyła się przez własną aplikację Meta. Następny krok jednorazowo przeprowadzi cię przez jej konfigurację.',
    metaConnectedAlready: 'Połączono',
    prepareTitle: 'Przygotuj swoją aplikację Meta',
    prepareIntro:
      'Zrób to raz w Meta for Developers. Potrzebujesz dostępu administratora do portfolio biznesowego swojej firmy w Meta.',
    prepareAppTitle: 'Utwórz lub otwórz swoją aplikację Meta',
    prepareAppReporting: 'Włącz Marketing API i Facebook Login for Business.',
    prepareAppManage: 'Dodaj przypadek użycia „Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Otwórz Meta for Developers',
    prepareRedirectTitle: 'Dodaj adres logowania Ponevy',
    prepareRedirectBody:
      'W Facebook Login for Business wklej go w polu Valid OAuth Redirect URIs. Zachowaj adresy, które już tam są, a potem zapisz.',
    prepareSecretTitle: 'Przygotuj App ID i App Secret',
    prepareSecretBody:
      'Oba znajdziesz w swojej aplikacji w sekcji App settings → Basic. W ostatnim kroku wkleisz je w bezpiecznym oknie, nigdy w Ponevie.',
    prepareDone: 'Gotowe',
    copy: 'Kopiuj',
    copied: 'Skopiowano',
    copyAddress: 'Kopiuj adres logowania',
    copyManual: 'Zaznacz adres i skopiuj go za pomocą klawiatury.',
    permissionsTitle: 'O co poprosi cię Meta',
    permissionsNote:
      'Meta może poprosić o weryfikację twojej firmy, zanim aplikacja uzyska dostęp do kont reklamowych innych niż twoje.',
    scopeAdsRead: 'Odczytywanie twoich reklam i ich wyników',
    scopeAdsManagement: 'Tworzenie i zmienianie reklam',
    scopeAdsMcp: 'Korzystanie z narzędzi reklamowych Meta',
    scopeBusiness: 'Wyświetlanie twoich portfolio biznesowych',
    scopePages: 'Wyświetlanie listy zarządzanych przez ciebie stron',
    scopePageEngagement: 'Odczytywanie informacji o stronach i zaangażowania',
    scopeInstagram: 'Odczytywanie podstawowych informacji o koncie na Instagramie',
    scopeCatalog: 'Zarządzanie katalogami produktów',
    guide: 'Przewodnik krok po kroku',
    metaAccessTitle: 'Kto może korzystać z Meta Ads?',
    metaAccessIntro:
      'Konta reklamowe zwykle należą do całej firmy. Możesz to zmienić w dowolnym momencie.',
    metaReadOnlyTitle: 'Tylko odczyt',
    metaReadOnlyBody:
      'Raporty nigdy niczego nie zmieniają w Meta. Aby Poneva mogła zarządzać reklamami, w pierwszym kroku wybierz zarządzanie reklamami.',
    signInTitle: 'Zaloguj się przez Meta',
    signInIntro: 'Otworzy się bezpieczne okno. Dokończ tam, a ta strona sama się zaktualizuje.',
    signInWindow: 'W bezpiecznym oknie',
    signInPaste: 'Wklej App ID i App Secret',
    signInChoose: 'Wybierz swoją firmę i jej konta reklamowe',
    signInApprove: 'Zatwierdź dostęp w Meta',
    signInWaiting: 'Czekanie na Meta…',
    signInSecret:
      'Twój App Secret trafia bezpośrednio do Nango, usługi logowania, z której korzysta Poneva, i jest tam przechowywany w postaci zaszyfrowanej. Poneva nigdy go nie widzi.',
    stopWaiting: 'Przestań czekać',
    doneTitle: 'Usługa {name} jest połączona',
    doneAccountsYou: 'Możesz teraz pytać Ponevę o {count}.',
    doneAccountsCompany: 'Wszyscy w firmie {company} mogą teraz pytać Ponevę o {count}.',
    doneTools: 'Meta udostępniła {count} dla tego konta.',
    doneTry: 'Spróbuj zapytać',
    doneAsk: 'Zapytaj o moje reklamy',
    doneDone: 'Gotowe',
    doneTryReport: 'Jak wypadły kampanie z zeszłego tygodnia?',
    doneTryReturn: 'Które reklamy przyniosły w tym miesiącu najlepszy zwrot?',
    continue: 'Dalej',
    continueToMeta: 'Przejdź do Meta',
    back: 'Wstecz',
    cancel: 'Anuluj',
    askHelp: 'Poproś Ponevę o pomoc',
    askHelpPrompt:
      'Pomóż mi skonfigurować naszą własną aplikację Meta, żeby połączyć Meta Ads z Ponevą.',
    chatTitle: 'Połącz, aby kontynuować',
    chatIntro: 'Ten czat zostaje tam, gdzie jest.',
    chatConnectedYou: 'Połączono dla ciebie',
    chatConnectedCompany: 'Połączono dla firmy {company}',
    chatUnavailable: 'Zalogowano · narzędzia niedostępne',
    chatFoot: 'Zaloguj się u dostawcy. Gdy pojawi się Połączono, poproś Ponevę o kontynuację.',
    chatPopupBlocked: 'Twoja przeglądarka zablokowała okno logowania.',
    chatOpenAuthorization: 'Otwórz logowanie',
    chatFallbackBody: 'Korzystaj z tej usługi w swojej pracy.',
  },
  pt: {
    eyebrow: 'Espaço de trabalho de {company}',
    title: 'Ligações',
    intro:
      'Ligue as suas ferramentas para encontrar informação e fazer o trabalho avançar. Escolha quem pode usar cada ligação e se o Poneva pode ler ou também fazer alterações.',
    activity: 'Atividade',
    backToConnections: 'Voltar às Ligações',
    yoursTitle: 'As suas ligações',
    filterLabel: 'Mostrar ligações',
    filterAll: 'Todas',
    filterMine: 'Só eu',
    filterShared: 'Partilhadas',
    filterAttention: 'Requerem atenção',
    columnConnection: 'Ligação',
    columnWho: 'Quem a pode usar',
    columnWhat: 'O que o Poneva pode fazer',
    columnStatus: 'Estado',
    yoursLoading: 'A carregar as suas ligações…',
    yoursEmpty: 'Ainda não há nada ligado. Pesquise abaixo para ligar a sua primeira ferramenta.',
    filterEmpty: 'Não há nada aqui de momento.',
    yoursLoadError: 'Não foi possível carregar as suas ligações.',
    serversLoadError: 'Não foi possível carregar os seus servidores.',
    databasesLoadError: 'Não foi possível carregar as suas bases de dados.',
    retry: 'Tentar novamente',
    stateConnected: 'Ligada',
    stateAttention: 'Requer início de sessão',
    stateReview: 'Rever acesso',
    stateUnavailable: 'Indisponível',
    stateOff: 'Desativada',
    stateConnecting: 'A ligar…',
    signInAgain: 'Iniciar sessão novamente',
    attentionBody:
      'O início de sessão em {name} expirou ou foi revogado. Inicie sessão novamente para continuar a usá-la.',
    reviewBody:
      'A conta associada a {name} mudou. Reveja quem a pode usar e o que o Poneva pode fazer antes de voltar a ser usada.',
    unavailableBody:
      'Tem sessão iniciada em {name}, mas as respetivas ferramentas não estão disponíveis de momento.',
    offBody: '{name} está desativada. Ninguém a pode usar até ser reativada. Nada é eliminado.',
    kindServer: 'Servidor próprio',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabela|many:de tabelas|other:tabelas',
    adAccountsNoun: 'one:conta de anúncios|many:de contas de anúncios|other:contas de anúncios',
    toolsNoun: 'one:ferramenta|many:de ferramentas|other:ferramentas',
    close: 'Fechar',
    moreActions: 'Mais ações para {name}',
    tabOverview: 'Resumo',
    tabAccess: 'Acesso',
    tabActivity: 'Atividade',
    connectedOn: 'Desde {date}',
    lastUsed: 'Última utilização {when}',
    whatTitle: 'O que o Poneva pode fazer',
    readsLine: 'Lê quando pede',
    changesAsk: 'Pergunta-lhe antes de alterar o que quer que seja',
    changesAllowed: 'Faz alterações sem perguntar. Cada alteração fica registada em Atividade.',
    changesNever: 'Nunca altera nada',
    readinessReady: '{count} à disposição',
    change: 'Alterar',
    tryTitle: 'Experimentar num chat',
    tryPrompt: 'Em que me podes ajudar com {name}?',
    addAccount: 'Adicionar outra conta',
    disconnect: 'Desligar',
    disconnectConfirm:
      'Desligar {name}? Deixa de funcionar nos chats, Apps e agentes até voltar a ser ligada.',
    disconnectKeep: 'Manter',
    nativeOnlyYou: 'Esta conta é só sua.',
    nativeEveryone: 'Todos em {company} podem usar esta conta.',
    providerPermissions: 'As permissões da conta no fornecedor continuam a aplicar-se.',
    manageDatabase: 'Gerir base de dados',
    activityLoading: 'A carregar a atividade…',
    activityLoadError: 'Não foi possível carregar a atividade.',
    activityRead: 'Leitura',
    activityChange: 'Alteração',
    activitySetting: 'Definição',
    activityDenied: 'Recusado',
    activityFailed: 'Falhou',
    activityUnknown: 'Resultado desconhecido',
    activityAll: 'Abrir toda a atividade',
    activityToday: 'Hoje',
    activityYesterday: 'Ontem',
    eventCreated: 'Ligação criada',
    eventShared: 'Partilha alterada',
    eventPersonAdded: 'Pessoa adicionada',
    eventPersonRemoved: 'Pessoa removida',
    eventAccessChanged: 'Acesso alterado',
    eventRevoked: 'Acesso desativado',
    eventRemoved: 'Ligação removida',
    eventSettings: 'Limite de tempo das consultas alterado',
    addTitle: 'Adicionar uma ligação',
    integrations: 'one:integração|many:de integrações|other:integrações',
    searchLabel: 'Pesquisar integrações',
    searchPlaceholder: 'Pesquisar integrações',
    clearSearch: 'Limpar pesquisa',
    inYours: 'Nas suas ligações',
    addNew: 'Adicionar nova',
    resultsFor: '{count} para «{query}»',
    resultsNoun: 'one:resultado|many:de resultados|other:resultados',
    popularFirst: 'Mais populares primeiro',
    searching: 'A pesquisar…',
    searchUnavailable: 'A pesquisa de ligações está temporariamente indisponível.',
    noResults:
      'Ainda não há correspondências. Experimente outro nome ou pergunte ao Poneva que ferramenta se adequa.',
    showMore: 'Mostrar mais',
    showMoreRetry: 'Tentar carregar mais novamente',
    loadingMore: 'A carregar…',
    moreFailed: 'Não foi possível carregar mais integrações. Os seus resultados continuam aqui.',
    manageMembers: 'Escolher o que os membros podem ligar',
    manageDone: 'Concluído',
    manageHint: 'Ative ou desative apps para os membros. As contas já ligadas continuam ligadas.',
    allowFor: 'Permitir novas ligações a {name} para {company}',
    tileConnect: 'Ligar',
    tileConnecting: 'A ligar…',
    tileConnected: 'Ligada',
    tileSignIn: 'Iniciar sessão novamente',
    tileOff: 'Desativada',
    tileUnavailable: 'Indisponível',
    tileNotAllowed: 'Não permitida',
    tileNotAllowedNote: 'O seu administrador desativou novas ligações',
    tileAccountOnly: 'Só ligação de conta · ainda sem ferramentas de chat',
    tileApiKey: 'Requer chave de API',
    tileOwnApp: 'Usa a sua própria app {name}',
    tileAdminSetup: 'Configuração por proprietário ou administrador',
    otherWays: 'Outras formas de ligar',
    serverTitle: 'Servidor próprio',
    serverBody: 'Adicione ferramentas de um servidor MCP gerido pela sua equipa.',
    serverAction: 'Adicionar servidor',
    databaseTitle: 'Uma base de dados',
    databaseBody: 'PostgreSQL, limitada aos esquemas ou tabelas que escolher.',
    databaseAction: 'Ligar',
    assistantsTitle: 'Outros assistentes de IA',
    assistantsBody:
      'Deixe o Claude, o ChatGPT ou um agente de programação ler o conhecimento aprovado da empresa.',
    assistantsAction: 'Configurar',
    assistantsSheetIntro:
      'Assistentes fora do Poneva que leem o conhecimento aprovado da empresa e os agentes de programação que trabalham por si.',
    footnote:
      'Aprova as alterações antes de serem executadas. Integrações fornecidas pela Pipedream; as ferramentas de cada app variam.',
    askBody:
      'Não sabe de qual precisa? O Poneva pode sugerir ligações com base no trabalho que descrever.',
    askAction: 'Perguntar no chat',
    askPrompt:
      'Que fontes da empresa seria mais útil ligar para o nosso trabalho, com base no que sabes sobre nós?',
    compactTitle: 'Traga as suas ferramentas.',
    forYou: 'Para si',
    browseAll: 'Ver todas as ferramentas',
    selectedForFocus: 'Selecionadas com base no seu foco',
    popupBlocked: 'O navegador bloqueou a janela de início de sessão.',
    popupOpen: 'Abrir a página de início de sessão',
    popupKeepChecking: 'Esta página continua a verificar.',
    noticeAdded: 'Ligação adicionada.',
    noticeAddedReturn: 'Ligação adicionada. Pode fechar este separador e voltar ao seu trabalho.',
    noticeSignInUnfinished:
      'O início de sessão não foi concluído. Use Iniciar sessão novamente no servidor para tentar outra vez.',
    noticeChecked: 'Ligação verificada com êxito.',
    noticeUnreachable: 'Ainda não foi possível contactar o servidor.',
    noticeSignInReady: 'Há uma página de início de sessão pronta num novo separador.',
    noticeAuthorized: 'A autorização está concluída.',
    noticeDatabaseRemoved: 'O PostgreSQL foi desligado.',
    noticeStoreConnected: 'Loja Shopify ligada.',
    noticeStoreChooseAccess:
      'Loja Shopify ligada. Abra-a em As suas ligações para escolher quem a pode usar.',
    allowFailed: 'Não foi possível atualizar {name}. Tente novamente.',
    accessUnknown: 'Os detalhes do acesso não estão disponíveis.',
    metaStep: 'Passo {step} de {total}',
    stepsLabel: 'Passos de configuração',
    stepChoose: 'Escolher',
    stepPrepare: 'Preparar',
    stepAccess: 'Acesso',
    stepSignIn: 'Iniciar sessão',
    metaChooseTitle: 'Ligar Meta Ads',
    metaChooseIntro:
      'Traga as suas contas de anúncios da Meta para o Poneva. Primeiro, escolha o que o Poneva pode fazer com elas.',
    metaReporting: 'Relatórios',
    metaReportingTag: 'Só de leitura',
    metaReportingBody:
      'Lê contas de anúncios, campanhas, conjuntos de anúncios, anúncios e resultados, bem como as suas Páginas do Facebook. O Poneva nunca altera nada na Meta.',
    metaManage: 'Relatórios e gestão de anúncios',
    metaManageTag: 'Leitura e escrita',
    metaManageBody:
      'Também cria e atualiza campanhas, anúncios e catálogos com as próprias ferramentas da Meta. Escolhe se o Poneva pergunta antes de cada alteração.',
    metaOwnApp:
      'A Meta pede que cada empresa se ligue através da sua própria app Meta. O próximo passo ajuda-o a configurá-la, uma única vez.',
    metaConnectedAlready: 'Ligada',
    prepareTitle: 'Prepare a sua app Meta',
    prepareIntro:
      'Faça isto uma vez no Meta for Developers. Precisa de acesso de administrador ao portefólio empresarial da sua empresa na Meta.',
    prepareAppTitle: 'Crie ou abra a sua app Meta',
    prepareAppReporting: 'Ative a Marketing API e o Facebook Login for Business.',
    prepareAppManage: 'Adicione o caso de utilização «Create and manage ads with the ads MCP».',
    prepareAppOpen: 'Abrir o Meta for Developers',
    prepareRedirectTitle: 'Adicione o endereço de início de sessão do Poneva',
    prepareRedirectBody:
      'No Facebook Login for Business, cole-o em Valid OAuth Redirect URIs. Mantenha os endereços que já lá estão e guarde.',
    prepareSecretTitle: 'Tenha à mão o App ID e o App Secret',
    prepareSecretBody:
      'Ambos estão na sua app em App settings → Basic. Vai colá-los numa janela segura no último passo, nunca no Poneva.',
    prepareDone: 'Feito',
    copy: 'Copiar',
    copied: 'Copiado',
    copyAddress: 'Copiar o endereço de início de sessão',
    copyManual: 'Selecione o endereço e copie-o com o teclado.',
    permissionsTitle: 'O que a Meta lhe vai pedir para aprovar',
    permissionsNote:
      'A Meta pode pedir para verificar a sua empresa antes de a app conseguir aceder a contas de anúncios além das suas.',
    scopeAdsRead: 'Ler os seus anúncios e os respetivos resultados',
    scopeAdsManagement: 'Criar e alterar anúncios',
    scopeAdsMcp: 'Usar as ferramentas de anúncios da Meta',
    scopeBusiness: 'Ver os seus portefólios empresariais',
    scopePages: 'Listar as Páginas que gere',
    scopePageEngagement: 'Ler os detalhes e a interação das Páginas',
    scopeInstagram: 'Ler os dados básicos da conta do Instagram',
    scopeCatalog: 'Gerir catálogos de produtos',
    guide: 'Guia passo a passo',
    metaAccessTitle: 'Quem pode usar o Meta Ads?',
    metaAccessIntro:
      'As contas de anúncios pertencem normalmente a toda a empresa. Pode alterar isto a qualquer momento.',
    metaReadOnlyTitle: 'Só de leitura',
    metaReadOnlyBody:
      'Os relatórios nunca alteram nada na Meta. Para o Poneva gerir anúncios, escolha a gestão de anúncios no primeiro passo.',
    signInTitle: 'Iniciar sessão com a Meta',
    signInIntro: 'Abre-se uma janela segura. Conclua lá e esta página atualiza-se sozinha.',
    signInWindow: 'Na janela segura',
    signInPaste: 'Cole o App ID e o App Secret',
    signInChoose: 'Escolha a sua empresa e as respetivas contas de anúncios',
    signInApprove: 'Aprove o acesso na Meta',
    signInWaiting: 'A aguardar pela Meta…',
    signInSecret:
      'O seu App Secret vai diretamente para a Nango, o serviço de início de sessão que o Poneva usa, e fica lá guardado de forma encriptada. O Poneva nunca o vê.',
    stopWaiting: 'Deixar de esperar',
    doneTitle: '{name} está ligado',
    doneAccountsYou: 'Já pode fazer perguntas ao Poneva sobre {count}.',
    doneAccountsCompany: 'Todos em {company} já podem fazer perguntas ao Poneva sobre {count}.',
    doneTools: 'A Meta disponibilizou {count} para esta conta.',
    doneTry: 'Experimente perguntar',
    doneAsk: 'Perguntar sobre os meus anúncios',
    doneDone: 'Concluído',
    doneTryReport: 'Como correram as campanhas da semana passada?',
    doneTryReturn: 'Que anúncios deram o melhor retorno este mês?',
    continue: 'Continuar',
    continueToMeta: 'Continuar para a Meta',
    back: 'Voltar',
    cancel: 'Cancelar',
    askHelp: 'Pedir ajuda ao Poneva',
    askHelpPrompt:
      'Ajuda-me a configurar a nossa própria app Meta para eu poder ligar o Meta Ads ao Poneva.',
    chatTitle: 'Ligue para continuar',
    chatIntro: 'Este chat fica onde está.',
    chatConnectedYou: 'Ligada para si',
    chatConnectedCompany: 'Ligada para {company}',
    chatUnavailable: 'Sessão iniciada · ferramentas indisponíveis',
    chatFoot: 'Inicie sessão no fornecedor. Quando aparecer Ligada, peça ao Poneva para continuar.',
    chatPopupBlocked: 'O seu navegador bloqueou a janela de início de sessão.',
    chatOpenAuthorization: 'Abrir início de sessão',
    chatFallbackBody: 'Use este serviço no seu trabalho.',
  },
  ro: {
    eyebrow: 'Spațiul de lucru {company}',
    title: 'Conexiuni',
    intro:
      'Conectează-ți instrumentele ca să găsești informații și să duci treaba la capăt. Alege cine poate folosi fiecare conexiune și dacă Poneva poate doar să citească sau și să facă modificări.',
    activity: 'Activitate',
    backToConnections: 'Înapoi la Conexiuni',
    yoursTitle: 'Conexiunile tale',
    filterLabel: 'Afișează conexiunile',
    filterAll: 'Toate',
    filterMine: 'Doar eu',
    filterShared: 'Partajate',
    filterAttention: 'Necesită atenție',
    columnConnection: 'Conexiune',
    columnWho: 'Cine o poate folosi',
    columnWhat: 'Ce poate face Poneva',
    columnStatus: 'Stare',
    yoursLoading: 'Se încarcă conexiunile tale…',
    yoursEmpty: 'Încă nu e nimic conectat. Caută mai jos ca să conectezi primul instrument.',
    filterEmpty: 'Nu e nimic aici momentan.',
    yoursLoadError: 'Conexiunile tale nu au putut fi încărcate.',
    serversLoadError: 'Serverele tale proprii nu au putut fi încărcate.',
    databasesLoadError: 'Bazele tale de date nu au putut fi încărcate.',
    retry: 'Încearcă din nou',
    stateConnected: 'Conectată',
    stateAttention: 'Necesită autentificare',
    stateReview: 'Verifică accesul',
    stateUnavailable: 'Indisponibilă',
    stateOff: 'Dezactivată',
    stateConnecting: 'Se conectează…',
    signInAgain: 'Autentifică-te din nou',
    attentionBody:
      'Autentificarea pentru {name} a expirat sau a fost revocată. Autentifică-te din nou ca să o folosești în continuare.',
    reviewBody:
      'Contul asociat cu {name} s-a schimbat. Verifică cine o poate folosi și ce poate face Poneva înainte să fie folosită din nou.',
    unavailableBody:
      'Autentificarea în {name} este activă, dar instrumentele nu sunt disponibile momentan.',
    offBody:
      '{name} este dezactivată. Nimeni nu o poate folosi până nu este reactivată. Nimic nu se șterge.',
    kindServer: 'Server propriu',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabel|few:tabele|other:de tabele',
    adAccountsNoun: 'one:cont publicitar|few:conturi publicitare|other:de conturi publicitare',
    toolsNoun: 'one:instrument|few:instrumente|other:de instrumente',
    close: 'Închide',
    moreActions: 'Mai multe acțiuni pentru {name}',
    tabOverview: 'Prezentare generală',
    tabAccess: 'Acces',
    tabActivity: 'Activitate',
    connectedOn: 'Din {date}',
    lastUsed: 'Folosită ultima dată {when}',
    whatTitle: 'Ce poate face Poneva',
    readsLine: 'Citește când îi ceri',
    changesAsk: 'Te întreabă înainte să schimbe ceva',
    changesAllowed:
      'Face modificări fără să întrebe. Fiecare modificare este înregistrată în Activitate.',
    changesNever: 'Nu schimbă niciodată nimic',
    readinessReady: '{count} la dispoziție',
    change: 'Schimbă',
    tryTitle: 'Încearcă într-un chat',
    tryPrompt: 'Cu ce mă poți ajuta folosind {name}?',
    addAccount: 'Adaugă alt cont',
    disconnect: 'Deconectează',
    disconnectConfirm:
      'Deconectezi {name}? Nu va mai funcționa în chaturi, aplicații și agenți până nu este conectată din nou.',
    disconnectKeep: 'Păstrează',
    nativeOnlyYou: 'Doar tu poți folosi acest cont.',
    nativeEveryone: 'Toată lumea din {company} poate folosi acest cont.',
    providerPermissions: 'Permisiunile contului la furnizor rămân valabile.',
    manageDatabase: 'Gestionează baza de date',
    activityLoading: 'Se încarcă activitatea…',
    activityLoadError: 'Activitatea nu a putut fi încărcată.',
    activityRead: 'Citire',
    activityChange: 'Modificare',
    activitySetting: 'Setare',
    activityDenied: 'Refuzat',
    activityFailed: 'Eșuat',
    activityUnknown: 'Rezultat necunoscut',
    activityAll: 'Deschide toată activitatea',
    activityToday: 'Astăzi',
    activityYesterday: 'Ieri',
    eventCreated: 'Conexiune creată',
    eventShared: 'Partajare modificată',
    eventPersonAdded: 'Persoană adăugată',
    eventPersonRemoved: 'Persoană eliminată',
    eventAccessChanged: 'Acces modificat',
    eventRevoked: 'Acces dezactivat',
    eventRemoved: 'Conexiune eliminată',
    eventSettings: 'Limita de timp a interogărilor modificată',
    addTitle: 'Adaugă o conexiune',
    integrations: 'one:integrare|few:integrări|other:de integrări',
    searchLabel: 'Caută integrări',
    searchPlaceholder: 'Caută integrări',
    clearSearch: 'Șterge căutarea',
    inYours: 'În conexiunile tale',
    addNew: 'Adaugă una nouă',
    resultsFor: '{count} pentru „{query}”',
    resultsNoun: 'one:rezultat|few:rezultate|other:de rezultate',
    popularFirst: 'Cele populare mai întâi',
    searching: 'Se caută…',
    searchUnavailable: 'Căutarea conexiunilor este temporar indisponibilă.',
    noResults:
      'Nimic nu se potrivește încă. Încearcă alt nume sau întreabă Poneva ce instrument se potrivește.',
    showMore: 'Afișează mai multe',
    showMoreRetry: 'Încearcă din nou să încarci mai multe',
    loadingMore: 'Se încarcă…',
    moreFailed: 'Nu s-au putut încărca mai multe integrări. Rezultatele tale sunt încă aici.',
    manageMembers: 'Alege ce pot conecta membrii',
    manageDone: 'Gata',
    manageHint:
      'Activează sau dezactivează aplicații pentru membri. Conturile deja conectate rămân conectate.',
    allowFor: 'Permite conexiuni noi la {name} pentru {company}',
    tileConnect: 'Conectează',
    tileConnecting: 'Se conectează…',
    tileConnected: 'Conectată',
    tileSignIn: 'Autentifică-te din nou',
    tileOff: 'Dezactivată',
    tileUnavailable: 'Indisponibilă',
    tileNotAllowed: 'Nepermisă',
    tileNotAllowedNote: 'Administratorul tău a dezactivat conexiunile noi',
    tileAccountOnly: 'Doar conexiune de cont · încă fără instrumente de chat',
    tileApiKey: 'Necesită cheie API',
    tileOwnApp: 'Folosește propria ta aplicație {name}',
    tileAdminSetup: 'Configurare de către proprietar sau administrator',
    otherWays: 'Alte moduri de conectare',
    serverTitle: 'Server propriu',
    serverBody: 'Adaugă instrumente de pe un server MCP administrat de echipa ta.',
    serverAction: 'Adaugă server',
    databaseTitle: 'O bază de date',
    databaseBody: 'PostgreSQL, limitată la schemele sau tabelele pe care le alegi.',
    databaseAction: 'Conectează',
    assistantsTitle: 'Alți asistenți AI',
    assistantsBody:
      'Permite ca Claude, ChatGPT sau un agent de programare să citească cunoștințele aprobate ale companiei.',
    assistantsAction: 'Configurează',
    assistantsSheetIntro:
      'Asistenți din afara Poneva care citesc cunoștințele aprobate ale companiei și agenții de programare care lucrează pentru tine.',
    footnote:
      'Aprobi modificările înainte să fie executate. Integrări oferite de Pipedream; instrumentele fiecărei aplicații diferă.',
    askBody:
      'Nu știi de care ai nevoie? Poneva îți poate sugera conexiuni pe baza muncii pe care o descrii.',
    askAction: 'Întreabă în chat',
    askPrompt:
      'Ce surse ale companiei ar fi cel mai util să conectăm pentru munca noastră, pe baza a ceea ce știi despre noi?',
    compactTitle: 'Adu-ți instrumentele cu tine.',
    forYou: 'Pentru tine',
    browseAll: 'Răsfoiește toate instrumentele',
    selectedForFocus: 'Selectate după direcția ta',
    popupBlocked: 'Browserul a blocat fereastra de autentificare.',
    popupOpen: 'Deschide pagina de autentificare',
    popupKeepChecking: 'Această pagină continuă să verifice.',
    noticeAdded: 'Conexiune adăugată.',
    noticeAddedReturn:
      'Conexiune adăugată. Poți închide această filă și să te întorci la munca ta.',
    noticeSignInUnfinished:
      'Autentificarea nu s-a finalizat. Folosește Autentifică-te din nou la server ca să mai încerci o dată.',
    noticeChecked: 'Conexiunea a fost verificată cu succes.',
    noticeUnreachable: 'Serverul nu a putut fi accesat încă.',
    noticeSignInReady: 'O pagină de autentificare este pregătită într-o filă nouă.',
    noticeAuthorized: 'Autorizarea este finalizată.',
    noticeDatabaseRemoved: 'PostgreSQL a fost deconectat.',
    noticeStoreConnected: 'Magazin Shopify conectat.',
    noticeStoreChooseAccess:
      'Magazin Shopify conectat. Deschide-l în Conexiunile tale ca să alegi cine îl poate folosi.',
    allowFailed: 'Nu s-a putut actualiza {name}. Încearcă din nou.',
    accessUnknown: 'Detaliile accesului nu sunt disponibile.',
    metaStep: 'Pasul {step} din {total}',
    stepsLabel: 'Pașii de configurare',
    stepChoose: 'Alege',
    stepPrepare: 'Pregătește',
    stepAccess: 'Acces',
    stepSignIn: 'Autentificare',
    metaChooseTitle: 'Conectează Meta Ads',
    metaChooseIntro:
      'Adu conturile tale publicitare Meta în Poneva. Mai întâi, alege ce poate face Poneva cu ele.',
    metaReporting: 'Rapoarte',
    metaReportingTag: 'Doar citire',
    metaReportingBody:
      'Citește conturi publicitare, campanii, seturi de reclame, reclame și rezultate, plus Paginile tale de Facebook. Poneva nu schimbă niciodată nimic în Meta.',
    metaManage: 'Rapoarte și gestionarea reclamelor',
    metaManageTag: 'Citire și scriere',
    metaManageBody:
      'Creează și actualizează și campanii, reclame și cataloage cu instrumentele proprii ale Meta. Tu alegi dacă Poneva întreabă înainte de fiecare modificare.',
    metaOwnApp:
      'Meta cere fiecărei companii să se conecteze prin propria aplicație Meta. Pasul următor te ghidează o singură dată prin configurare.',
    metaConnectedAlready: 'Conectată',
    prepareTitle: 'Pregătește-ți aplicația Meta',
    prepareIntro:
      'Fă asta o singură dată în Meta for Developers. Ai nevoie de acces de administrator la portofoliul de business al companiei tale în Meta.',
    prepareAppTitle: 'Creează sau deschide aplicația ta Meta',
    prepareAppReporting: 'Activează Marketing API și Facebook Login for Business.',
    prepareAppManage: 'Adaugă cazul de utilizare „Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Deschide Meta for Developers',
    prepareRedirectTitle: 'Adaugă adresa de autentificare Poneva',
    prepareRedirectBody:
      'În Facebook Login for Business, lipește-o la Valid OAuth Redirect URIs. Păstrează adresele care sunt deja acolo, apoi salvează.',
    prepareSecretTitle: 'Pregătește App ID și App Secret',
    prepareSecretBody:
      'Le găsești pe amândouă în aplicație, la App settings → Basic. Le vei lipi într-o fereastră securizată la ultimul pas, niciodată în Poneva.',
    prepareDone: 'Gata',
    copy: 'Copiază',
    copied: 'Copiat',
    copyAddress: 'Copiază adresa de autentificare',
    copyManual: 'Selectează adresa și copiaz-o cu tastatura.',
    permissionsTitle: 'Ce îți va cere Meta să aprobi',
    permissionsNote:
      'Meta poate cere verificarea companiei tale înainte ca aplicația să poată accesa și alte conturi publicitare decât ale tale.',
    scopeAdsRead: 'Citirea reclamelor tale și a rezultatelor lor',
    scopeAdsManagement: 'Crearea și modificarea reclamelor',
    scopeAdsMcp: 'Folosirea instrumentelor de reclame ale Meta',
    scopeBusiness: 'Vizualizarea portofoliilor tale de business',
    scopePages: 'Listarea Paginilor pe care le gestionezi',
    scopePageEngagement: 'Citirea detaliilor și a interacțiunilor Paginilor',
    scopeInstagram: 'Citirea detaliilor de bază ale contului de Instagram',
    scopeCatalog: 'Gestionarea cataloagelor de produse',
    guide: 'Ghid pas cu pas',
    metaAccessTitle: 'Cine poate folosi Meta Ads?',
    metaAccessIntro:
      'Conturile publicitare aparțin de obicei întregii companii. Poți schimba asta oricând.',
    metaReadOnlyTitle: 'Doar citire',
    metaReadOnlyBody:
      'Rapoartele nu schimbă niciodată nimic în Meta. Ca Poneva să gestioneze reclame, alege gestionarea reclamelor la primul pas.',
    signInTitle: 'Autentificare cu Meta',
    signInIntro:
      'Se deschide o fereastră securizată. Finalizează acolo, iar această pagină se actualizează singură.',
    signInWindow: 'În fereastra securizată',
    signInPaste: 'Lipește App ID și App Secret',
    signInChoose: 'Alege compania ta și conturile ei publicitare',
    signInApprove: 'Aprobă accesul în Meta',
    signInWaiting: 'Se așteaptă Meta…',
    signInSecret:
      'App Secret ajunge direct la Nango, serviciul de autentificare folosit de Poneva, și este stocat acolo criptat. Poneva nu îl vede niciodată.',
    stopWaiting: 'Nu mai aștepta',
    doneTitle: '{name} este conectat',
    doneAccountsYou: 'Acum poți întreba Poneva despre {count}.',
    doneAccountsCompany: 'Acum toată lumea din {company} poate întreba Poneva despre {count}.',
    doneTools: 'Meta a oferit {count} pentru acest cont.',
    doneTry: 'Încearcă să întrebi',
    doneAsk: 'Întreabă despre reclamele mele',
    doneDone: 'Gata',
    doneTryReport: 'Cum au mers campaniile de săptămâna trecută?',
    doneTryReturn: 'Ce reclame au adus cel mai bun randament luna aceasta?',
    continue: 'Continuă',
    continueToMeta: 'Continuă la Meta',
    back: 'Înapoi',
    cancel: 'Anulează',
    askHelp: 'Cere ajutor de la Poneva',
    askHelpPrompt:
      'Ajută-mă să configurez propria noastră aplicație Meta ca să pot conecta Meta Ads la Poneva.',
    chatTitle: 'Conectează pentru a continua',
    chatIntro: 'Acest chat rămâne unde este.',
    chatConnectedYou: 'Conectată pentru tine',
    chatConnectedCompany: 'Conectată pentru {company}',
    chatUnavailable: 'Autentificat · instrumente indisponibile',
    chatFoot: 'Autentifică-te la furnizor. Când apare Conectată, cere-i Poneva să continue.',
    chatPopupBlocked: 'Browserul tău a blocat fereastra de autentificare.',
    chatOpenAuthorization: 'Deschide autentificarea',
    chatFallbackBody: 'Folosește acest serviciu în munca ta.',
  },
  sk: {
    eyebrow: 'Pracovný priestor {company}',
    title: 'Pripojenia',
    intro:
      'Pripojte svoje nástroje, aby ste mohli hľadať informácie a vybavovať prácu. Pri každom pripojení zvoľte, kto ho môže používať a či môže Poneva čítať, alebo aj robiť zmeny.',
    activity: 'Aktivita',
    backToConnections: 'Späť na Pripojenia',
    yoursTitle: 'Vaše pripojenia',
    filterLabel: 'Zobraziť pripojenia',
    filterAll: 'Všetky',
    filterMine: 'Len ja',
    filterShared: 'Zdieľané',
    filterAttention: 'Vyžadujú pozornosť',
    columnConnection: 'Pripojenie',
    columnWho: 'Kto ho môže používať',
    columnWhat: 'Čo môže Poneva robiť',
    columnStatus: 'Stav',
    yoursLoading: 'Načítavajú sa vaše pripojenia…',
    yoursEmpty: 'Zatiaľ nie je nič pripojené. Vyhľadajte nižšie svoj prvý nástroj a pripojte ho.',
    filterEmpty: 'Momentálne tu nič nie je.',
    yoursLoadError: 'Vaše pripojenia sa nepodarilo načítať.',
    serversLoadError: 'Vaše vlastné servery sa nepodarilo načítať.',
    databasesLoadError: 'Vaše databázy sa nepodarilo načítať.',
    retry: 'Skúsiť znova',
    stateConnected: 'Pripojené',
    stateAttention: 'Vyžaduje prihlásenie',
    stateReview: 'Skontrolovať prístup',
    stateUnavailable: 'Nedostupné',
    stateOff: 'Vypnuté',
    stateConnecting: 'Pripája sa…',
    signInAgain: 'Znova sa prihlásiť',
    attentionBody:
      'Prihlásenie do služby {name} vypršalo alebo bolo odvolané. Ak ju chcete ďalej používať, prihláste sa znova.',
    reviewBody:
      'Účet za službou {name} sa zmenil. Skôr ako sa znova použije, skontrolujte, kto ju môže používať a čo môže Poneva robiť.',
    unavailableBody: 'Do služby {name} ste prihlásení, ale jej nástroje teraz nie sú k dispozícii.',
    offBody:
      'Služba {name} je vypnutá. Kým ju znova nezapnete, nikto ju nebude môcť používať. Nič sa neodstráni.',
    kindServer: 'Vlastný server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabuľka|few:tabuľky|many:tabuľky|other:tabuliek',
    adAccountsNoun:
      'one:reklamný účet|few:reklamné účty|many:reklamného účtu|other:reklamných účtov',
    toolsNoun: 'one:nástroj|few:nástroje|many:nástroja|other:nástrojov',
    close: 'Zavrieť',
    moreActions: 'Ďalšie akcie pre {name}',
    tabOverview: 'Prehľad',
    tabAccess: 'Prístup',
    tabActivity: 'Aktivita',
    connectedOn: 'Od {date}',
    lastUsed: 'Naposledy použité {when}',
    whatTitle: 'Čo môže Poneva robiť',
    readsLine: 'Číta, keď o to požiadate',
    changesAsk: 'Skôr než niečo zmení, opýta sa vás',
    changesAllowed: 'Robí zmeny bez pýtania. Každá sa zaznamená v časti Aktivita.',
    changesNever: 'Nikdy nič nemení',
    readinessReady: 'Pripravené: {count}',
    change: 'Zmeniť',
    tryTitle: 'Vyskúšajte to v chate',
    tryPrompt: 'S čím mi môžeš pomôcť cez {name}?',
    addAccount: 'Pridať ďalší účet',
    disconnect: 'Odpojiť',
    disconnectConfirm:
      'Odpojiť službu {name}? Kým ju znova nepripojíte, prestane fungovať v chatoch, aplikáciách a agentoch.',
    disconnectKeep: 'Ponechať',
    nativeOnlyYou: 'Tento účet môžete používať len vy.',
    nativeEveryone: 'Tento účet môžu používať všetci vo firme {company}.',
    providerPermissions: 'Vlastné oprávnenia účtu u poskytovateľa naďalej platia.',
    manageDatabase: 'Spravovať databázu',
    activityLoading: 'Načítava sa aktivita…',
    activityLoadError: 'Aktivitu sa nepodarilo načítať.',
    activityRead: 'Čítanie',
    activityChange: 'Zmena',
    activitySetting: 'Nastavenie',
    activityDenied: 'Zamietnuté',
    activityFailed: 'Zlyhalo',
    activityUnknown: 'Neznámy výsledok',
    activityAll: 'Otvoriť celú aktivitu',
    activityToday: 'Dnes',
    activityYesterday: 'Včera',
    eventCreated: 'Pripojenie vytvorené',
    eventShared: 'Zdieľanie zmenené',
    eventPersonAdded: 'Osoba pridaná',
    eventPersonRemoved: 'Osoba odstránená',
    eventAccessChanged: 'Prístup zmenený',
    eventRevoked: 'Prístup vypnutý',
    eventRemoved: 'Pripojenie odstránené',
    eventSettings: 'Časový limit dopytov zmenený',
    addTitle: 'Pridať pripojenie',
    integrations: 'one:integrácia|few:integrácie|many:integrácie|other:integrácií',
    searchLabel: 'Hľadať integrácie',
    searchPlaceholder: 'Hľadať integrácie',
    clearSearch: 'Vymazať hľadanie',
    inYours: 'Vo vašich pripojeniach',
    addNew: 'Pridať nové',
    resultsFor: '{count} pre „{query}“',
    resultsNoun: 'one:výsledok|few:výsledky|many:výsledku|other:výsledkov',
    popularFirst: 'Najprv obľúbené',
    searching: 'Hľadá sa…',
    searchUnavailable: 'Hľadanie pripojení je dočasne nedostupné.',
    noResults:
      'Zatiaľ nič nezodpovedá. Skúste iný názov alebo sa opýtajte Poneva, ktorý nástroj sa hodí.',
    showMore: 'Zobraziť ďalšie',
    showMoreRetry: 'Skúsiť znova načítať ďalšie',
    loadingMore: 'Načítava sa…',
    moreFailed: 'Ďalšie integrácie sa nepodarilo načítať. Vaše výsledky tu zostávajú.',
    manageMembers: 'Vybrať, čo môžu členovia pripojiť',
    manageDone: 'Hotovo',
    manageHint: 'Zapnite alebo vypnite aplikácie pre členov. Už pripojené účty zostanú pripojené.',
    allowFor: 'Povoliť nové pripojenia služby {name} pre firmu {company}',
    tileConnect: 'Pripojiť',
    tileConnecting: 'Pripája sa…',
    tileConnected: 'Pripojené',
    tileSignIn: 'Znova sa prihlásiť',
    tileOff: 'Vypnuté',
    tileUnavailable: 'Nedostupné',
    tileNotAllowed: 'Nepovolené',
    tileNotAllowedNote: 'Váš správca vypol nové pripojenia',
    tileAccountOnly: 'Len pripojenie účtu · zatiaľ bez nástrojov pre chat',
    tileApiKey: 'Vyžaduje sa kľúč API',
    tileOwnApp: 'Používa vašu vlastnú aplikáciu {name}',
    tileAdminSetup: 'Nastavuje vlastník alebo správca',
    otherWays: 'Ďalšie spôsoby pripojenia',
    serverTitle: 'Vlastný server',
    serverBody: 'Pridajte nástroje zo servera MCP, ktorý prevádzkuje váš tím.',
    serverAction: 'Pridať server',
    databaseTitle: 'Databáza',
    databaseBody: 'PostgreSQL, obmedzená na schémy alebo tabuľky, ktoré vyberiete.',
    databaseAction: 'Pripojiť',
    assistantsTitle: 'Ďalší asistenti AI',
    assistantsBody:
      'Nechajte Claude, ChatGPT alebo programovacieho agenta čítať schválené firemné znalosti.',
    assistantsAction: 'Nastaviť',
    assistantsSheetIntro:
      'Asistenti mimo Poneva, ktorí čítajú schválené firemné znalosti, a programovací agenti, ktorí pracujú za vás.',
    footnote:
      'Zmeny schvaľujete skôr, než sa vykonajú. Integrácie zabezpečuje Pipedream; nástroje jednotlivých aplikácií sa líšia.',
    askBody:
      'Neviete, ktoré potrebujete? Poneva vám môže navrhnúť pripojenia podľa práce, ktorú opíšete.',
    askAction: 'Opýtať sa v chate',
    askPrompt:
      'Ktoré firemné zdroje by bolo najužitočnejšie pripojiť pre našu prácu, podľa toho, čo o nás vieš?',
    compactTitle: 'Vezmite si so sebou svoje nástroje.',
    forYou: 'Pre vás',
    browseAll: 'Prehliadať všetky nástroje',
    selectedForFocus: 'Vybrané podľa vášho zamerania',
    popupBlocked: 'Prehliadač zablokoval prihlasovacie okno.',
    popupOpen: 'Otvoriť prihlasovaciu stránku',
    popupKeepChecking: 'Táto stránka to ďalej kontroluje.',
    noticeAdded: 'Pripojenie bolo pridané.',
    noticeAddedReturn:
      'Pripojenie bolo pridané. Túto kartu môžete zavrieť a vrátiť sa k svojej práci.',
    noticeSignInUnfinished:
      'Prihlásenie sa nedokončilo. Ak to chcete skúsiť znova, použite pri serveri tlačidlo Znova sa prihlásiť.',
    noticeChecked: 'Pripojenie bolo úspešne overené.',
    noticeUnreachable: 'Server sa zatiaľ nepodarilo kontaktovať.',
    noticeSignInReady: 'Prihlasovacia stránka je pripravená na novej karte.',
    noticeAuthorized: 'Povolenie prístupu je dokončené.',
    noticeDatabaseRemoved: 'Databáza PostgreSQL bola odpojená.',
    noticeStoreConnected: 'Obchod Shopify je pripojený.',
    noticeStoreChooseAccess:
      'Obchod Shopify je pripojený. Otvorte ho v sekcii Vaše pripojenia a zvoľte, kto ho môže používať.',
    allowFailed: 'Aplikáciu {name} sa nepodarilo aktualizovať. Skúste to znova.',
    accessUnknown: 'Podrobnosti o prístupe nie sú k dispozícii.',
    metaStep: 'Krok {step} z {total}',
    stepsLabel: 'Kroky nastavenia',
    stepChoose: 'Výber',
    stepPrepare: 'Príprava',
    stepAccess: 'Prístup',
    stepSignIn: 'Prihlásenie',
    metaChooseTitle: 'Pripojiť Meta Ads',
    metaChooseIntro:
      'Preneste svoje reklamné účty Meta do Poneva. Najprv zvoľte, čo s nimi Poneva smie robiť.',
    metaReporting: 'Reporty',
    metaReportingTag: 'Len čítanie',
    metaReportingBody:
      'Číta reklamné účty, kampane, skupiny reklám, reklamy a výsledky a tiež vaše stránky na Facebooku. Poneva v Meta nikdy nič nemení.',
    metaManage: 'Reporty a správa reklám',
    metaManageTag: 'Čítanie a zápis',
    metaManageBody:
      'Navyše vytvára a aktualizuje kampane, reklamy a katalógy pomocou vlastných nástrojov Meta. Vy rozhodnete, či sa Poneva pred každou zmenou opýta.',
    metaOwnApp:
      'Meta vyžaduje, aby sa každá firma pripájala cez vlastnú aplikáciu Meta. Ďalší krok vás raz prevedie jej nastavením.',
    metaConnectedAlready: 'Pripojené',
    prepareTitle: 'Pripravte si aplikáciu Meta',
    prepareIntro:
      'Urobte to raz v Meta for Developers. Potrebujete prístup správcu k firemnému účtu vašej spoločnosti v Meta.',
    prepareAppTitle: 'Vytvorte alebo otvorte svoju aplikáciu Meta',
    prepareAppReporting: 'Zapnite Marketing API a Facebook Login for Business.',
    prepareAppManage: 'Pridajte prípad použitia „Create and manage ads with the ads MCP“.',
    prepareAppOpen: 'Otvoriť Meta for Developers',
    prepareRedirectTitle: 'Pridajte prihlasovaciu adresu Poneva',
    prepareRedirectBody:
      'Vo Facebook Login for Business ju vložte do poľa Valid OAuth Redirect URIs. Adresy, ktoré tam už sú, ponechajte a potom uložte.',
    prepareSecretTitle: 'Pripravte si App ID a App Secret',
    prepareSecretBody:
      'Oboje nájdete v aplikácii v časti App settings → Basic. V poslednom kroku ich vložíte do zabezpečeného okna, nikdy nie do Poneva.',
    prepareDone: 'Hotovo',
    copy: 'Kopírovať',
    copied: 'Skopírované',
    copyAddress: 'Kopírovať prihlasovaciu adresu',
    copyManual: 'Označte adresu a skopírujte ju klávesnicou.',
    permissionsTitle: 'Čo vás Meta požiada schváliť',
    permissionsNote:
      'Skôr ako aplikácia získa prístup k reklamným účtom okrem vašich vlastných, môže Meta požiadať o overenie vašej firmy.',
    scopeAdsRead: 'Čítať vaše reklamy a ich výsledky',
    scopeAdsManagement: 'Vytvárať a meniť reklamy',
    scopeAdsMcp: 'Používať reklamné nástroje Meta',
    scopeBusiness: 'Zobraziť vaše firemné portfóliá',
    scopePages: 'Zobraziť zoznam stránok, ktoré spravujete',
    scopePageEngagement: 'Čítať údaje o stránkach a interakcie',
    scopeInstagram: 'Čítať základné údaje o účte na Instagrame',
    scopeCatalog: 'Spravovať katalógy produktov',
    guide: 'Podrobný návod',
    metaAccessTitle: 'Kto môže používať Meta Ads?',
    metaAccessIntro: 'Reklamné účty zvyčajne patria celej firme. Kedykoľvek to môžete zmeniť.',
    metaReadOnlyTitle: 'Len čítanie',
    metaReadOnlyBody:
      'Reporty v Meta nikdy nič nemenia. Ak chcete, aby Poneva spravovala reklamy, v prvom kroku zvoľte správu reklám.',
    signInTitle: 'Prihlásiť sa cez Meta',
    signInIntro: 'Otvorí sa zabezpečené okno. Dokončite to tam a táto stránka sa sama aktualizuje.',
    signInWindow: 'V zabezpečenom okne',
    signInPaste: 'Vložte App ID a App Secret',
    signInChoose: 'Vyberte svoju firmu a jej reklamné účty',
    signInApprove: 'Schváľte prístup v Meta',
    signInWaiting: 'Čaká sa na Meta…',
    signInSecret:
      'Váš App Secret ide priamo do Nango, prihlasovacej služby, ktorú Poneva používa, a je tam uložený zašifrovaný. Poneva ho nikdy neuvidí.',
    stopWaiting: 'Prestať čakať',
    doneTitle: 'Služba {name} je pripojená',
    doneAccountsYou: 'Teraz sa môžete Poneva pýtať na {count}.',
    doneAccountsCompany: 'Všetci vo firme {company} sa teraz môžu Poneva pýtať na {count}.',
    doneTools: 'Meta pre tento účet sprístupnila {count}.',
    doneTry: 'Skúste sa opýtať',
    doneAsk: 'Opýtať sa na moje reklamy',
    doneDone: 'Hotovo',
    doneTryReport: 'Ako sa darilo kampaniam z minulého týždňa?',
    doneTryReturn: 'Ktoré reklamy tento mesiac priniesli najlepšiu návratnosť?',
    continue: 'Pokračovať',
    continueToMeta: 'Pokračovať do Meta',
    back: 'Späť',
    cancel: 'Zrušiť',
    askHelp: 'Požiadať Poneva o pomoc',
    askHelpPrompt:
      'Pomôž mi nastaviť našu vlastnú aplikáciu Meta, aby sme mohli pripojiť Meta Ads k Poneva.',
    chatTitle: 'Pripojte a pokračujte',
    chatIntro: 'Tento chat zostane, kde je.',
    chatConnectedYou: 'Pripojené pre vás',
    chatConnectedCompany: 'Pripojené pre firmu {company}',
    chatUnavailable: 'Prihlásené · nástroje nie sú k dispozícii',
    chatFoot:
      'Prihláste sa u poskytovateľa. Keď sa zobrazí Pripojené, požiadajte Poneva, nech pokračuje.',
    chatPopupBlocked: 'Váš prehliadač zablokoval prihlasovacie okno.',
    chatOpenAuthorization: 'Otvoriť prihlásenie',
    chatFallbackBody: 'Používajte túto službu pri svojej práci.',
  },
  sl: {
    eyebrow: 'Delovni prostor {company}',
    title: 'Povezave',
    intro:
      'Povežite svoja orodja, da boste našli informacije in opravili delo. Za vsako povezavo izberite, kdo jo lahko uporablja in ali lahko Poneva bere ali tudi kaj spremeni.',
    activity: 'Dejavnost',
    backToConnections: 'Nazaj na Povezave',
    yoursTitle: 'Vaše povezave',
    filterLabel: 'Prikaži povezave',
    filterAll: 'Vse',
    filterMine: 'Samo jaz',
    filterShared: 'V skupni rabi',
    filterAttention: 'Zahtevajo pozornost',
    columnConnection: 'Povezava',
    columnWho: 'Kdo jo lahko uporablja',
    columnWhat: 'Kaj lahko Poneva počne',
    columnStatus: 'Stanje',
    yoursLoading: 'Nalaganje vaših povezav…',
    yoursEmpty: 'Nič še ni povezano. Poiščite spodaj in povežite svoje prvo orodje.',
    filterEmpty: 'Tu trenutno ni ničesar.',
    yoursLoadError: 'Vaših povezav ni bilo mogoče naložiti.',
    serversLoadError: 'Vaših strežnikov ni bilo mogoče naložiti.',
    databasesLoadError: 'Vaših zbirk podatkov ni bilo mogoče naložiti.',
    retry: 'Poskusi znova',
    stateConnected: 'Povezano',
    stateAttention: 'Potrebna je prijava',
    stateReview: 'Preglejte dostop',
    stateUnavailable: 'Ni na voljo',
    stateOff: 'Izklopljeno',
    stateConnecting: 'Povezovanje…',
    signInAgain: 'Znova se prijavite',
    attentionBody:
      'Prijava v storitev {name} je potekla ali je bila preklicana. Znova se prijavite, da jo boste lahko še naprej uporabljali.',
    reviewBody:
      'Račun za storitvijo {name} se je spremenil. Preden se znova uporabi, preglejte, kdo jo lahko uporablja in kaj lahko Poneva počne.',
    unavailableBody:
      'V storitev {name} ste prijavljeni, vendar njena orodja trenutno niso na voljo.',
    offBody:
      'Storitev {name} je izklopljena. Nihče je ne bo mogel uporabljati, dokler je znova ne vklopite. Nič se ne izbriše.',
    kindServer: 'Lastni strežnik',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabela|two:tabeli|few:tabele|other:tabel',
    adAccountsNoun:
      'one:oglaševalski račun|two:oglaševalska računa|few:oglaševalski računi|other:oglaševalskih računov',
    toolsNoun: 'one:orodje|two:orodji|few:orodja|other:orodij',
    close: 'Zapri',
    moreActions: 'Več dejanj za {name}',
    tabOverview: 'Pregled',
    tabAccess: 'Dostop',
    tabActivity: 'Dejavnost',
    connectedOn: 'Od {date}',
    lastUsed: 'Nazadnje uporabljeno {when}',
    whatTitle: 'Kaj lahko Poneva počne',
    readsLine: 'Bere, ko jo prosite',
    changesAsk: 'Vpraša vas, preden karkoli spremeni',
    changesAllowed: 'Spremembe izvaja brez vprašanja. Vsaka se zabeleži v razdelku Dejavnost.',
    changesNever: 'Nikoli ničesar ne spremeni',
    readinessReady: 'Na voljo: {count}',
    change: 'Spremeni',
    tryTitle: 'Preizkusite v klepetu',
    tryPrompt: 'Pri čem mi lahko pomagaš s storitvijo {name}?',
    addAccount: 'Dodaj še en račun',
    disconnect: 'Prekini povezavo',
    disconnectConfirm:
      'Želite prekiniti povezavo s storitvijo {name}? Ne bo več delovala v klepetih, aplikacijah in agentih, dokler je znova ne povežete.',
    disconnectKeep: 'Obdrži',
    nativeOnlyYou: 'Ta račun lahko uporabljate samo vi.',
    nativeEveryone: 'Ta račun lahko uporabljajo vsi v podjetju {company}.',
    providerPermissions: 'Lastna dovoljenja računa pri ponudniku še vedno veljajo.',
    manageDatabase: 'Upravljaj zbirko podatkov',
    activityLoading: 'Nalaganje dejavnosti…',
    activityLoadError: 'Dejavnosti ni bilo mogoče naložiti.',
    activityRead: 'Branje',
    activityChange: 'Sprememba',
    activitySetting: 'Nastavitev',
    activityDenied: 'Zavrnjeno',
    activityFailed: 'Neuspešno',
    activityUnknown: 'Neznan rezultat',
    activityAll: 'Odpri vso dejavnost',
    activityToday: 'Danes',
    activityYesterday: 'Včeraj',
    eventCreated: 'Povezava ustvarjena',
    eventShared: 'Deljenje spremenjeno',
    eventPersonAdded: 'Oseba dodana',
    eventPersonRemoved: 'Oseba odstranjena',
    eventAccessChanged: 'Dostop spremenjen',
    eventRevoked: 'Dostop izklopljen',
    eventRemoved: 'Povezava odstranjena',
    eventSettings: 'Časovna omejitev poizvedb spremenjena',
    addTitle: 'Dodaj povezavo',
    integrations: 'one:integracija|two:integraciji|few:integracije|other:integracij',
    searchLabel: 'Iskanje integracij',
    searchPlaceholder: 'Iskanje integracij',
    clearSearch: 'Počisti iskanje',
    inYours: 'V vaših povezavah',
    addNew: 'Dodaj novo',
    resultsFor: '{count} za »{query}«',
    resultsNoun: 'one:rezultat|two:rezultata|few:rezultati|other:rezultatov',
    popularFirst: 'Najprej priljubljene',
    searching: 'Iskanje…',
    searchUnavailable: 'Iskanje povezav trenutno ni na voljo.',
    noResults:
      'Nič se še ne ujema. Poskusite z drugim imenom ali vprašajte Ponevo, katero orodje je primerno.',
    showMore: 'Prikaži več',
    showMoreRetry: 'Znova poskusi naložiti več',
    loadingMore: 'Nalaganje…',
    moreFailed: 'Več integracij ni bilo mogoče naložiti. Vaši rezultati so še vedno tukaj.',
    manageMembers: 'Izberite, kaj lahko povežejo člani',
    manageDone: 'Končano',
    manageHint:
      'Vklopite ali izklopite aplikacije za člane. Računi, ki so že povezani, ostanejo povezani.',
    allowFor: 'Dovoli nove povezave s storitvijo {name} za podjetje {company}',
    tileConnect: 'Poveži',
    tileConnecting: 'Povezovanje…',
    tileConnected: 'Povezano',
    tileSignIn: 'Znova se prijavite',
    tileOff: 'Izklopljeno',
    tileUnavailable: 'Ni na voljo',
    tileNotAllowed: 'Ni dovoljeno',
    tileNotAllowedNote: 'Vaš skrbnik je izklopil nove povezave',
    tileAccountOnly: 'Samo povezava računa · še brez orodij za klepet',
    tileApiKey: 'Potreben je ključ API',
    tileOwnApp: 'Uporablja vašo lastno aplikacijo {name}',
    tileAdminSetup: 'Nastavi lastnik ali skrbnik',
    otherWays: 'Drugi načini povezovanja',
    serverTitle: 'Lastni strežnik',
    serverBody: 'Dodajte orodja s strežnika MCP, ki ga upravlja vaša ekipa.',
    serverAction: 'Dodaj strežnik',
    databaseTitle: 'Zbirka podatkov',
    databaseBody: 'PostgreSQL, omejen na sheme ali tabele, ki jih izberete.',
    databaseAction: 'Poveži',
    assistantsTitle: 'Drugi pomočniki UI',
    assistantsBody:
      'Dovolite, da Claude, ChatGPT ali agent za programiranje bere odobreno znanje podjetja.',
    assistantsAction: 'Nastavi',
    assistantsSheetIntro:
      'Pomočniki zunaj Poneve, ki berejo odobreno znanje podjetja, in agenti za programiranje, ki delajo za vas.',
    footnote:
      'Spremembe odobrite, preden se izvedejo. Integracije zagotavlja Pipedream; orodja posameznih aplikacij se razlikujejo.',
    askBody:
      'Ne veste, katero potrebujete? Poneva lahko predlaga povezave glede na delo, ki ga opišete.',
    askAction: 'Vprašaj v klepetu',
    askPrompt:
      'Katere vire podjetja bi bilo za naše delo najkoristneje povezati glede na to, kar veš o nas?',
    compactTitle: 'Prinesite svoja orodja.',
    forYou: 'Za vas',
    browseAll: 'Prebrskajte vsa orodja',
    selectedForFocus: 'Izbrano glede na vaš poudarek',
    popupBlocked: 'Brskalnik je blokiral okno za prijavo.',
    popupOpen: 'Odpri stran za prijavo',
    popupKeepChecking: 'Ta stran še naprej preverja.',
    noticeAdded: 'Povezava je dodana.',
    noticeAddedReturn: 'Povezava je dodana. Ta zavihek lahko zaprete in se vrnete k svojemu delu.',
    noticeSignInUnfinished:
      'Prijava ni bila dokončana. Za ponovni poskus pri strežniku izberite Znova se prijavite.',
    noticeChecked: 'Povezava je bila uspešno preverjena.',
    noticeUnreachable: 'Strežnika še ni bilo mogoče doseči.',
    noticeSignInReady: 'Stran za prijavo je pripravljena v novem zavihku.',
    noticeAuthorized: 'Odobritev je končana.',
    noticeDatabaseRemoved: 'Povezava s PostgreSQL je prekinjena.',
    noticeStoreConnected: 'Trgovina Shopify je povezana.',
    noticeStoreChooseAccess:
      'Trgovina Shopify je povezana. Odprite jo v razdelku Vaše povezave in izberite, kdo jo lahko uporablja.',
    allowFailed: 'Aplikacije {name} ni bilo mogoče posodobiti. Poskusite znova.',
    accessUnknown: 'Podrobnosti o dostopu niso na voljo.',
    metaStep: 'Korak {step} od {total}',
    stepsLabel: 'Koraki nastavitve',
    stepChoose: 'Izbira',
    stepPrepare: 'Priprava',
    stepAccess: 'Dostop',
    stepSignIn: 'Prijava',
    metaChooseTitle: 'Poveži Meta Ads',
    metaChooseIntro:
      'Prenesite svoje oglaševalske račune Meta v Ponevo. Najprej izberite, kaj sme Poneva početi z njimi.',
    metaReporting: 'Poročila',
    metaReportingTag: 'Samo branje',
    metaReportingBody:
      'Bere oglaševalske račune, kampanje, nabore oglasov, oglase in rezultate ter vaše strani na Facebooku. Poneva v Meti nikoli ničesar ne spremeni.',
    metaManage: 'Poročila in upravljanje oglasov',
    metaManageTag: 'Branje in pisanje',
    metaManageBody:
      'Poleg tega ustvarja in posodablja kampanje, oglase in kataloge z lastnimi orodji Mete. Vi izberete, ali Poneva vpraša pred vsako spremembo.',
    metaOwnApp:
      'Meta zahteva, da se vsako podjetje poveže prek lastne aplikacije Meta. Naslednji korak vas enkrat vodi skozi njeno nastavitev.',
    metaConnectedAlready: 'Povezano',
    prepareTitle: 'Pripravite svojo aplikacijo Meta',
    prepareIntro:
      'To naredite enkrat v Meta for Developers. Potrebujete skrbniški dostop do poslovnega računa vašega podjetja v Meti.',
    prepareAppTitle: 'Ustvarite ali odprite svojo aplikacijo Meta',
    prepareAppReporting: 'Vklopite Marketing API in Facebook Login for Business.',
    prepareAppManage: 'Dodajte primer uporabe »Create and manage ads with the ads MCP«.',
    prepareAppOpen: 'Odpri Meta for Developers',
    prepareRedirectTitle: 'Dodajte Ponevin naslov za prijavo',
    prepareRedirectBody:
      'V Facebook Login for Business ga prilepite v polje Valid OAuth Redirect URIs. Obdržite naslove, ki so že tam, nato shranite.',
    prepareSecretTitle: 'Pripravite App ID in App Secret',
    prepareSecretBody:
      'Oba najdete v svoji aplikaciji v razdelku App settings → Basic. V zadnjem koraku ju boste prilepili v varno okno, nikoli v Ponevo.',
    prepareDone: 'Končano',
    copy: 'Kopiraj',
    copied: 'Kopirano',
    copyAddress: 'Kopiraj naslov za prijavo',
    copyManual: 'Izberite naslov in ga kopirajte s tipkovnico.',
    permissionsTitle: 'Kaj vas bo Meta prosila, da odobrite',
    permissionsNote:
      'Meta lahko zahteva preverjanje vašega podjetja, preden lahko aplikacija dostopa do oglaševalskih računov zunaj vaših lastnih.',
    scopeAdsRead: 'Branje vaših oglasov in njihovih rezultatov',
    scopeAdsManagement: 'Ustvarjanje in spreminjanje oglasov',
    scopeAdsMcp: 'Uporaba oglaševalskih orodij Mete',
    scopeBusiness: 'Ogled vaših poslovnih portfeljev',
    scopePages: 'Seznam strani, ki jih upravljate',
    scopePageEngagement: 'Branje podatkov in angažiranosti strani',
    scopeInstagram: 'Branje osnovnih podatkov računa na Instagramu',
    scopeCatalog: 'Upravljanje katalogov izdelkov',
    guide: 'Vodnik po korakih',
    metaAccessTitle: 'Kdo lahko uporablja Meta Ads?',
    metaAccessIntro:
      'Oglaševalski računi običajno pripadajo celotnemu podjetju. To lahko kadar koli spremenite.',
    metaReadOnlyTitle: 'Samo branje',
    metaReadOnlyBody:
      'Poročila v Meti nikoli ničesar ne spremenijo. Če želite, da Poneva upravlja oglase, v prvem koraku izberite upravljanje oglasov.',
    signInTitle: 'Prijava z Meto',
    signInIntro: 'Odpre se varno okno. Dokončajte tam in ta stran se bo posodobila sama.',
    signInWindow: 'V varnem oknu',
    signInPaste: 'Prilepite App ID in App Secret',
    signInChoose: 'Izberite svoje podjetje in njegove oglaševalske račune',
    signInApprove: 'Odobrite dostop v Meti',
    signInWaiting: 'Čakanje na Meto…',
    signInSecret:
      'Vaš App Secret gre neposredno v Nango, storitev za prijavo, ki jo uporablja Poneva, in je tam shranjen šifriran. Poneva ga nikoli ne vidi.',
    stopWaiting: 'Prekini čakanje',
    doneTitle: 'Storitev {name} je povezana',
    doneAccountsYou:
      'Zdaj lahko Ponevo sprašujete o svojih oglaševalskih računih. Na voljo: {count}.',
    doneAccountsCompany:
      'Zdaj lahko vsi v podjetju {company} Ponevo sprašujejo o oglaševalskih računih. Na voljo: {count}.',
    doneTools: 'Meta je za ta račun omogočila {count}.',
    doneTry: 'Poskusite vprašati',
    doneAsk: 'Vprašaj o mojih oglasih',
    doneDone: 'Končano',
    doneTryReport: 'Kako so se odrezale kampanje prejšnjega tedna?',
    doneTryReturn: 'Kateri oglasi so ta mesec prinesli najboljši donos?',
    continue: 'Nadaljuj',
    continueToMeta: 'Nadaljuj v Meto',
    back: 'Nazaj',
    cancel: 'Prekliči',
    askHelp: 'Prosi Ponevo za pomoč',
    askHelpPrompt:
      'Pomagaj mi nastaviti našo lastno aplikacijo Meta, da bomo lahko povezali Meta Ads s Ponevo.',
    chatTitle: 'Povežite za nadaljevanje',
    chatIntro: 'Ta klepet ostane, kjer je.',
    chatConnectedYou: 'Povezano za vas',
    chatConnectedCompany: 'Povezano za podjetje {company}',
    chatUnavailable: 'Prijavljeno · orodja niso na voljo',
    chatFoot: 'Prijavite se pri ponudniku. Ko se prikaže Povezano, prosite Ponevo, naj nadaljuje.',
    chatPopupBlocked: 'Vaš brskalnik je blokiral okno za prijavo.',
    chatOpenAuthorization: 'Odpri prijavo',
    chatFallbackBody: 'Uporabljajte to storitev pri svojem delu.',
  },
  sv: {
    eyebrow: 'Arbetsytan för {company}',
    title: 'Anslutningar',
    intro:
      'Anslut dina verktyg för att hitta information och få saker gjorda. Välj vem som kan använda varje anslutning och om Poneva kan läsa eller göra ändringar.',
    activity: 'Aktivitet',
    backToConnections: 'Tillbaka till Anslutningar',
    yoursTitle: 'Dina anslutningar',
    filterLabel: 'Visa anslutningar',
    filterAll: 'Alla',
    filterMine: 'Bara jag',
    filterShared: 'Delade',
    filterAttention: 'Kräver åtgärd',
    columnConnection: 'Anslutning',
    columnWho: 'Vem kan använda den',
    columnWhat: 'Vad Poneva kan göra',
    columnStatus: 'Status',
    yoursLoading: 'Läser in dina anslutningar…',
    yoursEmpty: 'Inget är anslutet än. Sök nedan för att ansluta ditt första verktyg.',
    filterEmpty: 'Inget här just nu.',
    yoursLoadError: 'Det gick inte att läsa in dina anslutningar.',
    serversLoadError: 'Det gick inte att läsa in dina egna servrar.',
    databasesLoadError: 'Det gick inte att läsa in dina databaser.',
    retry: 'Försök igen',
    stateConnected: 'Ansluten',
    stateAttention: 'Kräver inloggning',
    stateReview: 'Granska åtkomst',
    stateUnavailable: 'Inte tillgänglig',
    stateOff: 'Avstängd',
    stateConnecting: 'Ansluter…',
    signInAgain: 'Logga in igen',
    attentionBody:
      'Inloggningen för {name} har gått ut eller återkallats. Logga in igen för att fortsätta använda den.',
    reviewBody:
      'Kontot bakom {name} har ändrats. Granska vem som kan använda den och vad Poneva kan göra innan den används igen.',
    unavailableBody: '{name} är inloggad, men dess verktyg är inte tillgängliga just nu.',
    offBody: '{name} är avstängd. Ingen kan använda den förrän den slås på igen. Inget raderas.',
    kindServer: 'Din egen server',
    kindDatabase: 'PostgreSQL',
    tablesNoun: 'one:tabell|other:tabeller',
    adAccountsNoun: 'one:annonskonto|other:annonskonton',
    toolsNoun: 'one:verktyg|other:verktyg',
    close: 'Stäng',
    moreActions: 'Fler åtgärder för {name}',
    tabOverview: 'Översikt',
    tabAccess: 'Åtkomst',
    tabActivity: 'Aktivitet',
    connectedOn: 'Sedan {date}',
    lastUsed: 'Senast använd {when}',
    whatTitle: 'Vad Poneva kan göra',
    readsLine: 'Läser när du ber om det',
    changesAsk: 'Frågar dig innan något ändras',
    changesAllowed: 'Gör ändringar utan att fråga. Varje ändring registreras under Aktivitet.',
    changesNever: 'Ändrar aldrig något',
    readinessReady: '{count} redo',
    change: 'Ändra',
    tryTitle: 'Prova i en chatt',
    tryPrompt: 'Vad kan du hjälpa mig med via {name}?',
    addAccount: 'Lägg till ett konto till',
    disconnect: 'Koppla från',
    disconnectConfirm:
      'Koppla från {name}? Den slutar fungera i chattar, appar och agenter tills den ansluts igen.',
    disconnectKeep: 'Behåll',
    nativeOnlyYou: 'Bara du kan använda det här kontot.',
    nativeEveryone: 'Alla på {company} kan använda det här kontot.',
    providerPermissions: 'Leverantörens egna kontobehörigheter gäller fortfarande.',
    manageDatabase: 'Hantera databas',
    activityLoading: 'Läser in aktivitet…',
    activityLoadError: 'Det gick inte att läsa in aktiviteten.',
    activityRead: 'Läsning',
    activityChange: 'Ändring',
    activitySetting: 'Inställning',
    activityDenied: 'Nekad',
    activityFailed: 'Misslyckad',
    activityUnknown: 'Okänt resultat',
    activityAll: 'Öppna all aktivitet',
    activityToday: 'I dag',
    activityYesterday: 'I går',
    eventCreated: 'Anslutning skapad',
    eventShared: 'Delning ändrad',
    eventPersonAdded: 'Person tillagd',
    eventPersonRemoved: 'Person borttagen',
    eventAccessChanged: 'Åtkomst ändrad',
    eventRevoked: 'Åtkomst avstängd',
    eventRemoved: 'Anslutning borttagen',
    eventSettings: 'Tidsgräns för frågor ändrad',
    addTitle: 'Lägg till en anslutning',
    integrations: 'one:integration|other:integrationer',
    searchLabel: 'Sök integrationer',
    searchPlaceholder: 'Sök integrationer',
    clearSearch: 'Rensa sökningen',
    inYours: 'I dina anslutningar',
    addNew: 'Lägg till ny',
    resultsFor: '{count} för ”{query}”',
    resultsNoun: 'one:träff|other:träffar',
    popularFirst: 'Populära först',
    searching: 'Söker…',
    searchUnavailable: 'Det går inte att söka efter anslutningar just nu.',
    noResults:
      'Inget matchar det än. Prova ett annat namn eller fråga Poneva vilket verktyg som passar.',
    showMore: 'Visa fler',
    showMoreRetry: 'Försök läsa in fler igen',
    loadingMore: 'Läser in…',
    moreFailed: 'Det gick inte att läsa in fler integrationer. Dina träffar finns kvar.',
    manageMembers: 'Välj vad medlemmar kan ansluta',
    manageDone: 'Klar',
    manageHint:
      'Slå på eller stäng av appar för medlemmar. Konton som redan är anslutna förblir anslutna.',
    allowFor: 'Tillåt nya anslutningar till {name} för {company}',
    tileConnect: 'Anslut',
    tileConnecting: 'Ansluter…',
    tileConnected: 'Ansluten',
    tileSignIn: 'Logga in igen',
    tileOff: 'Avstängd',
    tileUnavailable: 'Inte tillgänglig',
    tileNotAllowed: 'Inte tillåten',
    tileNotAllowedNote: 'Din administratör har stängt av nya anslutningar',
    tileAccountOnly: 'Bara kontoanslutning · inga chattverktyg än',
    tileApiKey: 'API-nyckel krävs',
    tileOwnApp: 'Använder din egen {name}-app',
    tileAdminSetup: 'Ställs in av ägare eller administratör',
    otherWays: 'Andra sätt att ansluta',
    serverTitle: 'Din egen server',
    serverBody: 'Lägg till verktyg från en MCP-server som ditt team driver.',
    serverAction: 'Lägg till server',
    databaseTitle: 'En databas',
    databaseBody: 'PostgreSQL, begränsad till de scheman eller tabeller du väljer.',
    databaseAction: 'Anslut',
    assistantsTitle: 'Andra AI-assistenter',
    assistantsBody: 'Låt Claude, ChatGPT eller en kodagent läsa godkänd företagskunskap.',
    assistantsAction: 'Ställ in',
    assistantsSheetIntro:
      'Assistenter utanför Poneva som läser godkänd företagskunskap, och kodagenterna som arbetar åt dig.',
    footnote:
      'Du godkänner ändringar innan de genomförs. Integrationerna drivs av Pipedream; vilka verktyg varje app erbjuder varierar.',
    askBody:
      'Osäker på vilken du behöver? Poneva kan föreslå anslutningar utifrån arbetet du beskriver.',
    askAction: 'Fråga i chatten',
    askPrompt:
      'Vilka av företagets källor skulle vara mest användbara att ansluta för vårt arbete, utifrån det du vet om oss?',
    compactTitle: 'Ta med dina verktyg.',
    forYou: 'För dig',
    browseAll: 'Visa alla verktyg',
    selectedForFocus: 'Valda utifrån ditt fokus',
    popupBlocked: 'Webbläsaren blockerade inloggningsfönstret.',
    popupOpen: 'Öppna inloggningssidan',
    popupKeepChecking: 'Den här sidan fortsätter att kontrollera.',
    noticeAdded: 'Anslutningen har lagts till.',
    noticeAddedReturn:
      'Anslutningen har lagts till. Du kan stänga den här fliken och gå tillbaka till ditt arbete.',
    noticeSignInUnfinished:
      'Inloggningen slutfördes inte. Välj Logga in igen för servern för att försöka igen.',
    noticeChecked: 'Anslutningen har testats och fungerar.',
    noticeUnreachable: 'Det gick inte att nå servern än.',
    noticeSignInReady: 'En inloggningssida är redo i en ny flik.',
    noticeAuthorized: 'Godkännandet är klart.',
    noticeDatabaseRemoved: 'PostgreSQL har kopplats från.',
    noticeStoreConnected: 'Shopify-butiken är ansluten.',
    noticeStoreChooseAccess:
      'Shopify-butiken är ansluten. Öppna den under Dina anslutningar och välj vem som kan använda den.',
    allowFailed: 'Det gick inte att uppdatera {name}. Försök igen.',
    accessUnknown: 'Åtkomstuppgifterna är inte tillgängliga.',
    metaStep: 'Steg {step} av {total}',
    stepsLabel: 'Steg i konfigurationen',
    stepChoose: 'Välj',
    stepPrepare: 'Förbered',
    stepAccess: 'Åtkomst',
    stepSignIn: 'Logga in',
    metaChooseTitle: 'Anslut Meta Ads',
    metaChooseIntro:
      'Ta in dina annonskonton från Meta i Poneva. Välj först vad Poneva får göra med dem.',
    metaReporting: 'Rapportering',
    metaReportingTag: 'Bara läsning',
    metaReportingBody:
      'Läser annonskonton, kampanjer, annonsgrupper, annonser och resultat, plus dina Facebook-sidor. Poneva ändrar aldrig något i Meta.',
    metaManage: 'Rapportering och annonshantering',
    metaManageTag: 'Läsning och skrivning',
    metaManageBody:
      'Skapar och uppdaterar också kampanjer, annonser och kataloger med Metas egna verktyg. Du väljer om Poneva ska fråga före varje ändring.',
    metaOwnApp:
      'Meta kräver att varje företag ansluter via en egen Meta-app. Nästa steg visar hur du ställer in den, en gång.',
    metaConnectedAlready: 'Ansluten',
    prepareTitle: 'Förbered din Meta-app',
    prepareIntro:
      'Gör det här en gång i Meta for Developers. Du behöver administratörsåtkomst till ditt företag i Meta.',
    prepareAppTitle: 'Skapa eller öppna din Meta-app',
    prepareAppReporting: 'Slå på Marketing API och Facebook Login for Business.',
    prepareAppManage: 'Lägg till användningsfallet ”Create and manage ads with the ads MCP”.',
    prepareAppOpen: 'Öppna Meta for Developers',
    prepareRedirectTitle: 'Lägg till Ponevas inloggningsadress',
    prepareRedirectBody:
      'I Facebook Login for Business klistrar du in den under Valid OAuth Redirect URIs. Behåll adresserna som redan finns där och spara sedan.',
    prepareSecretTitle: 'Ha ditt App ID och din App Secret redo',
    prepareSecretBody:
      'Båda finns i din app under App settings → Basic. Du klistrar in dem i ett säkert fönster i sista steget, aldrig i Poneva.',
    prepareDone: 'Klart',
    copy: 'Kopiera',
    copied: 'Kopierat',
    copyAddress: 'Kopiera inloggningsadressen',
    copyManual: 'Markera adressen och kopiera den med tangentbordet.',
    permissionsTitle: 'Det här ber Meta dig att godkänna',
    permissionsNote:
      'Meta kan vilja verifiera ditt företag innan appen kan nå andra annonskonton än dina egna.',
    scopeAdsRead: 'Läsa dina annonser och deras resultat',
    scopeAdsManagement: 'Skapa och ändra annonser',
    scopeAdsMcp: 'Använda Metas annonsverktyg',
    scopeBusiness: 'Se dina företagsportföljer',
    scopePages: 'Lista sidorna du hanterar',
    scopePageEngagement: 'Läsa sidinformation och engagemang',
    scopeInstagram: 'Läsa grundläggande information om Instagramkonton',
    scopeCatalog: 'Hantera produktkataloger',
    guide: 'Steg-för-steg-guide',
    metaAccessTitle: 'Vem kan använda Meta Ads?',
    metaAccessIntro: 'Annonskonton tillhör oftast hela företaget. Du kan ändra det när som helst.',
    metaReadOnlyTitle: 'Bara läsning',
    metaReadOnlyBody:
      'Rapportering ändrar aldrig något i Meta. Vill du att Poneva ska hantera annonser väljer du annonshantering i första steget.',
    signInTitle: 'Logga in med Meta',
    signInIntro:
      'Ett säkert fönster öppnas. Slutför där, så uppdateras den här sidan av sig själv.',
    signInWindow: 'I det säkra fönstret',
    signInPaste: 'Klistra in ditt App ID och din App Secret',
    signInChoose: 'Välj ditt företag och dess annonskonton',
    signInApprove: 'Godkänn åtkomsten i Meta',
    signInWaiting: 'Väntar på Meta…',
    signInSecret:
      'Din App Secret går direkt till Nango, inloggningstjänsten som Poneva använder, och lagras krypterad där. Poneva ser den aldrig.',
    stopWaiting: 'Sluta vänta',
    doneTitle: '{name} är ansluten',
    doneAccountsYou: 'Nu kan du fråga Poneva om {count}.',
    doneAccountsCompany: 'Nu kan alla på {company} fråga Poneva om {count}.',
    doneTools: 'Meta tillhandahöll {count} för det här kontot.',
    doneTry: 'Prova att fråga',
    doneAsk: 'Fråga om mina annonser',
    doneDone: 'Klar',
    doneTryReport: 'Hur gick förra veckans kampanjer?',
    doneTryReturn: 'Vilka annonser gav bäst avkastning den här månaden?',
    continue: 'Fortsätt',
    continueToMeta: 'Fortsätt till Meta',
    back: 'Tillbaka',
    cancel: 'Avbryt',
    askHelp: 'Be Poneva om hjälp',
    askHelpPrompt:
      'Hjälp mig att ställa in vår egen Meta-app så att jag kan ansluta Meta Ads till Poneva.',
    chatTitle: 'Anslut för att fortsätta',
    chatIntro: 'Den här chatten stannar där den är.',
    chatConnectedYou: 'Ansluten för dig',
    chatConnectedCompany: 'Ansluten för {company}',
    chatUnavailable: 'Inloggad · verktygen är inte tillgängliga',
    chatFoot: 'Logga in hos leverantören. När det står Ansluten ber du Poneva att fortsätta.',
    chatPopupBlocked: 'Din webbläsare blockerade inloggningsfönstret.',
    chatOpenAuthorization: 'Öppna inloggningen',
    chatFallbackBody: 'Använd tjänsten i ditt arbete.',
  },
};

export type ConnectionsPageCopyKey = keyof typeof copy.en;
export default copy satisfies NamespaceCatalog<ConnectionsPageCopyKey>;
